/**
 * The Lillebjörn — The Fitting Room bundle builder.
 * Native Shopify Web Component (no build step, no framework), reading real
 * product/variant JSON emitted by sections/lillebjorn-fitting-room.liquid —
 * same pattern as lillebjorn-size-guide.js, adapted for a multi-step guided
 * bundle flow (collar + leash + harness + free engraved nametag).
 *
 * Sizing/breed/guardrail/width-matrix/bundle-pricing logic lives in the
 * shared, framework-free assets/lillebjorn-fit-logic.js
 * (window.LillebjornFitLogic, loaded before this file) — this file is
 * page-rendering, Shopify variant resolution, and cart only.
 *
 * BUILD SCOPE (see the approved implementation plan): the full happy-path
 * flow below is built and working end to end. The documented edge-case
 * *states* (out-of-range measurement banner, breed-not-found polish,
 * review mismatch banner, personalization validation, nametag sold-out
 * steering) each have a concrete, named condition computed in deriveVals()
 * but their full rendering is left as `// TODO(edge-case)` — see the
 * implementation plan's "Edge-case stub hook points" section.
 */
(function () {
  "use strict";

  if (customElements.get("lillebjorn-fitting-room")) return;

  var FL = window.LillebjornFitLogic;
  if (!FL) {
    console.error("lillebjorn-fitting-room: window.LillebjornFitLogic is missing — load lillebjorn-fit-logic.js first.");
    return;
  }

  function esc(str) { return FL.escapeHtml(str); }

  /* ============================================================
     Money formatting — mirrors lillebjorn-size-guide.js's money()/
     MONEY_FORMATS exactly, so every surface renders the shop's real
     money_format identically.
     ============================================================ */
  function formatNumber(value, decimals, thousandsSep, decimalSep) {
    var sign = value < 0 ? "-" : "";
    var fixed = Math.abs(value).toFixed(decimals);
    var parts = fixed.split(".");
    var withThousands = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    return sign + withThousands + (parts[1] ? decimalSep + parts[1] : "");
  }
  var MONEY_FORMATS = {
    amount: { decimals: 2, thousands: ",", decimal: "." },
    amount_no_decimals: { decimals: 0, thousands: ",", decimal: "." },
    amount_with_comma_separator: { decimals: 2, thousands: ".", decimal: "," },
    amount_no_decimals_with_comma_separator: { decimals: 0, thousands: ".", decimal: "," },
    amount_with_space_separator: { decimals: 2, thousands: " ", decimal: "," },
    amount_no_decimals_with_space_separator: { decimals: 0, thousands: " ", decimal: "," },
    // Apostrophe grouping (CHF and other de-CH style markets). Omitting these
    // two did not degrade gracefully: the regex below simply found no match,
    // .replace() returned the format string untouched, and every price on
    // every surface rendered the raw Liquid token —
    // "CHF {{amount_with_apostrophe_separator}}". Matches the theme's own
    // SHTHelper.formatMoney, which has always supported both.
    amount_with_apostrophe_separator: { decimals: 2, thousands: "'", decimal: "." },
    amount_no_decimals_with_apostrophe_separator: { decimals: 0, thousands: "'", decimal: "." }
  };
  function money(cents, format) {
    if (typeof cents !== "number" || isNaN(cents)) return "";
    var value = cents / 100;
    return (format || "€{{amount}}").replace(
      /\{\{\s*(amount_no_decimals_with_comma_separator|amount_no_decimals_with_space_separator|amount_no_decimals_with_apostrophe_separator|amount_with_comma_separator|amount_with_space_separator|amount_with_apostrophe_separator|amount_no_decimals|amount)\s*\}\}/g,
      function (match, token) {
        var spec = MONEY_FORMATS[token];
        return formatNumber(value, spec.decimals, spec.thousands, spec.decimal);
      }
    );
  }
  function round2(n) { return Math.round(n * 100) / 100; }
  function cmToInPrecise(cm) { return Math.round(cm / 2.54 * 100) / 100; }
  function inToCm(inches) { return inches * 2.54; }

  /* ============================================================
     Dual units — ALWAYS BOTH.
     A shopper measuring a dog holds one tape, in one unit, and should
     never have to open a modal, hunt a toggle, or do mental arithmetic to
     find out whether "34 cm" is their 13½ inches. Every measurement
     surface in the Fitting Room (Step-1 recap, inline size pills, the
     collar/harness size lines, product cards, review) renders cm AND in
     side by side. The cm/in toggle survives only inside the size
     assistant, where it switches the INPUT domain (slider step, typed
     value) — not what's displayed.
     ============================================================ */
  // One decimal for inches in display copy. cmToInPrecise's two decimals
  // are right for the assistant's reference chart but read as spurious
  // precision inline — "27–32 cm / 10.63–12.6 in" mixes two significant
  // figures with three in one range and looks like a rendering bug.
  function inDisp(cm) { return Math.round(cm / 2.54 * 10) / 10; }
  function dual(cm) {
    if (cm === null || cm === undefined || isNaN(cm)) return "";
    return round2(cm) + " cm / " + inDisp(cm) + " in";
  }
  function dualRange(lo, hi) {
    if (lo === null || lo === undefined || hi === null || hi === undefined) return "";
    return lo + "–" + hi + " cm / " + inDisp(lo) + "–" + inDisp(hi) + " in";
  }
  // The real adjustability window behind a size letter, in cm.
  function sizeBoundsCm(key, size) {
    var sizes = key === "neck" ? FL.COLLAR_SIZES : FL.HARNESS_SIZES;
    var nodes = key === "neck" ? FL.NECK_NODES : FL.CHEST_NODES;
    var mx = key === "neck" ? FL.NECK_CUSTOM_MAX : FL.CHEST_CUSTOM_MAX;
    var i = sizes.indexOf(size);
    if (i === -1) return null;
    return { lo: nodes[i], hi: nodes[i + 1] || mx };
  }
  function sizeRangeDual(key, size) {
    var b = sizeBoundsCm(key, size);
    return b ? dualRange(b.lo, b.hi) : "";
  }
  function leatherHex(key) {
    var m = FL.LEATHERS.filter(function (l) { return l.key === key; })[0];
    return m ? m.hex : "#c7c1b8";
  }
  // Uids for individually-committed set items (Mix & Match arrays).
  var uidSeq = 0;
  function nextUid() { uidSeq += 1; return "i" + uidSeq.toString(36) + Date.now().toString(36); }

  /* ============================================================
     Real Shopify variant resolution — mirrors lillebjorn-size-guide.js's
     private helpers exactly (widthMatches/resolveVariant/productFor).
     ============================================================ */
  // Case- and whitespace-insensitive form of a variant option value, so a
  // catalog that writes "25 MM", "25mm" or "25 mm" all resolve to the same
  // thing. Deliberately NOT a fuzzy match: only presentation differences
  // are absorbed.
  function normalizeOption(value) {
    return String(value == null ? "" : value).toLowerCase().replace(/\s+/g, "");
  }
  function widthMatches(optionValue, wantWidth) {
    if (!wantWidth || !optionValue) return true;
    var escaped = wantWidth.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp("^" + escaped + "\\b", "i").test(String(optionValue).trim());
  }
  function resolveVariant(productData, want) {
    if (!productData || !productData.variants || !productData.variants.length) return null;
    var pool = productData.variants.filter(function (v) { return v.available !== false; });
    if (!pool.length) return null;
    // Size compared on a normalised form (case-folded, whitespace removed)
    // rather than raw ===.
    //
    // This surfaced the moment lillebjorn-product-json.liquid started
    // reading size options named "Tag size"/"Size (mm)" correctly. Before
    // that they emitted a blank size, which resolveVariant treats as
    // matching ANY request — so the Pebble nametag silently returned its
    // FIRST variant for both 25mm and 32mm and showed one price for both.
    // With the real values finally arriving, a catalog that writes
    // "25 MM" or "25 mm" stopped matching the "25mm" this file asks for,
    // and the tag flipped to "Currently unavailable" — a true statement
    // about a false premise. Normalising closes that gap without loosening
    // the match to anything genuinely different ("Small" still won't
    // match "25mm", and should not).
    var sizeOk = function (v) { return !want.size || !v.options.size || normalizeOption(v.options.size) === normalizeOption(want.size); };
    var widthOk = function (v) { return !want.width || !v.options.width || widthMatches(v.options.width, want.width); };
    var found = pool.filter(function (v) { return sizeOk(v) && widthOk(v); });
    return found[0] || null;
  }
  // Same anatomical-stock gate as lillebjorn-size-guide.js's
  // productCarriesWidthData/productHasWidth: a family the width matrix
  // allows may still not be stocked at that width by THIS exact mapped
  // product.
  function productHasWidth(product, width) {
    if (!width || !product || !product.variants) return true;
    return product.variants.some(function (v) {
      return v.options && v.options.width && widthMatches(v.options.width, width);
    });
  }
  // Does this mapped product expose a Width option at all? A single-cut
  // product (e.g. Aeris Classic — strictly 3cm) carries none, and must
  // collapse to its family's fixed defaultWidth, never the matrix list.
  // Mirrors the Size Guide's productCarriesWidthData exactly.
  function productCarriesWidthData(product) {
    return !!(product && product.variants && product.variants.some(function (v) {
      return v.options && v.options.width;
    }));
  }
  function productFor(productMap, familyKey, colorKey) {
    return (productMap[familyKey] && productMap[familyKey][colorKey]) || null;
  }
  function imageFor(product, variant) {
    if (variant && variant.image) return variant.image;
    return (product && product.variants && product.variants[0] && product.variants[0].image) || "";
  }
  /* THE photo resolver for collars and leashes.

     Strap width and size are SPEC dimensions, not photographic ones — the
     catalog has one set of photography per leather, and leather is already
     encoded in the mapped product (each colourway is its own product). The
     card and the committed item must therefore resolve to the SAME url.

     They didn't. Cards called imageFor(product, null) — width-independent,
     correct — while _addCollarItem/_addLeashItem called
     imageFor(product, variant), which returns variant.featured_image when
     one exists (see lillebjorn-product-json.liquid). So the moment a
     merchant attached a per-variant photo, the same collar showed one
     image on the card the shopper bought it from and a different one in
     the set chip, the sidebar and the Review line — a mismatch introduced
     purely by data entry, with no code change and no way to notice it in
     review. One resolver makes the two incapable of disagreeing. */
  function productPhoto(product) {
    return (product && product.variants && product.variants[0] && product.variants[0].image) || "";
  }
  // A product is orderable if it has at least one available variant —
  // the swatch/size gating test (C4).
  function productIsAvailable(product) {
    return !!(product && product.variants && product.variants.some(function (v) { return v.available !== false; }));
  }
  // Real price range from the mapped product's own variants — card prices
  // come from live catalog data, never a hardcoded label that can drift
  // out of sync with the store.
  function priceRangeCents(product) {
    if (!product || !product.variants) return null;
    var min = null, max = null;
    product.variants.forEach(function (v) {
      if (v.available === false || typeof v.price !== "number") return;
      if (min === null || v.price < min) min = v.price;
      if (max === null || v.price > max) max = v.price;
    });
    return min === null ? null : { min: min, max: max };
  }
  // Walks the nested kit-imagery tree (design line -> color -> style ->
  // width, see lillebjorn-kit-collection-scan.liquid) for the first real
  // photo it can find — used only as the hero image's last-resort
  // fallback (handoff §8) when no manual hero_image is set, so any
  // populated Bundle photo is good enough, not a specific one.
  function firstKitImageUrl(tierObj) {
    if (!tierObj || typeof tierObj !== "object") return "";
    if (tierObj.url) return tierObj.url;
    for (var key in tierObj) {
      if (!Object.prototype.hasOwnProperty.call(tierObj, key)) continue;
      var found = firstKitImageUrl(tierObj[key]);
      if (found) return found;
    }
    return "";
  }

  var FAMILY_LABELS = {
    "aeris-classic": "Aeris Classic",
    "vero-classic": "Vero Classic",
    "aeris-wide": "Aeris Wide",
    "vero-wide": "Vero Wide"
  };
  var FAMILY_DESCRIPTORS = {
    "aeris-classic": { blurb: "Perforated leather for airflow.", best: "warm climates & active dogs" },
    "vero-classic": { blurb: "Smooth everyday leather, slim and light.", best: "most dogs, daily wear" },
    "aeris-wide": { blurb: "Wide and perforated.", best: "big, active dogs in the heat" },
    "vero-wide": { blurb: "Wider strap that spreads pressure.", best: "large & sighthound breeds" }
  };
  // Vero lines are leather-stampable (personalization-single); Aeris is
  // perforated and never offers stamping (handoff §1/§10).
  function familyIsStampable(familyKey) { return familyKey.indexOf("vero") === 0; }

  var TAG_DESIGNS = [
    { key: "pebble", label: "Pebble Nametag" },
    { key: "ripple", label: "Ripple Nametag" }
  ];
  var TAG_SIZES = ["25mm", "32mm"];
  var LEASH_WIDTHS = ["1.5cm", "2cm"];

  function leatherDisplayName(key) {
    var m = FL.LEATHERS.filter(function (l) { return l.key === key; })[0];
    return m ? m.name : (key ? key.charAt(0).toUpperCase() + key.slice(1) : "");
  }

  // Typical-breed examples per size row (Size Guide parity) — built once,
  // lazily, from the full breed database via each profile's adult
  // defaults; curated (hand-measured) breeds list first.
  var BREEDS_BY_COLLAR_SIZE = null, BREEDS_BY_HARNESS_SIZE = null;
  function ensureBreedSizeMaps() {
    if (BREEDS_BY_COLLAR_SIZE) return;
    BREEDS_BY_COLLAR_SIZE = {}; BREEDS_BY_HARNESS_SIZE = {};
    FL.COLLAR_SIZES.forEach(function (sz) { BREEDS_BY_COLLAR_SIZE[sz] = []; });
    FL.HARNESS_SIZES.forEach(function (sz) { BREEDS_BY_HARNESS_SIZE[sz] = []; });
    FL.BREED_NAMES.forEach(function (name) {
      var p = FL.resolveBreedProfile(name);
      var curated = !!FL.CURATED_BREEDS[name.toLowerCase()];
      var ns = FL.computeSizeLetter("neck", p.neckDefault, null);
      var cs = FL.computeSizeLetter("chest", p.chestDefault, null);
      if (BREEDS_BY_COLLAR_SIZE[ns]) BREEDS_BY_COLLAR_SIZE[ns].push({ name: name, curated: curated });
      if (BREEDS_BY_HARNESS_SIZE[cs]) BREEDS_BY_HARNESS_SIZE[cs].push({ name: name, curated: curated });
    });
  }
  function topBreedsForSize(bucket, limit) {
    if (!bucket || !bucket.length) return "";
    var sorted = bucket.slice().sort(function (a, b) { return (b.curated ? 1 : 0) - (a.curated ? 1 : 0); });
    return sorted.slice(0, limit || 3).map(function (b) { return b.name; }).join(", ");
  }

  /* ============================================================
     "A Fitting Room is on screen and in use" — published for other
     scripts on the page.

     The store-wide free-gift modal (assets/free-gift-modal.js, rendered on
     every page from layout/theme.liquid) listens for "cart:refresh", which
     this component dispatches on a successful add. Its 350ms debounce can
     therefore fire while the shopper is still standing in the configurator
     — and on a slow connection it fires BEFORE the navigation to /cart
     completes, throwing a full-screen "pick your free gift" dialog over a
     half-built set. That is the single most disruptive interruption
     available in this flow: it arrives at the exact moment of purchase
     intent, and it offers a reward the shopper has usually already earned
     inside the builder.

     Three signals, deliberately: a boolean for scripts that want a cheap
     check, a body class for CSS and for anything that prefers markup, and
     an instance COUNT so a theme-editor section swap — which can connect
     the replacement before disconnecting the original — can never leave
     the flag stuck on or prematurely off.
     ============================================================ */
  var LB_FR_ACTIVE_CLASS = "lb-fr-open";
  function announceFittingRoomPresence(delta) {
    try {
      var n = (window.LB_FR_INSTANCES || 0) + delta;
      if (n < 0) n = 0;
      window.LB_FR_INSTANCES = n;
      window.LB_FR_ACTIVE = n > 0;
      if (document.body) document.body.classList.toggle(LB_FR_ACTIVE_CLASS, n > 0);
      // Fires only on the true 1 -> 0 transition. Lets a suppressed
      // consumer re-evaluate the moment the configurator leaves the page
      // WITHOUT polling — and is a no-op on the normal path, where the
      // component tears down during a navigation to /cart and the next
      // page evaluates from scratch anyway.
      if (delta < 0 && n === 0) {
        document.dispatchEvent(new CustomEvent("lillebjorn:fitting-room-closed", { bubbles: true }));
      }
    } catch (e) { /* never let presence bookkeeping break the builder */ }
  }

  function LillebjornFittingRoom() {
    return Reflect.construct(HTMLElement, [], LillebjornFittingRoom);
  }
  Object.setPrototypeOf(LillebjornFittingRoom.prototype, HTMLElement.prototype);
  Object.setPrototypeOf(LillebjornFittingRoom, HTMLElement);

  // One factory owns the pristine state shape — connect AND "Start over"
  // both build from here, so a full reset can never miss a field.
  LillebjornFittingRoom.prototype._initialState = function () {
    return {
      step: "entry",
      path: null,
      expressMode: false,

      ageProfile: "adult",
      dogName: "",
      breedName: "", breedQuery: "", showBreedList: false, breedActiveIdx: 0,
      unit: "cm",

      measureOpen: false, measureKey: null,
      neck: null, chest: null,
      neckBoundaryChoice: null, chestBoundaryChoice: null,
      // Provenance per dimension: 'measured' (assistant slider/typing) |
      // 'pill' (standard-size pick) | 'breed-average' (our estimate, taken
      // explicitly) | null — drives honest readout copy, the Review
      // checkpoint, and the _lb_sizing_basis line-item property.
      neckSource: null, chestSource: null,
      measureDraftSource: null,
      // Per-card configuration: every product card carries its OWN swatch
      // row and width selector — colors/widths keyed by family (collars)
      // and style (leashes), never one global pick per step.
      collarFamily: null, collarColors: {}, collarWidths: {},
      // Per-card size overrides (family → letter): cards inherit the
      // Step-1 size but any card can confirm/switch its own.
      collarSizes: {},
      // Classic/Wide grid tab (null = follow the recommendation's class).
      collarTab: null,
      // Manual Body & Neck Build override for mixed/unknown breeds —
      // maps onto the calculator's BUILD_TO_CATEGORY rules.
      buildOverride: "",
      // Stamping is per collar family, not one global switch: a set can
      // legitimately contain a stamped Vero Classic and an unstamped
      // Vero Wide, and Aeris can never be stamped at all.
      collarStampFor: {}, collarStampText: "", stampError: null,
      leashStyle: FL.LEASH_STYLE_DEFAULT, leashColors: {}, leashWidths: {},
      // null = inherit the last-added collar's leather until the shopper
      // explicitly picks one on the harness step.
      harnessColor: null,
      tagDesign: "ripple", tagSize: "32mm", tagEngraveName: "", tagEngravePhone: "",
      // "No engraving needed": keeps the tag in the set but blank.
      tagNoEngraving: false,
      // Validation flag: engraving left empty without the blank-tag
      // checkbox — highlights the fields and blocks Continue.
      tagError: false,

      // True Mix & Match: each category is an ARRAY of committed items,
      // each carrying its own color/size/width and a uid for removal.
      collars: [], leashes: [], harnesses: [], tag: null,

      // Staged measurement + boundary (snug/relaxed) choice while the
      // size assistant is open — commits ONLY via "Apply"; closing
      // discards both.
      measureDraft: null,
      measureDraftBoundary: null,

      spellingConfirmed: false,
      // Set when the shopper steps out of Review (an Edit action or a
      // rail-segment click) — the revisited step's commit then saves and
      // returns straight to Review instead of walking the chain forward.
      editingFrom: false,
      // Non-destructive edit: the uid of the committed item currently
      // loaded back into its product card. The item STAYS in the set the
      // whole time (so navigating away can never silently delete it) and
      // the next commit REPLACES it in place instead of appending.
      editingUid: null,

      // Two-step "Start over" — one stray click must not be able to
      // destroy a fully configured set.
      confirmReset: false,
      // Transient in-context nudge: which card's size row needs attention.
      sizePromptFor: null,
      // Same idea for Review's spelling gate — set when the shopper
      // presses Add to cart without having confirmed.
      spellingPrompt: false,

      cartStatus: null, // null | 'adding' | 'added' | 'error'
      cartError: null,
      // Transient feedback: one-shot toast + a polite screen-reader
      // announcement, both cleared on the next interaction.
      toast: null,
      announce: "",
      mobileSummaryExpanded: false
    };
  };

  LillebjornFittingRoom.prototype.connectedCallback = function () {
    // Before the re-connect early-return below: a node that is moved in the
    // DOM (rather than destroyed) still needs to re-register its presence.
    announceFittingRoomPresence(1);
    if (this._lbfrInitialized) { this._wireDocumentListeners(); return; }
    this._lbfrInitialized = true;
    this.classList.add("lb-fr");

    var dataScript = this.querySelector('script[data-lb-fr-products]');
    this.productMap = {};
    try {
      this.productMap = dataScript ? JSON.parse(dataScript.textContent) : {};
    } catch (e) {
      console.error("lillebjorn-fitting-room: could not parse product JSON", e);
    }

    this.supportEmail = this.getAttribute("data-support-email") || "support@thelillebjorn.com";
    this.moneyFormat = this.getAttribute("data-money-format") || "€{{amount}}";
    this.leadTimeText = this.getAttribute("data-lead-time-text") || "1–10 days";
    this.heroImage = this.getAttribute("data-hero-image") || "";
    this.heroTestimonial = this.getAttribute("data-hero-testimonial") || "";

    var discountPercent = parseFloat(this.getAttribute("data-set-discount-percent"));
    this.discountRate = (!isNaN(discountPercent) && discountPercent >= 0 && discountPercent <= 100)
      ? discountPercent / 100
      : FL.UPSELL_DISCOUNT_RATE_DEFAULT;

    var giftThreshold = parseFloat(this.getAttribute("data-gift-threshold-dollars"));
    var baseThresholdCents = Math.round((!isNaN(giftThreshold) && giftThreshold > 0 ? giftThreshold : 200) * 100);
    // Multi-currency: the threshold is configured in the store's base
    // currency — convert into the shopper's active presentment currency
    // via Shopify's live rate so the progress bar and "away" amounts
    // agree with the prices on screen ($, €, £, …). Formatting itself
    // always flows through the shop's money_format.
    // Kept as the BASE (shop-currency) figure only. The presentment-
    // currency value is resolved per render by _resolveGiftThresholdCents()
    // — see there for why snapshotting it here was wrong.
    this.giftThresholdBaseCents = baseThresholdCents;
    this.giftThresholdCents = this._resolveGiftThresholdCents();

    var reviewRating = parseFloat(this.getAttribute("data-review-rating"));
    var reviewCount = parseInt(this.getAttribute("data-review-count"), 10);
    this.reviewRating = (!isNaN(reviewRating) && !isNaN(reviewCount) && reviewCount > 0) ? reviewRating : null;
    this.reviewCount = this.reviewRating != null ? reviewCount : null;

    this.state = this._initialState();
    this._activeFocusId = null;
    this._debounceTimers = {};

    this._hydrate();
    this.render();
    this._wireEvents();
    this._wireDocumentListeners();
    this._pushEvent("fitting_room_opened", {});
  };

  // ---------- Persistence ----------
  // A refresh (or a return visit) must not cost the shopper their set.
  // The state subset below (no transient UI flags, no cart status) is
  // written to localStorage on every render and restored on connect. The
  // versioned key means a future state-shape change simply orphans old
  // snapshots instead of corrupting hydration.
  var STORAGE_KEY = "lillebjorn_fr_state_v2";
  var PERSIST_KEYS = [
    "step", "path", "expressMode",
    "ageProfile", "dogName", "breedName", "breedQuery", "unit",
    "neck", "chest", "neckBoundaryChoice", "chestBoundaryChoice", "neckSource", "chestSource",
    "collarFamily", "collarColors", "collarWidths", "collarSizes", "collarTab", "buildOverride", "collarStampFor", "collarStampText",
    "leashStyle", "leashColors", "leashWidths", "harnessColor",
    "tagDesign", "tagSize", "tagEngraveName", "tagEngravePhone", "tagNoEngraving",
    "collars", "leashes", "harnesses", "tag",
    "spellingConfirmed", "editingFrom"
  ];

  // Writing on EVERY render meant a localStorage round-trip per slider
  // frame while dragging the measurement assistant — a synchronous,
  // main-thread disk write inside the drag loop. Coalesced to one write
  // per idle moment; the snapshot is only ever read on a fresh connect,
  // so a few hundred ms of lag costs nothing.
  LillebjornFittingRoom.prototype._persist = function () {
    var self = this;
    clearTimeout(this._persistTimer);
    this._persistTimer = setTimeout(function () { self._persistNow(); }, 200);
  };
  LillebjornFittingRoom.prototype._persistNow = function () {
    try {
      var s = this.state;
      var keep = {};
      PERSIST_KEYS.forEach(function (k) { keep[k] = s[k]; });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(keep));
    } catch (e) { /* storage unavailable (private mode, quota) — run stateless */ }
  };

  LillebjornFittingRoom.prototype._hydrate = function () {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var saved = JSON.parse(raw);
      if (!saved || typeof saved !== "object") return;
      var s = this.state;
      PERSIST_KEYS.forEach(function (k) {
        if (Object.prototype.hasOwnProperty.call(saved, k)) s[k] = saved[k];
      });
      // Shape guards — a corrupt/old snapshot must never break arrays/maps.
      if (!Array.isArray(s.collars)) s.collars = [];
      if (!Array.isArray(s.leashes)) s.leashes = [];
      if (!Array.isArray(s.harnesses)) s.harnesses = [];
      if (!s.collarColors || typeof s.collarColors !== "object") s.collarColors = {};
      if (!s.leashColors || typeof s.leashColors !== "object") s.leashColors = {};
      if (!s.collarWidths || typeof s.collarWidths !== "object") s.collarWidths = {};
      if (!s.leashWidths || typeof s.leashWidths !== "object") s.leashWidths = {};
      if (!s.collarSizes || typeof s.collarSizes !== "object") s.collarSizes = {};
      if (!s.collarStampFor || typeof s.collarStampFor !== "object") s.collarStampFor = {};
      // Never resurrect a post-purchase screen into a fresh session.
      if (s.step === "added") s.step = "review";
    } catch (e) { /* corrupted snapshot — start fresh */ }
  };

  LillebjornFittingRoom.prototype.disconnectedCallback = function () {
    // Before the early-return below, so the count stays balanced against
    // connectedCallback's unconditional increment — otherwise a node that
    // never wired its document listeners would leak a permanent +1 and
    // suppress the free-gift modal for the rest of the session.
    announceFittingRoomPresence(-1);
    if (!this._docListenersWired) return;
    window.removeEventListener("popstate", this._onPopState);
    window.removeEventListener("pagehide", this._onPageHide);
    document.removeEventListener("visibilitychange", this._onPageHide);
    document.documentElement.style.removeProperty("overflow");
    // Every pending timer must die with the element. Three of them
    // (toast dismissal, size-prompt dismissal, the measurement debounce)
    // called setState on a detached node — harmless visually, since
    // nothing is on screen, but each one pinned `this` and its whole
    // state object in memory until it fired, and re-rendered a tree
    // nobody could see. In a Shopify theme this element is torn down and
    // re-created on every section re-render in the editor, so they
    // accumulate.
    if (this._chromeObserver) { this._chromeObserver.disconnect(); this._chromeObserver = null; }
    clearTimeout(this._persistTimer);
    clearTimeout(this._toastTimer);
    clearTimeout(this._promptTimer);
    if (this._debounceTimers) {
      var self = this;
      Object.keys(this._debounceTimers).forEach(function (k) { clearTimeout(self._debounceTimers[k]); });
    }
    this._persistNow();
    this._docListenersWired = false;
  };

  var proto = LillebjornFittingRoom.prototype;

  proto.setState = function (patch) {
    Object.assign(this.state, patch);
    this.render();
  };

  proto._pushEvent = function (name, payload) {
    try {
      if (!window.dataLayer || typeof window.dataLayer.push !== "function") return;
      window.dataLayer.push(Object.assign({ event: name }, payload || {}));
    } catch (e) {
      console.warn("lillebjorn-fitting-room: dataLayer.push failed", e);
    }
  };

  /* ============================================================
     Breed / sizing derivation
     ============================================================ */
  proto.currentBreedProfile = function () {
    var profile = FL.resolveBreedProfile(this.state.breedName);
    // Manual Body & Neck Build override (calculator parity): only applies
    // on the fallback path — a curated/archetype breed already carries a
    // measured category, and the override must never silently reclassify
    // it. BUILD_TO_CATEGORY deliberately omits "Compact / Broad".
    var build = this.state.buildOverride;
    if (build && profile.isFallback && FL.BUILD_TO_CATEGORY[build]) {
      return Object.assign({}, profile, { category: FL.BUILD_TO_CATEGORY[build], build: build });
    }
    return profile;
  };

  proto.applyBreedSelection = function (name) {
    // A breed change re-derives every ESTIMATE downstream (size letters,
    // width matrix, recommendations) — but never overwrites a real
    // measurement the shopper already entered: their dog's neck doesn't
    // change because the breed field did.
    this.setState({ breedName: name, breedQuery: name, showBreedList: false });
    this._pushEvent("fitting_room_breed_selected", { breed: name });
  };

  /* Gift threshold, resolved LIVE rather than snapshotted at mount.
     Two independent failure modes this closes:

     FX. window.Shopify.currency is populated by an inline Shopify script,
     and this file is deferred, so the rate read during connectedCallback
     can still be the pre-hydration default of 1. Frozen at 1 on a store
     presenting IDR (rate ~16000), a Rp 3,200,000 threshold becomes Rp 200
     and every set unlocks the free tag — while free-gift-modal.js, which
     reads the rate on every check, correctly sees the same cart as far
     below the line. The two then disagree permanently.

     DRIFT. The threshold exists in two places: this section's
     gift_threshold_dollars and the theme's settings.free_gift_threshold,
     which the modal uses. They agree today only because both happen to be
     200. free-gift-modal.js now publishes its own base figure, and we
     prefer it whenever present, so editing either one in the admin can no
     longer put the two surfaces out of step. The section setting remains
     the fallback for pages where the modal isn't rendered (free gift
     disabled, or the snippet absent). */
  proto._resolveGiftThresholdCents = function () {
    var published = window.LB_FREE_GIFT_THRESHOLD_BASE_CENTS;
    var base = (typeof published === "number" && published > 0)
      ? published
      : this.giftThresholdBaseCents;
    var rate = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    if (!(rate > 0)) rate = 1;
    return Math.round(base * rate);
  };

  proto.resolvedNeckCm = function (breed) {
    if (this.state.neck !== null) return this.state.neck;
    return FL.applyAgeProfile(breed, this.state.ageProfile).neckSize;
  };
  proto.resolvedChestCm = function (breed) {
    if (this.state.chest !== null) return this.state.chest;
    return FL.applyAgeProfile(breed, this.state.ageProfile).chestSize;
  };
  proto.resolvedCollarSize = function (breed) {
    return FL.computeSizeLetter("neck", this.resolvedNeckCm(breed), this.state.neckBoundaryChoice);
  };
  proto.resolvedHarnessSize = function (breed) {
    return FL.computeSizeLetter("chest", this.resolvedChestCm(breed), this.state.chestBoundaryChoice);
  };

  /* ============================================================
     Product/family resolution for this catalog
     ============================================================ */
  proto.collarProductFor = function (familyKey, colorKey) {
    return productFor(this.productMap, familyKey, colorKey);
  };
  /* ONE collar-width resolver, shared by the card render and the add
     handler — the same consolidation resolveLeashWidth already applies to
     leashes, and for the same reason: they used to compute it separately
     and could disagree.

     EVERY stocked width stays selectable for every dog. The anatomical
     matrix (FL.pickPrimaryWidth) decides only which one earns the ★; it
     never removes a chip and never marks one as wrong. Two visual states,
     positive framing only.

     The divergence this closes: pickPrimaryWidth returns null for a width
     the category allowlist excludes entirely (every Wide family on a toy
     or bully build — 12 of the 96 category/size/family cells). The card
     fell back to stockedWidths[0] and displayed "4.5cm"; _addCollarItem
     had no such fallback and committed `width: null`, which dropped the
     width from the set chip, the sidebar meta, the Review line and the
     "Strap width" cart property, and let resolveVariant match on size
     alone. Snapping `rec` to a genuinely stocked width here fixes the
     display AND the payload from one place. */
  proto.collarWidthOptions = function (familyKey, colorKey, category, sizeLetter) {
    var product = this.collarProductFor(familyKey, colorKey);
    var famDef = FL.PRODUCT_FAMILIES[familyKey] || {};
    var familyWidths = famDef.widths || [];
    // A product exposing no Width option at all (single cut, e.g. Aeris
    // Classic) must collapse to its family's fixed width, never be probed
    // width-by-width: resolveVariant treats a blank v.options.width as
    // matching ANY requested width, so every candidate would "match".
    var stocked = (product && productCarriesWidthData(product))
      ? familyWidths.filter(function (w) { return productHasWidth(product, w); })
      : (famDef.defaultWidth ? [famDef.defaultWidth] : familyWidths.slice(0, 1));
    var rec = FL.pickPrimaryWidth(familyKey, category, sizeLetter);
    if (stocked.length && (!rec || stocked.indexOf(rec) === -1)) rec = stocked[0];
    // An explicit pick always wins — but a stale one (e.g. after a breed
    // change or a colour swap narrowed the options) falls back silently.
    var chosen = this.state.collarWidths[familyKey];
    if (chosen && stocked.indexOf(chosen) === -1) chosen = null;
    return { stocked: stocked, rec: rec, width: chosen || rec || stocked[0] || null };
  };
  proto.leashProductFor = function (style, colorKey) {
    var key = style === "Hands Free" ? "leash-hands-free" : "leash";
    return productFor(this.productMap, key, colorKey);
  };
  /* ONE leash-width resolver, shared by the card render, the derived CTA
     state, and the add handler.

     They used to compute it three different ways, and the disagreement
     produced a silently dead button: the card filtered the two widths
     down to what the mapped product actually stocks (so an Omni Hands
     Free stocked only in 2cm rendered a live, correctly-priced "Add to
     set"), while _addLeashItem took the raw size-based recommendation —
     1.5cm for a small dog — resolved no variant against a 2cm-only
     product, and returned false. The shopper tapped Add to set, the
     button acknowledged nothing, and no leash was ever added. The derived
     CTA had the same flaw from the other end, labelling it "Unavailable
     in this leather" when the leather was fine and only the width wasn't
     stocked. A single resolver makes those three states incapable of
     disagreeing. */
  proto.leashStockedWidths = function (style, colorKey) {
    var product = this.leashProductFor(style, colorKey);
    if (!product) return [];
    // A product that exposes NO Width option at all must not be probed
    // width-by-width: resolveVariant treats a blank v.options.width as
    // matching ANY requested width, so every candidate "matches" and the
    // card renders a full selector whose chips all resolve to the same
    // single variant. Same single-SKU collapse the collar cards already
    // apply via productCarriesWidthData.
    if (!productCarriesWidthData(product)) return [];
    return LEASH_WIDTHS.filter(function (w) { return !!resolveVariant(product, { width: w }); });
  };
  proto.resolveLeashWidth = function (style, colorKey, autoWidth) {
    var stocked = this.leashStockedWidths(style, colorKey);
    var chosen = this.state.leashWidths[style];
    if (chosen && stocked.indexOf(chosen) !== -1) return chosen;
    if (autoWidth && stocked.indexOf(autoWidth) !== -1) return autoWidth;
    return stocked[0] || chosen || autoWidth || null;
  };
  proto.harnessProductFor = function (colorKey) {
    return productFor(this.productMap, "harness", colorKey);
  };
  proto.tagProductFor = function (design, isGift) {
    var byDesign = this.productMap.tags && this.productMap.tags[design];
    if (!byDesign) return null;
    return isGift ? byDesign.gift : byDesign.paid;
  };
  proto.bundleMatchProductFor = function (tier, familyOrNull, colorKey, leashStyle) {
    var root = tier === "bundle" ? this.productMap["bundle-match"]
      : tier === "walkkit-collar" ? this.productMap["walkkit-collar-match"]
      : this.productMap["walkkit-harness-match"];
    if (!root) return null;
    var byColor = familyOrNull ? (root[familyOrNull] && root[familyOrNull][colorKey]) : root[colorKey];
    return (byColor && byColor[leashStyle]) || null;
  };

  /* ============================================================
     Adaptive path -> step list
     ============================================================ */
  // Every path walks the COMPLETE Mix & Match chain — "start with one
  // piece" is an entry point that reorders the journey to lead with the
  // chosen piece, never a truncated single-item checkout that dead-ends
  // before the leash/harness upsell steps (and with them, any chance of
  // reaching the 2-item discount or the $200 free-tag threshold). Each
  // product step carries its own "skip" affordance, so the un-asked-for
  // steps cost one tap to decline.
  var PATH_STEPS = {
    full: ["profile", "collar", "leash", "harness", "tag"],
    collar: ["profile", "collar", "leash", "harness", "tag"],
    leash: ["profile", "leash", "collar", "harness", "tag"],
    harness: ["profile", "harness", "collar", "leash", "tag"]
  };
  var STEP_LABELS = { profile: "Your Dog", collar: "Collar", leash: "Leash", harness: "Harness", tag: "Tag" };

  /* ============================================================
     Core derivation — every screen and panel reads this
     ============================================================ */
  proto.deriveVals = function () {
    var self = this;
    var s = this.state;
    // Re-resolved every render: picks up a late FX rate and the modal's
    // published base the moment either becomes available.
    this.giftThresholdCents = this._resolveGiftThresholdCents();
    var breed = this.currentBreedProfile();
    var dogLabel = s.dogName ? esc(s.dogName) : "";
    var possessive = dogLabel ? dogLabel + "’s" : "Your";

    var pathSteps = PATH_STEPS[s.path] || PATH_STEPS.full;
    var railSteps = pathSteps.map(function (key) { return { key: key, label: STEP_LABELS[key] }; });
    var stepIndex = pathSteps.indexOf(s.step);

    var neckCm = this.resolvedNeckCm(breed);
    var chestCm = this.resolvedChestCm(breed);
    var neckSet = s.neck !== null, chestSet = s.chest !== null;
    var neckGrounded = neckSet || !!s.breedName;
    var chestGrounded = chestSet || !!s.breedName;
    var neckEval = FL.evaluateMeasurement("neck", neckCm, { breedProfile: breed, ageProfile: s.ageProfile });
    var chestEval = FL.evaluateMeasurement("chest", chestCm, { breedProfile: breed, ageProfile: s.ageProfile });

    // Strict interlock, ported verbatim from the calculators' rules:
    //  - an unresolved boundary tie (even one arriving via a breed
    //    estimate landing exactly on a node) yields NO orderable size
    //    until LOOSE/SNUG is answered;
    //  - a custom-zone measurement NEVER buckets into XL — it yields no
    //    standard size and routes to the bespoke-request mailto instead.
    // The boundary interlock applies ONLY to measurements the shopper
    // actually took. It asks "was the tape held loose or snug?" — a
    // question with no meaning for a number we derived from a breed
    // average, because nobody held a tape. Gating a breed estimate on it
    // was the hidden second cause of the harness desync: a Chihuahua's
    // 30cm chest estimate lands exactly on a size node, so the collar
    // resolved to XXS while the harness silently returned null and read
    // "Not set yet", with no visible reason and no way to act on it.
    var neckBlocked = neckSet && neckEval.showBoundary && !s.neckBoundaryChoice && !neckEval.isCustom;
    var chestBlocked = chestSet && chestEval.showBoundary && !s.chestBoundaryChoice && !chestEval.isCustom;
    var neckCustom = neckGrounded && neckEval.isCustom;
    var chestCustom = chestGrounded && chestEval.isCustom;
    // The *Est letters keep feeding width-matrix/leash-width heuristics
    // (which need a working assumption) but never gate an order.
    var collarSizeEst = this.resolvedCollarSize(breed);
    var harnessSizeEst = this.resolvedHarnessSize(breed);
    var collarSize = (neckGrounded && !neckBlocked && !neckCustom) ? collarSizeEst : null;
    var harnessSize = (chestGrounded && !chestBlocked && !chestCustom) ? harnessSizeEst : null;
    // Guardrails surface persistently on the step (not just in the modal)
    // — but only for USER-ENTERED measurements; comparing a breed's own
    // estimate against its own range is meaningless.
    var neckGuardrail = (neckSet && neckEval.showGuardrail) ? neckEval : null;
    var chestGuardrail = (chestSet && chestEval.showGuardrail) ? chestEval : null;

    // "Suggested" vs "chosen". A size derived from a breed average and a
    // size the shopper tapped are different claims, and the pill row used
    // to render them identically — solid ink either way. That single
    // conflation produced the toggle bug: tapping the highlighted pill
    // promoted an estimate to an explicit pick (no visible change), and
    // tapping again cleared it back to the estimate (no visible change),
    // so the control looked inert through two real state transitions.
    var neckSuggested = !neckSet && !!collarSizeEst && !!s.breedName;
    var chestSuggested = !chestSet && !!harnessSizeEst && !!s.breedName;

    // How the size we are about to SELL was arrived at, PER DIMENSION:
    //   'measured'      — they held a tape.
    //   'pill'          — they picked a size letter themselves.
    //   'breed-average' — the number came off the breed table.
    //   null            — no size resolved at all.
    //
    // Derived from how the size ACTUALLY RESOLVED, not from whether the
    // shopper performed a commit. That distinction was the whole bug:
    // while step 1 had a "Use Akita average" button, taking the estimate
    // wrote neckSource = 'breed-average' explicitly. Removing that fork —
    // correctly — removed the only thing that ever set it on the ordinary
    // path. Type a breed, press Continue, and s.neck stays null with
    // neckSource null, so the basis computed to null, the Review line
    // never rendered and _pieceLine emitted no property: the safeguard was
    // inert on the exact journey it exists for.
    //
    // An unset dimension that still resolves to a size letter through
    // s.breedName IS a breed average — that is the definition, whether or
    // not anybody pressed a button — so it is named as one here.
    //
    // Carried onto the cart line in _pieceLine, so an order records what
    // it was cut from. These are made-to-order, engraved goods; "sized
    // from a breed average, never measured" is the single most useful
    // fact on a fit complaint, and it cannot be reconstructed later.
    var basisFor = function (src, isSet, suggested, size) {
      if (src === 'measured' || src === 'pill' || src === 'breed-average') return src;
      if (!isSet && suggested && size) return 'breed-average';
      return isSet ? 'pill' : null;
    };
    var neckBasis = basisFor(s.neckSource, neckSet, neckSuggested, collarSize);
    var chestBasis = basisFor(s.chestSource, chestSet, chestSuggested, harnessSize);
    // Set-level roll-up, worst-case first: one estimated dimension makes
    // the set estimated.
    var sizingBasis = (neckBasis === 'breed-average' || chestBasis === 'breed-average')
      ? 'breed-average'
      : ((neckBasis === 'measured' || chestBasis === 'measured') ? 'measured'
        : ((neckBasis || chestBasis) ? 'pill' : null));

    // The real observed spread for this breed, age-adjusted the same way
    // the point estimate is (applyAgeProfile scales the default, so an
    // unscaled range beside a scaled midpoint would contradict it on the
    // puppy path). Null for the mixed/unknown fallback, which has no
    // honest range to quote.
    var ageFactor = s.ageProfile === 'puppy' ? FL.PUPPY_SHRINK_FACTOR : 1;
    var spread = function (pair) {
      if (!pair || pair[0] == null || pair[1] == null) return null;
      return [Math.round(pair[0] * ageFactor), Math.round(pair[1] * ageFactor)];
    };
    var neckSpread = spread(breed.neck);
    var chestSpread = spread(breed.chest);

    // "Mixed breed / not sure" is a UI label and an unrecognised typed
    // name is not a breed at all — both resolve to MIXED_FALLBACK. Neither
    // can be dropped into breed-shaped copy: "typical for an adult Mixed
    // breed / not sure" and "Measure Mixed breed / not sure" are what came
    // out when they were. On the fallback path the sizing prose talks
    // about an average dog instead, and the CTA addresses the dog by name
    // if we have one.
    var breedIsGeneric = !!breed.isFallback;
    var breedPhrase = breedIsGeneric ? 'an average dog' : esc(s.breedName);
    // The same idea in the possessive, for the several places that name
    // what the size was drawn FROM ("sized from Akita averages").
    var averagesPhrase = breedIsGeneric ? 'average sizing' : esc(s.breedName) + ' averages';
    var measureSubject = s.dogName ? esc(s.dogName) : (breedIsGeneric || !s.breedName ? 'your dog' : esc(s.breedName));

    var outOfRangeNeck = neckCustom;
    var outOfRangeChest = chestCustom;
    var breedNotFound = !!breed.isFallback && !!s.breedName;

    // Fitting note (WHY_TEXT) with the calculator's puppy no-upsizing
    // suffix — shown contextually on the collar step and review.
    var whyText = s.breedName ? (FL.WHY_TEXT[breed.category] || FL.WHY_TEXT.standard)(breed) : "";
    if (whyText && s.ageProfile === "puppy") {
      whyText += " Sized to their exact current measurements — no upsizing — since a loose fit isn’t safe for a growing puppy.";
    }

    // A "recommendation" is only honest when it's derived from something
    // the shopper actually told us — a breed or a real measurement. With
    // neither, every screen falls back to neutral catalog framing: no
    // "Recommended for your dog" badges, no personalized headings.
    var hasProfile = !!(s.breedName || s.neck !== null || s.chest !== null);

    var lastCollar = s.collars.length ? s.collars[s.collars.length - 1] : null;
    var lastLeash = s.leashes.length ? s.leashes[s.leashes.length - 1] : null;
    var lastHarness = s.harnesses.length ? s.harnesses[s.harnesses.length - 1] : null;

    // ---- Collar family/width/color resolution ----
    // Recommendation set mirrors the Size Guide's algorithm exactly:
    // EVERY family in the category's `main` list is recommended (1..n
    // cards), whether a breed was chosen or the rules default to standard.
    var recRules = FL.RECOMMENDATION_RULES[breed.category] || FL.RECOMMENDATION_RULES.standard;
    var recommendedFamilies = recRules.main;
    var altFamilies = recRules.alt || [];
    var recommendedFamily = recommendedFamilies[0];
    var collarFamily = s.collarFamily || (lastCollar && lastCollar.familyKey) || recommendedFamily;
    // Classic/Wide tab: defaults to whichever class the primary
    // recommendation belongs to; re-prioritizes (never hides) cards.
    var familyTabOf = function (fk) {
      return (((FL.PRODUCT_FAMILIES[fk] || {}).widths || []).indexOf("4.5cm") !== -1) ? "wide" : "classic";
    };
    var activeCollarTab = s.collarTab || familyTabOf(recommendedFamily);
    var collarWidthAuto = FL.pickPrimaryWidth(collarFamily, breed.category, collarSizeEst);
    var chosenCollarWidth = s.collarWidths[collarFamily] || null;
    var collarWidth = chosenCollarWidth || collarWidthAuto;
    var collarColor = s.collarColors[collarFamily] || "black";
    var collarProduct = this.collarProductFor(collarFamily, collarColor);
    // Same anatomical-stock gate the Size Calculator applies
    // (productCarriesWidthData): the width matrix can propose a width the
    // family allows in principle but THIS mapped product doesn't stock
    // (e.g. "2cm" for a family stocked only in 3cm) — which would leave
    // every variant lookup null and the CTA dead. Snap the default to a
    // width the product actually carries; an explicit user pick is kept.
    if (collarProduct && collarWidth && !chosenCollarWidth && !productHasWidth(collarProduct, collarWidth)) {
      var stockedWidths = ((FL.PRODUCT_FAMILIES[collarFamily] || {}).widths || []).filter(function (w) {
        return productHasWidth(collarProduct, w);
      });
      collarWidth = stockedWidths[0] || null;
    }
    // The active family's effective size: its per-card override, else the
    // inherited Step-1 size — the CTA prices/gates on exactly this.
    var activeCollarSize = s.collarSizes[collarFamily] || collarSize;
    var collarVariant = (collarProduct && activeCollarSize) ? resolveVariant(collarProduct, { size: activeCollarSize, width: collarWidth }) : null;
    var collarStampable = familyIsStampable(collarFamily);

    var collarFamilyCards = ["vero-classic", "aeris-classic", "vero-wide", "aeris-wide"].map(function (fk) {
      var allowedWidths = FL.getAllowedWidths(fk, breed.category, collarSizeEst);
      var cardColor = s.collarColors[fk] || "black";
      var product = self.collarProductFor(fk, cardColor);
      var range = priceRangeCents(product);
      // Widths come from the CATALOG: the family's declared widths
      // filtered to what this mapped Shopify product actually stocks —
      // with the calculator's single-SKU collapse: a product exposing no
      // Width option locks to the family's fixed defaultWidth (Aeris
      // Classic → strictly 3cm), never the full matrix list.
      var famDefC = FL.PRODUCT_FAMILIES[fk] || {};
      var familyWidths = famDefC.widths || [];
      // Single resolver — the add handler calls the exact same method, so
      // the width on screen is always the width committed to the cart.
      var wOpts = self.collarWidthOptions(fk, cardColor, breed.category, collarSizeEst);
      var stockedWidths = wOpts.stocked;
      var recWidth = wOpts.rec;
      var cardWidth = wOpts.width;
      // Per-card size: the card's own override, else the inherited
      // Step-1 size — every card shows its inline pills with this
      // highlighted (universal card UX, matching the harness).
      var cardSize = s.collarSizes[fk] || collarSize;
      // Stock reality (Size Guide's resolveFamilyOption): does a REAL
      // available variant exist at this card's size + width?
      // null = no size resolved yet (unknown), true/false once it is.
      var cardVariant = (cardSize && product) ? resolveVariant(product, { size: cardSize, width: cardWidth }) : null;
      return {
        key: fk,
        label: FAMILY_LABELS[fk],
        widthsLabel: (stockedWidths.length ? stockedWidths : familyWidths).join(" / "),
        fromCents: range && range.min,
        priceVaries: !!(range && range.max > range.min),
        blurb: FAMILY_DESCRIPTORS[fk].blurb,
        best: FAMILY_DESCRIPTORS[fk].best,
        recommended: recommendedFamilies.indexOf(fk) !== -1,
        altRecommended: altFamilies.indexOf(fk) !== -1,
        // A wide 4.5cm cross-sell is only SIZE-appropriate on medium+
        // necks (or a sighthound category, where wide IS the main rec) —
        // the badge layer consults this so an XS neck never gets an
        // unjustified "Also suitable" on a wide collar.
        altSizeAppropriate: allowedWidths.length > 0 &&
          (familyTabOf(fk) !== "wide" || breed.category === "sighthound" || ["M", "L", "XL"].indexOf(collarSizeEst) !== -1),
        tab: familyTabOf(fk),
        colorKey: cardColor,
        addedCount: s.collars.filter(function (c) { return c.familyKey === fk; }).length,
        image: imageFor(product, null),
        stockedWidths: stockedWidths,
        recWidth: recWidth,
        cardWidth: cardWidth,
        cardSize: cardSize,
        sizeOptions: FL.COLLAR_SIZES.map(function (szz) {
          return { sz: szz, ok: !!(product && resolveVariant(product, { size: szz, width: cardWidth })) };
        }),
        available: cardSize ? !!cardVariant : null,
        allowedWidths: allowedWidths
      };
    });
    // Tab re-prioritization (Size Guide rule): matching-tab cards sort to
    // the front, then main recommendations, then alt cross-sells — every
    // card stays visible with its badges intact.
    collarFamilyCards.sort(function (a, b) {
      var at = a.tab === activeCollarTab ? 1 : 0, bt = b.tab === activeCollarTab ? 1 : 0;
      if (at !== bt) return bt - at;
      var ar = a.recommended ? 2 : (a.altRecommended ? 1 : 0);
      var br = b.recommended ? 2 : (b.altRecommended ? 1 : 0);
      return br - ar;
    });

    // ---- Leash resolution — width auto-matches via the calculator's own
    // FL.LEASH_WIDTH_BY_* maps with the Size Guide's PRIORITY ORDER: the
    // harness/chest dimension wins whenever it's active or resolved
    // (added harness > resolved chest size > added collar > estimate). ----
    var leashWidthAuto = lastHarness
      ? FL.LEASH_WIDTH_BY_HARNESS_SIZE[lastHarness.size]
      : (harnessSize
        ? FL.LEASH_WIDTH_BY_HARNESS_SIZE[harnessSize]
        : (lastCollar
          ? FL.LEASH_WIDTH_BY_COLLAR_SIZE[lastCollar.size]
          : FL.LEASH_WIDTH_BY_COLLAR_SIZE[collarSizeEst]));
    /* WHICH dimension produced that number — resolved from the SAME chain,
       immediately beside it, so the two can never drift apart.

       They had drifted. The recommendation copy built its own label with
       the priority INVERTED (collar first, then harness), while the number
       above takes the chest first. A shopper with an M collar and a small
       chest therefore read "Recommended for your M Aeris Classic: 1.5cm" —
       a sentence whose own rule says M means 2cm. The number was right;
       the label named the wrong body part, so the tool looked broken at
       precisely the moment it was being most careful.

       `dimension` is the noun for the trailing "matched to your …" clause;
       `label` names the thing in the shopper's own terms. Note the final
       fallback uses collarSizeEst, never d.collarSize — the latter is null
       for a bespoke or boundary-blocked neck, which used to render the
       literal string "null collar". */
    var leashWidthBasis = lastHarness
      ? { dimension: "harness", label: lastHarness.size + " harness" }
      : (harnessSize
        ? { dimension: "chest", label: "chest size " + harnessSize }
        : (lastCollar
          ? { dimension: "collar", label: lastCollar.size + " " + (FAMILY_LABELS[lastCollar.familyKey] || "collar") }
          : { dimension: "neck", label: collarSizeEst + " collar" }));
    var leashColor = s.leashColors[s.leashStyle] || (lastCollar ? lastCollar.colorKey : "black");
    var leashWidth = this.resolveLeashWidth(s.leashStyle, leashColor, leashWidthAuto);
    var leashProduct = this.leashProductFor(s.leashStyle, leashColor);
    var leashVariant = leashProduct ? resolveVariant(leashProduct, { width: leashWidth }) : null;

    // ---- Harness resolution ----
    var harnessColor = s.harnessColor || (lastCollar ? lastCollar.colorKey : "black");
    var harnessProduct = this.harnessProductFor(harnessColor);
    var harnessVariant = (harnessProduct && harnessSize) ? resolveVariant(harnessProduct, { size: harnessSize }) : null;

    // ---- Running subtotal — EVERY committed item, individually, with its
    // own size/width/color carried through to the sidebar and review. ----
    // Full brand titles with exact specifications everywhere an item is
    // named: "Aeris Classic Collar · Black · 3cm · XL" — never a generic
    // "Collar" line.
    var pieces = [];
    s.collars.forEach(function (c) {
      var cName = (FAMILY_LABELS[c.familyKey] || "") + " Collar";
      var cSpec = [leatherDisplayName(c.colorKey), c.width, c.size].filter(Boolean).join(" · ");
      pieces.push({
        uid: c.uid, key: "collar", label: cName,
        // Which measurement this piece was cut from — read by Review to
        // show the sizing basis on the line it actually applies to.
        sizeKey: c.size ? "neck" : null,
        detail: cName + " · " + cSpec,
        meta: cSpec,
        extra: c.stampEnabled ? "Stamp: “" + esc(c.stampText) + "”" : "",
        cents: c.cents, image: c.image, colorKey: c.colorKey
      });
    });
    s.leashes.forEach(function (l) {
      var lName = (FL.LEASH_STYLE_DISPLAY_NAME[l.style] || l.style) + " Leash";
      var lSpec = [leatherDisplayName(l.colorKey), l.width].filter(Boolean).join(" · ");
      pieces.push({
        uid: l.uid, key: "leash", label: lName,
        detail: lName + " · " + lSpec,
        meta: lSpec,
        extra: "",
        cents: l.cents, image: l.image, colorKey: l.colorKey
      });
    });
    s.harnesses.forEach(function (h) {
      var hSpec = [leatherDisplayName(h.colorKey), h.size].filter(Boolean).join(" · ");
      pieces.push({
        uid: h.uid, key: "harness", label: "Vero Harness",
        sizeKey: h.size ? "chest" : null,
        detail: "Vero Harness · " + hSpec,
        meta: hSpec,
        extra: "",
        cents: h.cents, image: h.image, colorKey: h.colorKey
      });
    });
    var subtotalCents = pieces.reduce(function (sum, p) { return sum + (p.cents || 0); }, 0);
    var itemCount = pieces.length;
    var discountUnlocked = itemCount >= 2;
    var pricing = discountUnlocked ? FL.computeBundlePricing(pieces.map(function (p) { return p.cents; }), this.discountRate) : null;
    // Per-line discounted figures the moment the set unlocks — every
    // surface (sidebar rows, CTAs, mobile bar) shows the honest price,
    // not full prices with a correction at the very end.
    var rate = this.discountRate;
    pieces = pieces.map(function (p) {
      return Object.assign({}, p, { discountedCents: discountUnlocked ? Math.round(p.cents * (1 - rate)) : p.cents });
    });
    var discountedSubtotalCents = pieces.reduce(function (sum, p) { return sum + p.discountedCents; }, 0);
    var lineSavingsCents = subtotalCents - discountedSubtotalCents;

    /* Threshold parity with the store-wide free-gift modal.

       That modal is the older, store-wide authority on this promotion and
       it measures AFTER discount: eligibleSubtotal() sums each line's
       `final_line_price`, which is net of Shopify's discount allocations
       (see assets/free-gift-modal.js). This component measured BEFORE
       discount, off the raw merchandise sum.

       The two therefore disagreed on every set they both saw. With a 10%
       set discount and a 200 threshold, a 200.00 set posted 180.00 of
       final_line_price: the Fitting Room granted the free tag, the modal
       recomputed, found the cart short, and called removeAllGifts() —
       deleting the tag before the shopper reached the cart. Because the set
       discount is precisely what pushes the cart under the line, that fired
       on MOST qualifying sets rather than on an edge case.

       giftEligibleCents is the discounted per-line sum, which is what
       Shopify allocates and therefore what final_line_price will report. */
    var giftEligibleCents = discountedSubtotalCents;
    var giftUnlocked = giftEligibleCents >= this.giftThresholdCents;
    var giftProgressPct = Math.min(100, Math.round((giftEligibleCents / this.giftThresholdCents) * 100));
    var giftRemainingCents = Math.max(0, this.giftThresholdCents - giftEligibleCents);
    // Concrete, honest incentive maths for the "one more piece" nudge:
    // adding a second item discounts the WHOLE set, so the shopper saves
    // at least 10% of what's already in the basket — a real number, not a
    // vague "unlock savings".
    var pendingDiscountCents = discountUnlocked ? 0 : Math.round(subtotalCents * rate);

    // ---- Nametag resolution ----
    var tagDesign = s.tag ? s.tag.design : s.tagDesign;
    var tagSize = s.tag ? s.tag.size : s.tagSize;
    var tagPaidProduct = this.tagProductFor(tagDesign, false);
    var tagGiftProduct = this.tagProductFor(tagDesign, true);
    var tagPaidVariant = tagPaidProduct ? resolveVariant(tagPaidProduct, { size: tagSize }) : null;
    var tagGiftVariant = tagGiftProduct ? resolveVariant(tagGiftProduct, { size: tagSize }) : null;
    // TODO(edge-case): steer to Ripple / reword the $200 reward when a
    // design's gift stock is low/out (handoff §7's sold-out policy) —
    // needs inventory_quantity, which lillebjorn-product-json.liquid
    // doesn't emit today; `available` alone is what's wired here.
    var tagGiftAvailable = !!tagGiftVariant;

    /* FOUR gift states, derived once so no surface can invent a fifth.

       The bug this replaces: the tag step gated its hero on
       `giftUnlocked && tagGiftAvailable` but printed giftRemainingCents in
       the fallback branch. When the threshold WAS met and the hidden $0
       gift product was simply unmapped — a documented, easy-to-miss
       merchant step (see the section schema's own warning about the
       duplicate pebble-nametag-32mm listing) — giftUnlocked was true,
       tagGiftAvailable false, and giftRemainingCents exactly 0, so the
       amber box read, verbatim: "Add one more piece and this tag is free —
       you're €0.00 away."

       INVARIANT: giftRemainingCents may only be formatted into copy when
       giftState === 'progressing'. */
    var giftState = !tagGiftVariant ? 'unconfigured'
      : (giftUnlocked ? 'unlocked'
        : (itemCount ? 'progressing' : 'idle'));

    // ---- Review math ----
    // The tag only participates in totals when the shopper actually kept
    // it — a skipped tag adds neither a price line nor a "you save" claim.
    var tagValueCents = (tagPaidVariant && tagPaidVariant.price) || 0;
    var tagChosen = !!s.tag;
    var tagIsGift = tagChosen && giftUnlocked && tagGiftAvailable;
    var reviewDiscountCents = pricing ? pricing.savingsCents : 0;
    var reviewGiftSavingsCents = tagIsGift ? tagValueCents : 0;
    // The three review lines must actually reconcile: Subtotal − You save
    // = Total, arithmetic a shopper can do in their head. Previously the
    // subtotal EXCLUDED the tag while the total ADDED it back, so a set
    // with a paid $32 tag read "Subtotal $100 · You save $10 · Total
    // $122" — three numbers that visibly don't add up, on the one screen
    // where trust in the pricing decides the sale. The tag now sits in
    // the subtotal at its real value, and the gift is expressed the same
    // way every other saving is: as a deduction.
    var reviewSubtotalCents = subtotalCents + (tagChosen ? tagValueCents : 0);
    var youSaveCents = reviewDiscountCents + reviewGiftSavingsCents;
    var reviewTotalCents = reviewSubtotalCents - youSaveCents;

    // TODO(edge-case): the collar-vs-leash width mismatch banner (handoff
    // §9, wireframe 1o) — condition below is the real rule (a genuinely
    // slim 1.5cm leash next to a 4.5cm Wide collar), rendering it in
    // screenReview is the stub.
    var mismatchWarning = s.collars.some(function (c) { return c.width === "4.5cm"; }) &&
      s.leashes.some(function (l) { return l.width === "1.5cm"; });

    // Spelling confirmation is only demanded when something is actually
    // personalized (a stamped collar or an engraved tag).
    var needsSpellingConfirm = s.collars.some(function (c) { return c.stampEnabled; }) ||
      (tagChosen && !s.tag.noEngraving && !!(s.tag.engraveName || s.tag.engravePhone));

    return {
      breed: breed, dogLabel: dogLabel, possessive: possessive, hasProfile: hasProfile,
      pathSteps: pathSteps, railSteps: railSteps, stepIndex: stepIndex,
      neckCm: neckCm, chestCm: chestCm, collarSize: collarSize, harnessSize: harnessSize,
      neckEval: neckEval, chestEval: chestEval,
      outOfRangeNeck: outOfRangeNeck, outOfRangeChest: outOfRangeChest, breedNotFound: breedNotFound,
      neckBlocked: neckBlocked, chestBlocked: chestBlocked,
      neckCustom: neckCustom, chestCustom: chestCustom,
      neckGuardrail: neckGuardrail, chestGuardrail: chestGuardrail,
      neckSuggested: neckSuggested, chestSuggested: chestSuggested,
      sizingBasis: sizingBasis, neckBasis: neckBasis, chestBasis: chestBasis,
      neckSpread: neckSpread, chestSpread: chestSpread,
      breedIsGeneric: breedIsGeneric, breedPhrase: breedPhrase, measureSubject: measureSubject,
      averagesPhrase: averagesPhrase,
      // Bespoke = the dog is genuinely outside the standard matrix, which
      // is a product tier, not an error. Carried explicitly so every
      // surface can present it as the premium option it is.
      neckBespoke: neckCustom, chestBespoke: chestCustom,
      bespokeFor: s.dogName ? esc(s.dogName) : (s.breedName ? esc(s.breedName) : 'your dog'),
      whyText: whyText, activeCollarTab: activeCollarTab,

      collarFamily: collarFamily, collarWidth: collarWidth, collarColor: collarColor,
      collarProduct: collarProduct, collarVariant: collarVariant, collarStampable: collarStampable,
      collarFamilyCards: collarFamilyCards, recommendedFamily: recommendedFamily,

      leashWidth: leashWidth, leashWidthAuto: leashWidthAuto, leashWidthBasis: leashWidthBasis,
      leashColor: leashColor, leashProduct: leashProduct, leashVariant: leashVariant,
      harnessColor: harnessColor, harnessProduct: harnessProduct, harnessVariant: harnessVariant,

      pieces: pieces, subtotalCents: subtotalCents, itemCount: itemCount,
      discountedSubtotalCents: discountedSubtotalCents, lineSavingsCents: lineSavingsCents,
      discountUnlocked: discountUnlocked, pricing: pricing,
      giftUnlocked: giftUnlocked, giftProgressPct: giftProgressPct, giftRemainingCents: giftRemainingCents,
      giftState: giftState,
      pendingDiscountCents: pendingDiscountCents, discountPct: Math.round(this.discountRate * 100),

      tagDesign: tagDesign, tagSize: tagSize,
      tagPaidProduct: tagPaidProduct, tagGiftProduct: tagGiftProduct,
      tagPaidVariant: tagPaidVariant, tagGiftVariant: tagGiftVariant, tagGiftAvailable: tagGiftAvailable,
      tagValueCents: tagValueCents, tagChosen: tagChosen, tagIsGift: tagIsGift,

      reviewSubtotalCents: reviewSubtotalCents, reviewDiscountCents: reviewDiscountCents,
      reviewGiftSavingsCents: reviewGiftSavingsCents, reviewTotalCents: reviewTotalCents,
      youSaveCents: youSaveCents, mismatchWarning: mismatchWarning,
      needsSpellingConfirm: needsSpellingConfirm,

      moneyFormat: this.moneyFormat
    };
  };

  /* ============================================================
     Bundle-match + cart building
     ============================================================ */
  proto.attemptBundleMatch = function (d) {
    var s = this.state;
    // Pre-priced set SKUs map 1:1 pieces only — any multi-quantity
    // category falls back to individual cart lines.
    var c = s.collars, l = s.leashes, h = s.harnesses;
    if (c.length === 1 && l.length === 1 && h.length === 1) {
      var bundleProduct = this.bundleMatchProductFor("bundle", c[0].familyKey, c[0].colorKey, l[0].style);
      var bundleVariant = bundleProduct ? resolveVariant(bundleProduct, { size: c[0].size, width: c[0].width }) : null;
      if (bundleVariant) return { tier: "bundle", variantId: bundleVariant.id, cents: bundleVariant.price, covers: ["collar", "leash", "harness"] };
    }
    if (c.length === 1 && l.length === 1 && h.length === 0) {
      var kitProduct = this.bundleMatchProductFor("walkkit-collar", c[0].familyKey, c[0].colorKey, l[0].style);
      var kitVariant = kitProduct ? resolveVariant(kitProduct, { size: c[0].size, width: c[0].width }) : null;
      if (kitVariant) return { tier: "walkkit", variantId: kitVariant.id, cents: kitVariant.price, covers: ["collar", "leash"] };
    }
    if (c.length === 0 && l.length === 1 && h.length === 1) {
      var hkProduct = this.bundleMatchProductFor("walkkit-harness", null, h[0].colorKey, l[0].style);
      var hkVariant = hkProduct ? resolveVariant(hkProduct, { size: h[0].size }) : null;
      if (hkVariant) return { tier: "walkkit", variantId: hkVariant.id, cents: hkVariant.price, covers: ["leash", "harness"] };
    }
    return null;
  };

  proto.buildCartItems = function (d) {
    var s = this.state;
    var self = this;
    var items = [];
    var match = this.attemptBundleMatch(d);
    var lists = { collar: s.collars, leash: s.leashes, harness: s.harnesses };

    if (match) {
      // The discount rate is a merchant setting (data-set-discount-percent).
      // These two labels hardcoded "10%", so a shop configured at 15%
      // would ship cart lines — and therefore order confirmations and
      // packing slips — stating a discount that doesn't match what was
      // charged.
      var pctLabel = Math.round(this.discountRate * 100) + "% off set";
      var setLabel = (match.tier === "bundle" ? "Complete Bundle (" : "Walk Kit (") + pctLabel + ")";
      var mainProps = { "Set": setLabel };
      var soloCollar = s.collars[0];
      if (soloCollar && soloCollar.stampEnabled && match.covers.indexOf("collar") !== -1) {
        mainProps["Collar stamp"] = soloCollar.stampText;
      }
      items.push({ id: match.variantId, quantity: 1, properties: mainProps });
      Object.keys(lists).forEach(function (type) {
        if (match.covers.indexOf(type) !== -1) return;
        lists[type].forEach(function (pc) { items.push(self._pieceLine(pc, type, d)); });
      });
    } else {
      Object.keys(lists).forEach(function (type) {
        lists[type].forEach(function (pc) { items.push(self._pieceLine(pc, type, d)); });
      });
    }

    if (s.tag) {
      var isGift = d.giftUnlocked && d.tagGiftAvailable;
      var tagVariantId = isGift ? (d.tagGiftVariant && d.tagGiftVariant.id) : (d.tagPaidVariant && d.tagPaidVariant.id);
      if (tagVariantId) {
        var tagProps = {};
        // Interop with the store-wide free-gift modal (assets/free-gift-modal.js,
        // rendered on every page from layout/theme.liquid). It listens for
        // "cart:refresh" — which addToCart below dispatches — and decides
        // whether the cart already contains a complimentary line.
        //
        // Without these two properties it could not see this tag at all:
        // its gift list comes from settings.free_gift_products (theme
        // settings) while ours comes from the section's own nametag pickers,
        // and the two are configured independently. So it treated a
        // qualifying set as giftless and opened its own modal offering a
        // SECOND free gift. The source stamp is what stops its
        // removeAllGifts() from deleting this line in the opposite case.
        // Both keys are underscore-prefixed, so neither renders in the cart
        // (see main-cart.liquid / cart-drawer.liquid's property_first_char check).
        if (isGift) {
          tagProps["_free_gift"] = "true";
          tagProps["_free_gift_source"] = "fitting-room";
          // The line's own escape route. This complimentary tag is a
          // separate hidden $0 PRODUCT, not a discounted variant of the
          // paid one, so nothing can "un-free" it by editing properties —
          // it can only be swapped for the paid variant. If the cart later
          // stops qualifying, the modal re-prices this line instead of
          // deleting it (engraving and all), and this is the only way it
          // can know which variant to swap in: the gift->paid mapping
          // lives in THIS section's settings, which the store-wide script
          // cannot read. Omitted when the paid product is unmapped, in
          // which case the line is simply left alone.
          var upgradeVariant = d.tagPaidVariant && d.tagPaidVariant.id;
          if (upgradeVariant) tagProps["_free_gift_upgrade_variant"] = String(upgradeVariant);
        }
        if (s.tag.noEngraving) tagProps["Engraving"] = "None — blank tag";
        else {
          if (s.tag.engraveName) tagProps["Engraving name"] = s.tag.engraveName;
          if (s.tag.engravePhone) tagProps["Engraving phone"] = s.tag.engravePhone;
        }
        items.push({ id: tagVariantId, quantity: 1, properties: tagProps });
      }
    }
    // Coherence gate. Every line is posted in ONE /cart/add.js call, so
    // Shopify accepts the whole batch or none of it — but a line carrying
    // an undefined id would make Shopify reject the entire request with a
    // generic error, losing the good lines with it. A variant id can go
    // missing legitimately: a snapshot restored from localStorage days
    // later may reference a variant the merchant has since deleted. Drop
    // the unresolvable lines here and report them, so the shopper gets
    // the rest of their set plus a specific reason, rather than an
    // all-or-nothing failure they can't act on.
    var dropped = [];
    var clean = items.filter(function (it) {
      var ok = it && (typeof it.id === "number" || typeof it.id === "string") && it.id;
      if (!ok) dropped.push(it);
      return ok;
    });
    this._lastDroppedLines = dropped.length;
    return clean;
  };
  proto._pieceLine = function (piece, key, d) {
    var props = {};
    // The set discount needs TWO or more pieces (deriveVals' discountUnlocked
    // = itemCount >= 2). This property was emitted unconditionally, so a
    // single collar shipped a cart line reading "Set: Custom Set (10%
    // preview — confirmed at checkout)" on an order that receives no
    // discount at all — and it is genuinely visible to the shopper:
    // sections/main-cart.liquid and snippets/cart-drawer.liquid both render
    // every property whose key doesn't start with "_". So it reached the
    // cart page, the cart drawer, the order confirmation email and the
    // workshop's packing slip.
    if (d && d.discountUnlocked) {
      props["Set"] = "Custom Set (" + d.discountPct + "% — applied at checkout)";
      // Underscore-prefixed: carried for fulfilment/support, never rendered.
      props["_lb_set_size"] = String(d.itemCount);
    }
    // Spec echoed onto the line item. The variant already encodes size and
    // width, but the variant TITLE is not guaranteed to surface them in
    // the cart, on the order confirmation, or on the workshop's packing
    // slip — and these are made-to-order goods where a mis-read width is
    // a remake. Cheap to carry, expensive to omit.
    if (piece.size) props["Size"] = piece.size;
    // WHERE THAT SIZE CAME FROM. Underscore-prefixed, so it rides the
    // order, the confirmation and the packing slip without ever showing
    // in the cart (same convention as _lb_set_size above).
    //
    // This is the record that matters on a fit complaint for a hand-cut,
    // engraved, non-resellable piece: it says whether the customer gave
    // us a measurement, picked a size letter themselves, or accepted our
    // breed average — and it can only be captured here, at the moment of
    // the order. It is also the feedback loop on the breed table: a
    // breed whose averages generate returns will show up in this field
    // long before anyone thinks to audit the numbers.
    // Per dimension, like the Review line: a collar is cut from the neck
    // and a harness from the chest, so a measured neck must not stamp the
    // harness "Measured by customer" because the SET-level roll-up said
    // so. Leashes have no size and never reach this.
    var pieceBasis = key === "collar" ? (d && d.neckBasis)
      : (key === "harness" ? (d && d.chestBasis) : null);
    if (piece.size && pieceBasis) {
      props["_lb_sizing_basis"] = pieceBasis === "breed-average"
        ? ((d.breedIsGeneric || !this.state.breedName ? "Average sizing" : this.state.breedName + " average") + " — not measured")
        : (pieceBasis === "measured" ? "Measured by customer" : "Size chosen by customer");
    }
    if (piece.width) props["Strap width"] = piece.width;
    if (piece.colorKey) props["Leather"] = leatherDisplayName(piece.colorKey);
    if (key === "collar" && piece.stampEnabled && piece.stampText) props["Stamp"] = piece.stampText;
    return { id: piece.variantId, quantity: 1, properties: props };
  };

  /* ============================================================
     Screen templates
     ============================================================ */

  /* Cheapest live variant across every colourway of a category — the entry
     screen's "from" prices used to be hardcoded US dollar strings
     ("from $249", "from $69"), so they silently lied the moment a price
     changed in Shopify AND rendered dollars to a shopper browsing in
     euros or pounds while every other price on the page used the shop's
     real money_format. */
  proto._categoryFromCents = function (kind) {
    var self = this;
    var min = null;
    function consider(product) {
      var r = priceRangeCents(product);
      if (r && (min === null || r.min < min)) min = r.min;
    }
    FL.LEATHERS.forEach(function (l) {
      if (kind === "collar") {
        Object.keys(FAMILY_LABELS).forEach(function (fk) { consider(self.collarProductFor(fk, l.key)); });
      } else if (kind === "leash") {
        FL.LEASH_STYLE_OPTIONS.forEach(function (st) { consider(self.leashProductFor(st, l.key)); });
      } else {
        consider(self.harnessProductFor(l.key));
      }
    });
    return min;
  };
  proto._fromLabel = function (kind) {
    var cents = this._categoryFromCents(kind);
    return cents === null ? "" : "from " + money(cents, this.moneyFormat);
  };

  proto.screenEntry = function (d) {
    var self = this;
    var heroImg = this.heroImage || firstKitImageUrl(this.productMap["kit-imagery"] && this.productMap["kit-imagery"].bundle);
    var trust = this.reviewRating != null ? (this.reviewRating.toFixed(1) + " ★ · " + this.reviewCount + " reviews") : "";
    // Full-set "from": the three cheapest pieces WITH the set discount
    // already applied, so the headline number is the one the shopper will
    // actually be charged.
    var partMins = ["collar", "leash", "harness"].map(function (k) { return self._categoryFromCents(k); });
    var setFrom = partMins.every(function (v) { return v !== null; })
      ? "from " + money(Math.round(partMins.reduce(function (a, b) { return a + b; }, 0) * (1 - this.discountRate)), this.moneyFormat)
      : "";
    return '' +
      // No top bar on the entry screen.
      //
      // Once the duplicated wordmark went, this row held one right-aligned
      // link above an otherwise empty strip — and it put a shortcut
      // ("Quick Assemble") ABOVE the sentence that explains what is being
      // assembled. A shopper met the escape hatch before the offer. The
      // button now sits after the hero copy, where it reads as the
      // alternative to the four cards below it rather than as chrome, and
      // the screen opens directly on the headline.
      '<div class="lb-fr__body lb-fr__entry" style="text-align:center">' +
      '<p class="lb-fr__eyebrow" style="color:var(--lbfr-accent-link);margin-bottom:12px">The Fitting Room</p>' +
      '<h1 class="lb-fr__h1" style="font-size:var(--lbfr-t-hero);max-width:560px;margin:0 auto 12px">Let’s build your dog’s set.</h1>' +
      '<p class="lb-fr__sub" style="max-width:460px;margin:0 auto;font-size:var(--lbfr-t-lead)">Fitted collar, matched leash, sized harness — the full set carries ' + d.discountPct + '% off and a complimentary engraved tag.</p>' +
      (trust ? '<p style="font-size:var(--lbfr-t-micro);letter-spacing:0.16em;color:var(--lbfr-ink-soft);margin:14px 0 0">' + esc(trust) + '</p>' : '') +
      '<div class="lb-fr__entry-shortcut">' +
      '<button class="lb-fr__express' + (this.state.expressMode ? " lb-fr__express--active" : "") + '" data-action="expressStart">Quick Assemble →</button>' +
      '</div>' +
      '</div>' +
      '<div class="lb-fr__body" style="padding:0 var(--lbfr-gutter) 30px">' +
      '<div class="lb-fr__cardgrid">' +
      '<button class="lb-fr__scard" style="grid-column:1/-1" data-action="choosePath" data-path="full">' +
      '<span class="lb-fr__badge">Best value</span>' +
      (heroImg ? '<span class="lb-fr__scard-media" style="aspect-ratio:21/8;background-image:url(' + heroImg + ')"></span>' : '<span class="lb-fr__scard-media lb-fr__skel" style="aspect-ratio:21/8"></span>') +
      '<span class="lb-fr__scard-body">' +
      '<span class="lb-fr__scard-titlerow"><span class="lb-fr__scard-name" style="font-size:var(--lbfr-t-head)">The Full Set</span><span class="lb-fr__scard-price">' + setFrom + '</span></span>' +
      '<span class="lb-fr__scard-meta">Collar · Leash · Harness · Engraved tag</span>' +
      '</span></button>' +
      ["collar", "leash", "harness"].map(function (p) {
        var label = p === "collar" ? "The Collar" : p === "leash" ? "The Leash" : "The Harness";
        var from = self._fromLabel(p);
        var product = p === "collar" ? self.collarProductFor("vero-classic", "black")
          : p === "leash" ? self.leashProductFor("Classic", "black")
          : self.harnessProductFor("black");
        var img = imageFor(product, null);
        return '<button class="lb-fr__scard" data-action="choosePath" data-path="' + p + '">' +
          (img ? '<span class="lb-fr__scard-media" style="background-image:url(' + img + ')"></span>' : '<span class="lb-fr__scard-media lb-fr__skel"></span>') +
          '<span class="lb-fr__scard-body">' +
          '<span class="lb-fr__scard-titlerow"><span class="lb-fr__scard-name">' + label + '</span><span class="lb-fr__scard-price">' + from + '</span></span>' +
          '<span class="lb-fr__scard-meta">Start here — add the rest as you go</span>' +
          '</span></button>';
      }).join('') +
      '</div>' +
      (this.heroTestimonial ? '<p style="font-style:italic;font-size:var(--lbfr-t-caption);line-height:1.6;color:var(--lbfr-ink-soft);margin:28px auto 0;max-width:480px;text-align:center">“' + esc(this.heroTestimonial) + '”</p>' : '') +
      '</div>' +
      '<div class="lb-fr__foot" style="justify-content:center"><span class="lb-fr__trust">Complimentary Worldwide Shipping · Free stamping &amp; engraving · Handcrafted to order</span></div>';
  };

  // Shared by the profile render and the combobox keyboard handler so
  // both always agree on which options are visible.
  proto._breedMatches = function () {
    var q = this.state.breedQuery;
    return FL.BREED_NAMES.filter(function (n) {
      return !q || n.toLowerCase().indexOf(q.toLowerCase()) !== -1;
    }).slice(0, 6);
  };

  /* ============================================================
     Screen 1's CTA — the payload, the basis, and the check.

     "Continue" is a TRANSPORT verb: it describes motion between screens,
     carries no payload, and is the default label of every wizard on the
     internet. That is the correct word when nothing is being decided, and
     step 1 is not that screen — it settles the size that every later step
     inherits and the cart line is cut from. One label spanning all four
     bases (measured / pill / breed-average / bespoke) cannot inform, and
     the step tracked all of that in state only to discard it at the one
     control where it mattered.

     Three jobs, split so each does one thing:
       ctaLabel     — WHAT is being carried forward ("Continue with XL")
       ctaMeta      — WHAT IT RESTS ON ("...our starting point for Akita")
       ctaSecondary — the alternative to the action about to be taken

     The goal is not hesitation, it is ENCODING. A shopper who registers
     "XL, not measured" can revisit it at Review; a click that never
     registered becomes "your site told me XL" on a fit complaint.

     Note on hierarchy, because this ground has been walked before: an
     earlier pass forked this CTA into "Measure Akita" (primary) + "Use
     Akita average" (secondary) and was correctly reverted — the loudest
     button on the screen asked for a tape measure the shopper rarely has
     to hand. That reversal stands. The difference here is direction:
     Continue stays solid, primary and always enabled; the measure trigger
     is 11px uppercase ink-soft text on a transparent border, in the
     ctaSecondary slot the product steps already use. Same neighbourhood,
     opposite hierarchy. It exists because "Measure precisely" currently
     lives in a pill-row header ~600px up the page on desktop, and off
     screen entirely on mobile where the CTA is in a sticky bar — so the
     two controls representing the actual choice have never once appeared
     together.

     Every slot used here already ships, is already styled, and already
     renders in BOTH the desktop foot and the mobile bar. Step 1 was the
     only step passing none of them.
     ============================================================ */
  proto._profileCta = function (d) {
    var s = this.state;

    // Who the copy addresses. NOT d.measureSubject: that falls back to the
    // BREED name, so a blank name field yields "Cut to Akita's own
    // measurements" — a sentence about the breed standard, not about this
    // dog, in the one place we are promising to cut to THIS dog.
    var subject = s.dogName ? esc(s.dogName) : 'your dog';

    var dims = [
      { key: 'neck', noun: 'Collar', word: 'neck', size: d.collarSize, basis: d.neckBasis, bespoke: d.neckBespoke },
      { key: 'chest', noun: 'Harness', word: 'chest', size: d.harnessSize, basis: d.chestBasis, bespoke: d.chestBespoke }
    ];
    // "Carried" = a size we will sell that nobody measured. That is the
    // set this whole CTA exists to name, and it deliberately includes
    // 'pill': a tapped size letter is an owned choice, but it is still not
    // a measurement, and the shopper is entitled to be told so.
    var bespoke = dims.filter(function (x) { return x.bespoke; });
    var carried = dims.filter(function (x) { return x.size && x.basis !== 'measured'; });
    var measured = dims.filter(function (x) { return x.size && x.basis === 'measured'; });

    var base = { back: false, ctaAction: 'goCollarLeashHarness', ctaEnabled: true };

    // ---- Nothing resolved at all ----
    // Unchanged behaviour: the primary action is honestly a skip, never a
    // "Continue" that walks into locked steps.
    if (!bespoke.length && !carried.length && !measured.length) {
      return Object.assign({}, base, {
        ctaLabel: 'Skip size for now →',
        ctaShort: 'Skip size →',
        ctaSecondary: { action: 'openMeasure', variant: 'outline', label: 'Measure (1 min)', short: 'Measure', data: { key: 'neck' } }
      });
    }

    // ---- Bespoke outranks everything ----
    // Off-matrix currently reads like the calculator failed, and the
    // shopper's rational next move is to skip the piece. Named as a tier
    // it reads as what it is: the premium path. No secondary — there is
    // no standard size to check against.
    if (bespoke.length) {
      return Object.assign({}, base, {
        ctaLabel: 'Continue with a bespoke fit →',
        ctaShort: 'Continue →',
        ctaMeta: 'Beyond standard sizing — cut by hand to ' + subject + '’s numbers',
        ctaMetaShort: 'Bespoke · cut by hand to ' + subject + '’s numbers'
      });
    }

    // ---- Everything grounded was measured ----
    // The reward state. Brass tone (the default — no ctaMetaTone), and the
    // secondary WITHDRAWS: removing a control is the confirmation. At this
    // price point that reads better than praise.
    if (!carried.length) {
      return Object.assign({}, base, {
        ctaLabel: 'Continue →',
        ctaShort: 'Continue →',
        ctaMeta: 'Cut to ' + subject + '’s own measurements',
        ctaMetaShort: 'Cut to ' + subject + '’s measurements'
      });
    }

    // ---- Something is carried forward unverified ----
    var letters = carried.map(function (x) { return x.size; });
    var same = letters[0] === letters[letters.length - 1];
    // Short form drops the spaces: a 40-char uppercase label at 0.16em
    // tracking wraps to three lines in a 375px bar — a failure this
    // component has already shipped once.
    var payloadLong = same ? letters[0] : letters.join(' / ');
    var payloadShort = same ? letters[0] : letters.join('/');

    // The same clause the pill legend uses, so the button, the meta line
    // and the legend finally say the same thing in the same words.
    var forBreed = (s.breedName && !d.breedIsGeneric) ? ' for ' + d.breedPhrase : '';
    var phraseFor = function (x, plural) {
      if (x.basis === 'breed-average') {
        return (plural ? 'our starting sizes' : 'our starting point') + forBreed;
      }
      return plural ? 'the sizes you chose' : 'the size you chose';
    };

    // Phone variants of the same three sentences, each written to survive
    // a 320px bar on one line (~40 characters at 11px). Same claim, fewer
    // words — "not measured" is load-bearing and always last.
    var shortFor = function (x) { return x.basis === 'breed-average' ? 'our starting point' : 'your choice'; };
    var metaShort;
    var meta;
    if (carried.length === 1 && measured.length) {
      // One measured, one not. Name WHICH dimension is still unverified —
      // "XL" alone would be ambiguous with a measured size on screen.
      meta = carried[0].noun + ' is ' + phraseFor(carried[0], false) + ' — not measured';
      metaShort = carried[0].noun + ' ' + carried[0].size + ' · not measured';
    } else if (carried.length === 1) {
      meta = carried[0].size + ' is ' + phraseFor(carried[0], false) + ' — not measured';
      metaShort = carried[0].size + ' is ' + shortFor(carried[0]) + ' · not measured';
    } else if (carried[0].basis === carried[1].basis) {
      meta = 'These are ' + phraseFor(carried[0], true) + ' — not measured';
      metaShort = carried[0].size + ' / ' + carried[1].size + ' · ' + shortFor(carried[0]) + ' · not measured';
    } else {
      // Mixed bases across the two dimensions — one tapped, one still off
      // the breed table. This is a COMMON path, not an edge case: tapping
      // the suggested collar pill records 'pill' for the neck while the
      // chest stays 'breed-average', so any shopper who confirms one size
      // lands here. It gets named per dimension rather than a vague
      // catch-all, because "XL" alone is ambiguous with two sizes on
      // screen carrying two different bases.
      var word = function (x) { return x.basis === 'breed-average' ? 'estimated' : 'chosen'; };
      meta = carried[0].noun + ' ' + carried[0].size + ' ' + word(carried[0]) +
        ' · ' + carried[1].noun + ' ' + carried[1].size + ' ' + word(carried[1]) + ' — not measured';
      metaShort = carried[0].size + ' / ' + carried[1].size + ' · not measured';
    }

    // Deep-link the check to the dimension that actually needs it, so
    // measuring the neck silently narrows the CTA area to the chest.
    var target = carried[0];
    var named = carried.length === 1 && measured.length;
    return Object.assign({}, base, {
      ctaLabel: 'Continue with ' + payloadLong + ' →',
      ctaShort: 'Continue · ' + payloadShort + ' →',
      ctaMeta: meta,
      ctaMetaShort: metaShort,
      ctaMetaTone: 'neutral',
      ctaSecondary: {
        action: 'openMeasure',
        variant: 'outline',
        label: named ? 'Measure ' + target.word + ' (1 min)' : 'Measure (1 min)',
        // Phone label never names the dimension. "Measure chest" is 38px
        // wider, and at 320px that pushed the primary into a two-line
        // wrap and the bar past its budget. Nothing is lost: the basis
        // line directly above already reads "Harness XL · not measured",
        // and the button still deep-links to that dimension via data-key.
        short: 'Measure',
        data: { key: target.key }
      }
    });
  };

  proto.screenProfile = function (d) {
    var s = this.state;
    var breedNames = this._breedMatches();
    return this._stepShell(d, 'profile',
      '<h1 class="lb-fr__h1">First, a little about your dog</h1>' +
      '<p class="lb-fr__sub" style="font-size:var(--lbfr-t-caption);max-width:420px">Name and breed shape the tag engraving and the collar’s proportions.</p>' +
      '<p class="lb-fr__q" style="margin-top:20px">Puppy or adult?</p>' +
      '<div class="lb-fr__seg" style="margin-bottom:20px">' +
      '<button data-action="setAge" data-age="puppy" aria-pressed="' + (s.ageProfile === "puppy") + '">Puppy</button>' +
      '<button data-action="setAge" data-age="adult" aria-pressed="' + (s.ageProfile === "adult") + '">Adult</button></div>' +
      '<p class="lb-fr__q">Your dog’s name <span style="font-weight:400;text-transform:none;letter-spacing:0">(optional)</span></p>' +
      // The 380px cap is a readable-field-length rule, not a layout rule.
      // Left inline it also applied at 430px — where the measure is only
      // 398px — so the two inputs stopped 18px short of the fitcard and
      // pill rows directly beneath them. It lives in CSS now and lifts on
      // mobile, where the measure is already narrower than 380.
      '<input class="lb-fr__field lb-fr__profile-input" style="margin-bottom:20px" data-input="dogName" placeholder="e.g. Luna" value="' + esc(s.dogName) + '">' +
      '<p class="lb-fr__q">Breed</p>' +
      '<div class="lb-fr__profile-input" style="position:relative">' +
      '<input class="lb-fr__field" data-input="breed" role="combobox" aria-expanded="' + !!s.showBreedList + '" aria-autocomplete="list" aria-controls="lbfr-breed-list" placeholder="Start typing… e.g. Beagle" value="' + esc(s.breedQuery) + '" autocomplete="off">' +
      (s.showBreedList ? '<div class="lb-fr__card" id="lbfr-breed-list" role="listbox" style="padding:4px 0;margin-top:4px;position:absolute;left:0;right:0;z-index:9999;max-height:280px;overflow-y:auto;background:#fff;border:1px solid #e5e0d8;box-shadow:0 12px 32px rgba(35,29,23,.08)">' +
        (breedNames.length ? breedNames.map(function (n, i) {
          var active = i === s.breedActiveIdx;
          return '<button class="lb-fr__bopt" role="option" aria-selected="' + active + '" data-active="' + active + '" data-action="pickBreed" data-name="' + esc(n) + '">' + esc(n) + '</button>';
        }).join('') : '<div style="padding:8px 14px;font-size:var(--lbfr-t-caption);color:var(--lbfr-ink-soft);font-style:italic">No matches</div>') +
        // Resolves through FL.resolveBreedProfile's fallback profile —
        // an honest "average dog" baseline, visibly committed into the
        // field like any other pick (not a dead-end that just closes
        // the list).
        '<button class="lb-fr__bopt lb-fr__bopt--fallback" role="option" aria-selected="' + (s.breedActiveIdx === breedNames.length) + '" data-active="' + (s.breedActiveIdx === breedNames.length) + '" data-action="pickBreed" data-name="Mixed breed / not sure">Mixed or not sure → use average sizing</button>' +
        '</div>' : '') +
      '</div>' +
      // Fallback path: name not on file → say so plainly, and offer the
      // calculator's Body & Neck Build override so sighthound/bully/toy
      // width rules still compute correctly for mixes.
      (d.breed.isFallback && s.breedName ?
        '<p class="lb-fr__micro" style="margin:8px 0 0">We don’t have “' + esc(s.breedName) + '” on file — using average sizing. Choose the closest build below for a sharper match.</p>' +
        '<p class="lb-fr__q" style="margin:14px 0 8px">Body &amp; neck build</p>' +
        '<select class="lb-fr__field lb-fr__profile-input" data-input="buildOverride">' +
        '<option value=""' + (s.buildOverride ? '' : ' selected') + '>Choose the closest build…</option>' +
        FL.BUILD_OPTIONS.filter(function (b) { return FL.BUILD_TO_CATEGORY[b]; }).map(function (b) {
          return '<option value="' + esc(b) + '"' + (s.buildOverride === b ? ' selected' : '') + '>' + esc(FL.BUILD_OPTION_LABELS[b] || b) + '</option>';
        }).join('') +
        '</select>'
      : '') +
      // One sizing surface, no mode toggle: the size pills are always
      // visible with the calculated/selected size highlighted, the
      // entered measurements read out above them, and the Size Guide
      // links open the assistant. Continue stays the single primary CTA.
      '<div style="margin-top:22px">' +
      // Structured fit-status micro-card: NECK / CHEST as aligned
      // key-value rows — see valFor() below for the three wording states
      // (standard-size range / measured exact / not measured yet).
      (function () {
        // Fit recap — four honest states per dimension, every one of them
        // stating BOTH units and the resulting size letter with its real
        // adjustability window, so nothing here needs a second click to
        // become useful:
        //  A pill pick:  "Size M · fits 32–38 cm / 12.6–15 in"
        //  B measured:   "34.5 cm / 13.58 in → Size M (32–38 cm / …)"
        //  C estimated:  "≈ 34 cm / 13.39 in → Size M — Beagle average"
        //  D unset:      "Not set yet — pick a size below"
        // State C is new: previously a chosen breed lit up an estimated
        // size letter in the pills below while this card still read "Not
        // measured yet" — the recap and the control directly under it
        // contradicting each other on the very first screen.
        var row = function (label, value) {
          return '<div class="lb-fr__fitcard-row">' +
            '<span class="lb-fr__fitcard-label">' + label + '</span>' +
            '<span class="lb-fr__fitcard-val">' + value + '</span></div>';
        };
        // The estimate is age-adjusted, so the sentence describing it has
        // to name the same dog the number was computed for.
        var ageWord = s.ageProfile === 'puppy' ? 'a growing' : 'an adult';
        // The separator is ' · ', never ' → '. An arrow is a CAUSAL
        // operator: "44 → Size XL" reads as input-produces-output, the way
        // a calculator works, and that is exactly the claim a breed
        // estimate cannot support. Every hedge around it (the ≈, "typical
        // for", the legend's "starting point") arrives AFTER a conclusion
        // that has already been delivered as a result — and a hedge after
        // a conclusion is the weakest position in persuasion.
        //
        // One notation for both states, deliberately: keeping the arrow
        // for measured values and dropping it only for estimates would
        // build a two-notation system that only a careful reader decodes,
        // and careful readers were never the problem. The WORD carries the
        // distinction instead — "starting size" vs "size" — which also
        // makes this line agree with the pill legend six pixels below it
        // (_sizePillRow: "XL is our starting point for Akita"). Those two
        // used different vocabulary for the same claim until now.
        //
        // Bold tracks confidence: an estimate is unemphasised, a real
        // measurement keeps its weight and earns it.
        //
        // NOT changed: the size assistant's own readout (_inlineSizeAssistant,
        // "34.5 cm → size M"). That one responds live to a slider the
        // shopper is dragging, so there the arrow IS the function and is
        // correct. Leave it alone.
        function sizeTail(key, size, estimated) {
          if (!size) return '';
          var r = sizeRangeDual(key, size);
          return ' · ' + (estimated ? 'starting size ' + size : 'size <b>' + size + '</b>') +
            (r ? ' <span class="lb-fr__fitcard-dim">(' + r + ')</span>' : '');
        }
        function valFor(key, val, src, size, estCm, bespoke, estOnly) {
          // Bespoke is a PRODUCT TIER, not a missing value. The old code
          // fell through to "Not set yet" here, so choosing an Afghan
          // Hound — whose 80cm chest is past the top of the standard
          // matrix — read as though the app had failed to work something
          // out, and the shopper's rational next move was to skip the
          // harness entirely.
          if (bespoke) {
            return '<span class="lb-fr__bespoke-tag">Bespoke fit</span>' +
              '<span class="lb-fr__fitcard-dim"> — handcrafted to ' + d.bespokeFor + '’s ' +
              (key === 'neck' ? 'neck' : 'chest') + ' (' + dual(estOnly != null ? estOnly : val) + ')</span>';
          }
          if (val === null) {
            if (size && estCm != null) {
              // "— Akita average" was accurate and still read as a RESULT.
              // The honest frame is a starting point plus the spread it
              // was drawn from: a shopper who sees that Akita necks run
              // 39–49cm understands in one glance why a tape beats a
              // breed name, which no amount of "this is only an estimate"
              // achieves. The spread is real data (breed.neck/.chest),
              // not a disclaimer.
              var spr = key === 'neck' ? d.neckSpread : d.chestSpread;
              return '<span class="lb-fr__fitcard-dim">≈ ' + dual(estCm) + '</span>' + sizeTail(key, size, true) +
                (s.breedName
                  ? '<span class="lb-fr__fitcard-dim"> — typical for ' +
                    (d.breedIsGeneric ? d.breedPhrase : ageWord + ' ' + d.breedPhrase) +
                    (spr ? '. Most measure ' + dualRange(spr[0], spr[1]) : '') + '</span>'
                  : '');
            }
            return '<span class="lb-fr__fitcard-dim">Not set yet — pick a size below</span>';
          }
          if (src === 'pill') {
            return '<b>Size ' + size + '</b> <span class="lb-fr__fitcard-dim">· fits ' + sizeRangeDual(key, size) + '</span>';
          }
          // Accepted the breed estimate. It must NOT render like a
          // measured number — the value behind it is a range midpoint we
          // chose, not one the shopper took, and that distinction is the
          // whole point of recording the basis in the first place.
          if (src === 'breed-average') {
            return '<b>Size ' + size + '</b> <span class="lb-fr__fitcard-dim">· from ' + esc(s.breedName) +
              ' averages, not measured · fits ' + sizeRangeDual(key, size) + '</span>';
          }
          // A number the shopper actually took: emphasised, and named
          // "size", not "starting size".
          return dual(val) + sizeTail(key, size, false);
        }
        return '<div class="lb-fr__fitcard">' +
          row('Neck', valFor('neck', s.neck, s.neckSource, d.collarSize, s.breedName ? d.neckCm : null, d.neckBespoke, d.neckCm)) +
          row('Chest', valFor('chest', s.chest, s.chestSource, d.harnessSize, s.breedName ? d.chestCm : null, d.chestBespoke, d.chestCm)) +
          '</div>' +
          // One shared bespoke banner when either dimension is off-matrix.
          ((d.neckBespoke || d.chestBespoke)
            ? '<div class="lb-fr__bespoke-note">' +
              '<b>' + (d.neckBespoke && d.chestBespoke ? 'A collar and harness' : (d.neckBespoke ? 'A collar' : 'A harness')) +
              ' made to ' + d.bespokeFor + '’s measurements.</b> ' +
              esc(s.breedName || 'Your dog') + ' sits beyond our standard size range — so we cut this one by hand, ' +
              'to the exact numbers above. Same leather, same hardware, no extra charge for the fitting.</div>'
            : '');
      })() +
      this._manualSizePills(d) +
      '</div>',
      // CTA config lives in _profileCta above — see its header for why the
      // primary stays a single always-enabled action (the "Measure Akita"
      // primary/secondary fork was tried and correctly reverted), and why
      // the label, the meta line and the secondary check each carry exactly
      // one job.
      this._profileCta(d)
    );
  };

  // Manual sizing pill row: committing a pill maps to that size range's
  // midpoint cm, so every downstream derivation (variants, leash width,
  // guardrails) works identically to a measured flow — never a parallel
  // size code path. Reused on Step 1 AND as the inline mode-switcher on
  // the product steps.
  proto._sizePillRow = function (key, d, opts) {
    opts = opts || {};
    var s = this.state;
    var isNeck = key === 'neck';
    var sizes = isNeck ? FL.COLLAR_SIZES : FL.HARNESS_SIZES;
    var current = isNeck ? d.collarSize : d.harnessSize;
    var isSet = isNeck ? s.neck !== null : s.chest !== null;
    var head = opts.label
      ? '<p class="lb-fr__q" style="margin:0 0 8px;display:flex;align-items:baseline;gap:14px;flex-wrap:wrap">' + opts.label +
        (opts.guideLink ? '<button class="lb-fr__link" style="font-weight:400;letter-spacing:0;text-transform:none" data-action="openMeasure" data-key="' + key + '">Measure precisely</button>' : '') +
        '</p>'
      : '';
    // Highlight the RESOLVED size — measured, manually picked, or breed-
    // estimated — so calculated size and pills read as one surface.
    // Every pill carries its real cm AND inch window in the tooltip/aria
    // label, and the selected pill's window is printed underneath: the
    // shopper never has to open a chart to learn what "M" actually means.
    // Three distinguishable pill states, because there are three real
    // states: CHOSEN (solid — the shopper tapped it), SUGGESTED (outlined
    // in accent — derived from the breed average, not yet confirmed), and
    // AVAILABLE. Tapping a suggested pill confirms it; tapping a chosen
    // pill releases it back to the suggestion. Both transitions are now
    // visible, which is the whole of the "toggle does nothing" bug.
    var suggested = isNeck ? d.neckSuggested : d.chestSuggested;
    var bespoke = isNeck ? d.neckBespoke : d.chestBespoke;
    var pills = '<div class="lb-fr__pillbar" role="group" aria-label="' + (isNeck ? 'Collar' : 'Harness') + ' size">' + sizes.map(function (sz) {
      var isCurrent = !!current && sz === current;
      var sel = isCurrent && !suggested;
      var sug = isCurrent && suggested;
      var rng = sizeRangeDual(key, sz);
      return '<button class="lb-fr__pill-sz' + (sel ? ' lb-fr__pill-sz--sel' : '') + (sug ? ' lb-fr__pill-sz--suggested' : '') +
        '" data-action="manualSize" data-key="' + key + '" data-size="' + sz + '" aria-pressed="' + sel + '"' +
        ' title="' + sz + ' — fits ' + rng + (sug ? ' (suggested for ' + esc(s.breedName) + ' — tap to confirm)' : (sel ? ' — tap again to clear' : '')) + '"' +
        ' aria-label="Size ' + sz + ', fits ' + rng + (sug ? ', suggested' : '') + '">' + sz + '</button>';
    }).join('') +
      // Bespoke sits in the row as a real, selectable-looking option
      // rather than leaving every standard pill dark and the row
      // apparently broken.
      (bespoke ? '<span class="lb-fr__pill-sz lb-fr__pill-sz--bespoke" aria-current="true">Bespoke</span>' : '') +
      '</div>';
    var fromAverage = (isNeck ? s.neckSource : s.chestSource) === 'breed-average';
    var legend = bespoke
      ? '<p class="lb-fr__pillbar-legend"><b>Beyond standard sizing</b> — cut by hand to ' + d.bespokeFor + '’s measurements.</p>'
      : (current
        ? '<p class="lb-fr__pillbar-legend">' +
          // Three legends for three bases. "Starting point" is doing real
          // work in the first one: it tells the shopper the number is
          // where we BEGIN, not where we landed, at the exact moment they
          // are deciding whether to reach for a tape.
          (suggested
            ? '<b>' + current + '</b> is our starting point' + (d.breedIsGeneric ? '' : ' for ' + d.breedPhrase) +
              ' — measure and we’ll cut to your numbers · fits '
            : (fromAverage
              ? '<b>' + current + '</b> from ' + d.averagesPhrase + ' — not measured · fits '
              : '<b>' + current + '</b> fits ')) +
          sizeRangeDual(key, current) + '</p>'
        : '');
    return head + pills + legend;
  };
  proto._manualSizePills = function (d) {
    return this._sizePillRow('neck', d, { label: 'Collar size', guideLink: true }) +
      this._sizePillRow('chest', d, { label: 'Harness size', guideLink: true });
  };

  // (The former _profileMeasurements row block is gone — Step 1 now shows
  // the always-visible size pills with a measured-values readout above,
  // and the size assistant handles measuring.)

  // (The former _measurePillFor size-confidence card / neutral dropdown
  // is gone: size resolution now lives ONLY in the slim size-status line
  // and the size assistant modal — no duplicate bottom configuration UI.)

  // Bespoke-request mailto with the calculator's full detail payload.
  proto._customMailto = function (isNeck, d) {
    return FL.buildCustomSizeMailto({
      breedName: this.state.breedName, ageProfile: this.state.ageProfile, breedProfile: d.breed,
      neckSize: Math.round(d.neckCm), chestSize: Math.round(d.chestCm),
      isCustomCollar: isNeck, isCustomHarness: !isNeck, supportEmail: this.supportEmail
    });
  };

  // Persistent per-measurement status: guardrail (amber quiet / red boxed,
  // matching the Size Guide's deliberate tone hierarchy).
  proto._guardrailNote = function (guardrail) {
    if (!guardrail) return '';
    return guardrail.guardrailTone === 'red'
      ? '<div class="lb-fr__errbox" style="margin-top:10px">' + esc(guardrail.guardrailMessage) + '</div>'
      : '<p class="lb-fr__guardrail-quiet">' + esc(guardrail.guardrailMessage) + '</p>';
  };

  /* ============================================================
     In-card set manifest.
     Previously the ONLY place a committed item could be removed or edited
     was the desktop order-summary sidebar — which is display:none on
     mobile, buried one tap deep inside the sticky bar's collapsed drawer.
     So on a phone, adding a second collar in the wrong colour was a
     one-way door: the card said "In Your Set (2)" and offered nothing but
     "+ Add another". Every card now lists its own committed instances as
     chips, each one editable and removable in place, right where the
     shopper made the mistake.
     ============================================================ */
  /* Swipe affordance for the mobile carousel. Position is the useful
     fact ("2 of 4"), the dots are the glanceable version of it. Hidden
     entirely above 560px, where the grid shows everything at once. */
  proto._swipeHint = function (count) {
    if (!count || count < 2) return '';
    var dots = '';
    for (var i = 0; i < count; i++) dots += '<i data-on="' + (i === 0 ? 'true' : 'false') + '"></i>';
    return '<p class="lb-fr__swipehint" aria-hidden="true">' +
      '<span class="lb-fr__swipedots">' + dots + '</span>' +
      '<span class="lb-fr__swipepos">1 / ' + count + '</span>' +
      '<span>Swipe to compare</span></p>';
  };
  /* Keep the dots and the count in step with the rail's scroll position.
     Passive listener, no re-render — this must never cost a frame. */
  proto._watchCarousel = function () {
    var self = this;
    var rail = this.querySelector('.lb-fr__cardgrid');
    var hint = this.querySelector('.lb-fr__swipehint');
    if (!rail || !hint || rail._lbfrCarousel) return;
    rail._lbfrCarousel = true;
    var sync = function () {
      var cards = rail.children;
      if (!cards.length) return;
      var mid = rail.scrollLeft + rail.clientWidth / 2;
      var idx = 0, best = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var c = cards[i];
        var centre = c.offsetLeft + c.offsetWidth / 2;
        var dist = Math.abs(centre - mid);
        if (dist < best) { best = dist; idx = i; }
      }
      var dots = hint.querySelectorAll('.lb-fr__swipedots i');
      for (var j = 0; j < dots.length; j++) dots[j].setAttribute('data-on', j === idx ? 'true' : 'false');
      var pos = hint.querySelector('.lb-fr__swipepos');
      if (pos) pos.textContent = (idx + 1) + ' / ' + cards.length;
    };
    rail.addEventListener('scroll', sync, { passive: true });
    sync();
  };

  proto._setChips = function (items, labelFor) {
    var self = this;
    // The wrapper is ALWAYS rendered, even with nothing in it. Collapsed
    // it contributes exactly zero height (grid-template-rows: 0fr), so it
    // costs no space on an empty card — but because the element persists
    // across renders, adding the first item animates the row open on a
    // transition instead of snapping 66px of new content into existence
    // and shoving every card below it down the page.
    var ownsEdit = !!this.state.editingUid && items.some(function (i) { return i.uid === self.state.editingUid; });
    var open = items.length > 0;
    return '<span class="lb-fr__chipswrap' + (open ? ' lb-fr__chipswrap--open' : '') + '"><span class="lb-fr__chipsclip">' +
      '<span class="lb-fr__chips">' + items.map(function (it) {
      var label = labelFor(it);
      var editing = self.state.editingUid === it.uid;
      return '<span class="lb-fr__chip' + (editing ? ' lb-fr__chip--editing' : '') + '">' +
        '<i class="lb-fr__chip-dot" style="background:' + leatherHex(it.colorKey) + '" aria-hidden="true"></i>' +
        '<button class="lb-fr__chip-label" data-action="editItem" data-uid="' + it.uid + '"' +
        ' aria-label="Edit ' + esc(label) + '" title="Edit ' + esc(label) + '">' + esc(label) + '</button>' +
        '<button class="lb-fr__chip-x" data-action="removeItem" data-uid="' + it.uid + '"' +
        ' aria-label="Remove ' + esc(label) + ' from your set" title="Remove">×</button>' +
        '</span>';
    }).join('') + (ownsEdit ? '<button class="lb-fr__chip-cancel" data-action="cancelEdit">Cancel edit</button>' : '') +
      '</span></span></span>';
  };

  proto.screenCollar = function (d) {
    var self = this;
    var s = this.state;
    // Per-card swatch rows — each family card owns its leather choice, so
    // a black Vero and a tan Aeris can join the same set.
    // Swatch availability is judged against the size and width the card is
    // ACTUALLY configured at, not merely "does this product exist". A
    // colour stocked only in XS used to render as a live, clickable swatch
    // on an XL card — the shopper picked it, the card stayed put, and the
    // bottom CTA silently went dead reading "Unavailable in XL". Now the
    // dead swatch is visibly dead before it's touched.
    var swatchesFor = function (fk, selectedColor, small, atSize, atWidth) {
      return FL.LEATHERS.map(function (l) {
        var sel = selectedColor === l.key;
        var product = self.collarProductFor(fk, l.key);
        var avail = atSize
          ? !!(product && resolveVariant(product, { size: atSize, width: atWidth }))
          : productIsAvailable(product);
        var why = avail ? l.name : l.name + ' — unavailable' + (atSize ? ' in ' + atSize : '');
        // aria-pressed makes the selected leather audible. Without it the
        // swatch row announced five identical unlabelled toggles with no
        // indication of which one was active.
        return '<button class="lb-fr__sw' + (small ? ' lb-fr__sw--s' : '') + (sel ? ' lb-fr__sw--sel' : '') + (avail ? '' : ' lb-fr__sw--oos') + '" style="background:' + l.hex + '"' +
          (avail ? ' data-action="setCollarCardColor" data-family="' + fk + '" data-color="' + l.key + '"' : ' disabled aria-disabled="true"') +
          ' aria-pressed="' + (sel ? 'true' : 'false') + '"' +
          ' aria-label="' + why + '" title="' + why + '">' + '</button>';
      }).join('');
    };

    var body;
    {
      // ---- Size status ----
      // The old empty state rendered a full bordered banner reading "No
      // size selected yet — every collar is cut to your dog's neck" with a
      // "Select size" button. It cost a full block of vertical real estate
      // above the fold to (a) restate a fact the shopper cannot act on,
      // (b) duplicate the size pills sitting inside every card six inches
      // below, and (c) push a modal at someone who has not yet decided
      // WHICH collar they want — the wrong question, in the wrong order,
      // in the loudest available voice. It is gone.
      //
      // What replaces it: nothing, when there is nothing to say. A shopper
      // who skipped Step 1 gets one quiet line of orientation under the
      // heading pointing at the controls that are already on screen, and
      // the cards' own size pills do the rest. The bordered size line now
      // appears ONLY when it carries real information — a resolved size
      // with its true cm/in window, an unresolved boundary tie, or a
      // custom-zone measurement that needs a bespoke request.
      var sizeStatus = '';
      if (d.neckBespoke) {
        // Framed as the premium tier it is, not as an apology for a
        // system limit. Same fact, opposite commercial effect.
        sizeStatus = '<div class="lb-fr__bespoke-banner">' +
          '<span class="lb-fr__bespoke-eyebrow">Bespoke · no extra charge</span>' +
          '<p class="lb-fr__bespoke-head">Custom tailored for ' + d.bespokeFor + '</p>' +
          '<p class="lb-fr__bespoke-copy">A ' + dual(d.neckCm) + ' neck sits beyond our standard range, so this collar is ' +
          'cut by hand to those exact measurements — same leather, same hardware.</p>' +
          '<a class="lb-fr__btn lb-fr__btn--small" href="' + this._customMailto(true, d) + '">Request bespoke collar</a>' +
          '</div>';
      } else if (d.neckBlocked) {
        sizeStatus = '<div class="lb-fr__sizeline lb-fr__sizeline--boundary">' +
          '<span class="lb-fr__sizeline-chip">' + d.neckEval.boundaryLower + '/' + d.neckEval.boundaryUpper + '</span>' +
          '<span class="lb-fr__sizeline-copy">' + esc(d.neckEval.boundaryHeaderText) + '</span>' +
          '<button class="lb-fr__sizecard-verify" data-action="openMeasure" data-key="neck">Resolve</button>' +
          '</div>';
      } else if (d.collarSize) {
        var basis = s.neckSource === 'measured' ? 'from your measurement'
          : (s.neckSource === 'pill' ? 'size you selected'
            : 'estimated from ' + esc(s.breedName) + ' average');
        sizeStatus = '<div class="lb-fr__sizeline">' +
          '<span class="lb-fr__sizeline-chip">' + d.collarSize + '</span>' +
          '<span class="lb-fr__sizeline-copy"><b>Neck size</b> · fits ' + sizeRangeDual('neck', d.collarSize) +
          '<span class="lb-fr__sizeline-basis">' + basis + '</span></span>' +
          '<button class="lb-fr__sizecard-verify" data-action="openMeasure" data-key="neck">Adjust</button>' +
          '</div>';
      }
      sizeStatus += this._guardrailNote(d.neckGuardrail);
      if (d.whyText) {
        sizeStatus += '<p class="lb-fr__fitnote">' + esc(d.whyText) +
          (s.ageProfile === 'puppy' ? ' ' + esc(FL.PUPPY_COLLAR_DISCLAIMER[d.breed.category] || '') : '') + '</p>';
      }
      // Classic / Wide tabs: re-prioritize the grid, never hide cards.
      var tabs = '<div class="lb-fr__tabs">' +
        [{ k: 'classic', l: 'Classic' }, { k: 'wide', l: 'Wide / Hound' }].map(function (tb) {
          return '<button data-action="setCollarTab" data-tab="' + tb.k + '" aria-pressed="' + (d.activeCollarTab === tb.k) + '">' + tb.l + '</button>';
        }).join('') + '</div>';
      // The intro leadin is gone. It existed to route a shopper who
      // skipped Step 1 to the measuring assistant — but every collar card
      // now carries its own "Measure neck" trigger beside its own size
      // pills, so this was a second, differently-worded entrance to the
      // same modal, sitting at the top of the screen and scrolled out of
      // view by the time it was relevant. On mobile it also cost ~40px of
      // the fold to say something the cards say better.
      body = '<h1 class="lb-fr__h1">' + (d.hasProfile ? 'The collar, fitted to ' + (d.dogLabel || 'your dog') : 'The collar') + '</h1>' +
        sizeStatus + tabs +
        this._swipeHint(d.collarFamilyCards.length) +
        '<div class="lb-fr__cardgrid" role="group" aria-label="Collar styles" style="margin:8px 0 18px">' +
        d.collarFamilyCards.map(function (card) {
          var price = card.fromCents != null ? (card.priceVaries ? 'from ' : '') + money(card.fromCents, self.moneyFormat) : '';
          var widthSeg = card.stockedWidths.length > 1
            ? '<span class="lb-fr__scard-widths" role="group" aria-label="Strap width">' + card.stockedWidths.map(function (w) {
                var pressed = w === card.cardWidth;
                var star = d.hasProfile && w === card.recWidth;
                return '<button class="lb-fr__wbtn' + (pressed ? ' lb-fr__wbtn--sel' : '') + '" data-action="setCollarCardWidth" data-family="' + card.key + '" data-width="' + w + '" aria-pressed="' + pressed + '">' + w + (star ? ' <span class="lb-fr__rec-star">★</span>' : '') + '</button>';
              }).join('') + '</span>' +
              (d.hasProfile && card.recWidth ? '<span class="lb-fr__micro">★ ' + card.recWidth + ' recommended' + (s.breedName ? ' for ' + esc(s.breedName) : '') + '</span>' : '')
            : '';
          // Context-sensitive claims: recommendation badges require an
          // ACTUAL BREED — a size pick alone says nothing about build
          // category, so it only ever earns the neutral "Popular fit".
          // The alt cross-sell additionally requires size-appropriateness
          // (see altSizeAppropriate in deriveVals): no "Also suitable" on
          // a 4.5cm wide collar for an XS neck.
          var badge = '';
          var breedKnown = !!s.breedName;
          if (card.recommended) {
            badge = breedKnown
              ? '<span class="lb-fr__badge">★ Recommended for ' + esc(s.breedName) + '</span>'
              : '<span class="lb-fr__badge">Popular fit</span>';
          } else if (card.altRecommended && breedKnown && card.altSizeAppropriate) {
            badge = '<span class="lb-fr__badge lb-fr__badge--alt">Also suitable</span>';
          }
          if (card.available === false) badge += '<span class="lb-fr__badge lb-fr__badge--oos">Unavailable in ' + card.cardSize + '</span>';
          var cardItems = s.collars.filter(function (c) { return c.familyKey === card.key; });
          // The card is a container, not a control. It used to be
          // role="button" with its own data-action — a button wrapping
          // five other buttons, which is invalid semantics, a confusing
          // tab stop, and the delivery mechanism for the phantom
          // "selected" state. The only styling it now carries is
          // --added: a factual badge that this product is in the set.
          return '<div class="lb-fr__scard' + (card.addedCount ? ' lb-fr__scard--added' : '') + '" data-family="' + card.key + '">' +
            badge +
            (card.image ? '<span class="lb-fr__scard-media" style="background-image:url(' + card.image + ')"></span>' : '<span class="lb-fr__scard-media lb-fr__skel"></span>') +
            '<span class="lb-fr__scard-body">' +
            '<span class="lb-fr__scard-titlerow"><span class="lb-fr__scard-name">' + card.label + '</span><span class="lb-fr__scard-price">' + price + '</span></span>' +
            '<span class="lb-fr__scard-meta">' + card.widthsLabel + ' wide</span>' +
            '<span class="lb-fr__scard-blurb">' + card.blurb + '</span>' +
            // Universal inline size selector — inherits the Step-1 size,
            // switchable per card for multi-dog sets.
            //
            // The labelled header with its own "Measure neck" trigger is
            // the SAME anatomy the Vero Harness card already used for
            // chest ("Chest size · Measure precisely"). Collars previously
            // had neither: an unlabelled pill row, with the only route to
            // the measuring assistant parked in intro copy at the top of
            // the screen — a different place, a different wording, and off
            // screen entirely by the time a shopper on a phone is actually
            // looking at the pills. Same job, same control, same position,
            // on every product card.
            '<span class="lb-fr__scard-sizehead">Neck size' +
            '<button class="lb-fr__link lb-fr__scard-guide" data-action="openMeasure" data-key="neck">Measure neck</button></span>' +
            // Every size pill states the neck it fits, in BOTH units, in
            // its tooltip and aria-label — and the active pill's window is
            // printed underneath the row, so the size↔measurement mapping
            // is legible without a modal.
            '<span class="lb-fr__pillbar lb-fr__pillbar--card' +
            (s.sizePromptFor === card.key ? ' lb-fr__pillbar--prompt' : '') + '" role="group" aria-label="' + card.label + ' neck size">' + card.sizeOptions.map(function (o) {
              var isCur = card.cardSize === o.sz;
              // A per-card override is always an explicit choice; only the
              // INHERITED Step-1 size can still be a breed suggestion.
              var ownOverride = !!s.collarSizes[card.key];
              var pSel = isCur && (ownOverride || !d.neckSuggested);
              var pSug = isCur && !ownOverride && d.neckSuggested;
              var rng = sizeRangeDual('neck', o.sz);
              var lbl = o.ok ? ('Size ' + o.sz + ' — fits ' + rng) : ('Size ' + o.sz + ' — sold out in ' + card.label);
              return '<button class="lb-fr__pill-sz' + (pSel ? ' lb-fr__pill-sz--sel' : '') + (pSug ? ' lb-fr__pill-sz--suggested' : '') + '"' +
                (o.ok ? ' data-action="setCollarCardSize" data-family="' + card.key + '" data-size="' + o.sz + '"' : ' disabled aria-disabled="true"') +
                ' title="' + lbl + '" aria-label="' + lbl + '" aria-pressed="' + pSel + '">' + o.sz + '</button>';
            }).join('') +
            (d.neckBespoke ? '<span class="lb-fr__pill-sz lb-fr__pill-sz--bespoke" aria-current="true">Bespoke</span>' : '') +
            '</span>' +
            // ALWAYS rendered, even when empty. Conditionally inserting
            // this row grew the card ~14px the moment a size was picked,
            // shoving every card below it down the page mid-tap.
            // Range shown once per screen, not once per card. The size line
            // above the grid already states the resolved neck window; four
            // cards inheriting it repeated the identical string four more
            // times. It reappears on a card only when that card's size is
            // an OVERRIDE that differs from the step's — i.e. when it is
            // genuinely new information. The row stays reserved either way
            // so nothing shifts.
            '<span class="lb-fr__scard-fits' + (s.sizePromptFor === card.key ? ' lb-fr__scard-fits--prompt' : '') + '">' +
            (s.sizePromptFor === card.key ? 'Choose a size to add this collar'
              : (d.neckBespoke ? 'Cut to ' + dual(d.neckCm) + ' neck'
                : ((card.cardSize && card.cardSize !== d.collarSize)
                  ? card.cardSize + ' · fits ' + sizeRangeDual('neck', card.cardSize)
                  : '&nbsp;'))) + '</span>' +
            widthSeg +
            '<span class="lb-fr__scard-swatches" role="group" aria-label="' + card.label + ' leather">' + swatchesFor(card.key, card.colorKey, true, card.cardSize, card.cardWidth) + '</span>' +
            // Stamping lives ON THE PRODUCT it applies to.
            //
            // It used to be one global toggle in a panel below the grid.
            // A shopper could switch it on, add an Aeris, and see the
            // toggle sitting there lit — the commit quietly dropped the
            // stamp (correct data, but silently contradicting the
            // interface), and a two-collar set gave no way at all to say
            // which collar the name belonged on. Perforated Aeris simply
            // cannot take a stamp, so its card states that as a product
            // fact instead of offering a control that can't be honoured.
            self._cardStampBlock(card) +
            // THE add action for this product — the only one. It says what
            // it does from the first render, adds on one tap, and sits
            // directly under the swatches and pills that configured it, so
            // the thing being added is the thing being looked at.
            // Bespoke gate — mirrors the Vero Harness card exactly. A neck
            // past the standard matrix (>47 cm) has no stock size to add,
            // so the primary action becomes the bespoke request rather than
            // an "Add to set" that can only ever fail. Without this the
            // collar step carried the bespoke BANNER but still rendered a
            // live Add button: tapping it asked the shopper to "choose a
            // size", and tapping a size pill then overwrote their real
            // measurement with a range midpoint (see setCollarCardSize).
            '<span class="lb-fr__scard-actions">' +
            (d.neckBespoke
              ? '<a class="lb-fr__addbtn lb-fr__addbtn--primary" href="' + self._customMailto(true, d) + '">Request Custom Size</a>'
              : card.available === false
                ? '<button class="lb-fr__addbtn" disabled aria-disabled="true">Sold out in ' + card.cardSize + '</button>'
                : '<button class="lb-fr__addbtn lb-fr__addbtn--primary" data-action="addCollarCard" data-family="' + card.key + '">' +
                  (s.editingUid && cardItems.some(function (c) { return c.uid === s.editingUid; }) ? 'Update item'
                    : (card.addedCount ? '+ Add another' : 'Add to set')) + '</button>') +
            '<span class="lb-fr__count">' + (card.addedCount ? card.addedCount + ' in set' : '&nbsp;') + '</span>' +
            '</span>' +
            // Committed instances of THIS collar — editable and removable
            // where they were created, not only in a sidebar that mobile
            // never shows.
            self._setChips(cardItems, function (it) {
              return leatherDisplayName(it.colorKey) + ' · ' + [it.width, it.size].filter(Boolean).join(' · ');
            }) +
            '</span></div>';
        }).join('') +
        '</div>' +
        // The stamping panel used to render only when the ACTIVE family
        // happened to be stampable — and with nothing yet tapped the
        // active family defaults to the recommendation, which for a
        // standard build is the perforated (never-stampable) Aeris. So a
        // free personalisation simply did not exist on screen, and
        // appeared out of nowhere later if the shopper happened to tap a
        // Vero card. The panel is now always present: when the active
        // collar can't take a stamp it says which ones can, instead of
        // vanishing without explanation.
        '';
        // (No quiet "skip" link here any more: the ghost bottom CTA already
        // IS that action, and two controls for one intention is the same
        // duplication this pass exists to remove.)
    }

    var canAdd = !!d.collarVariant;
    var collarCta = this._productCta({ count: s.collars.length, noun: 'collar', plural: 'collars' });
    return this._stepShell(d, 'collar', body, collarCta);
  };

  /* ============================================================
     Shared bottom-CTA builder for the three product steps.

     The single worst conversion defect in the original build lived here:
     with nothing yet selected — i.e. the state EVERY shopper is in the
     moment they land on the step — the largest, darkest, highest-contrast
     control on the screen read "Skip collar & continue". The page's
     primary visual commitment was an instruction to not buy the product.
     A skip must remain available and one tap away, but it is a secondary
     intention and now carries secondary weight (ghost button); the dark
     primary is reserved for actions that move a product into the set.

     SECOND (the "phantom selection" removal): the bottom CTA no longer
     adds anything. It used to change identity based on which card had
     last been tapped — a card that could be scrolled far off screen,
     leaving a button reading "Add Vero Wide · €108,00" with no Vero Wide
     in sight. That coupling is what forced the card-select state to
     exist, and the select state paid for itself in nothing: tap-card →
     tap-CTA was exactly two taps, the same as tap-Add → tap-Continue,
     while adding a highlight that read as a radio group on a
     multi-item basket, a "chosen but not in your set" limbo with no
     meaning in shopping, and a latch that could not be un-set.

     Adding now lives on the product, in the card, next to the swatches
     that configure it. This control does one thing: move forward. It is
     the dark primary once the set has something in it, and a ghost
     "Continue without a collar" when it doesn't — still one tap, still
     always available, just no longer the loudest thing on the page.

     It also emits a SHORT label. The old desktop label ("Add Aeris
     Classic to set · €69 & continue") was 40+ uppercase characters at
     0.16em tracking; inside a 375px sticky bar that wrapped to three
     lines and blew the bar past the fold.
     ============================================================ */
  /* Optional SECOND action beside the primary. Added for the nametag
     step, where the primary must be "claim the reward" and the decline
     must stay one tap away rather than being the only button on screen. */
  function ctaSecondaryBtn(cta, useShort) {
    var sec = cta && cta.ctaSecondary;
    if (!sec) return "";
    var attrs = sec.data ? Object.keys(sec.data).map(function (k) {
      return ' data-' + k + '="' + esc(String(sec.data[k])) + '"';
    }).join("") : "";
    // variant opts a secondary INTO real button chrome. Deliberately
    // opt-in: this slot also carries the nametag step's decline action,
    // which must stay a quiet text link — promoting "no thanks" to an
    // outlined button beside the primary would give declining the same
    // visual weight as accepting.
    return '<button class="lb-fr__cta-secondary' + (sec.variant ? ' lb-fr__cta-secondary--' + sec.variant : '') +
      '" data-action="' + sec.action + '"' + attrs + '>' +
      ((useShort && sec.short) ? sec.short : sec.label) + '</button>';
  }
  function ctaDataAttrs(cta) {
    if (!cta || !cta.ctaData) return "";
    return Object.keys(cta.ctaData).map(function (k) {
      return ' data-' + k + '="' + esc(String(cta.ctaData[k])) + '"';
    }).join("");
  }

  proto._productCta = function (o) {
    if (o.count) {
      return {
        back: true, ctaAction: 'continueStep', ctaEnabled: true,
        ctaLabel: 'Continue →', ctaShort: 'Continue →',
        ctaMeta: o.count + ' ' + (o.count === 1 ? o.noun : o.plural) + ' in set',
        ctaMetaTone: 'neutral'
      };
    }
    // Nothing added: continuing without this piece is legitimate and must
    // stay one tap away — but it is a secondary intention, so it carries
    // secondary weight.
    return {
      back: true, ctaAction: 'skipCurrentStep', ctaEnabled: true, ctaGhost: true,
      ctaLabel: 'Continue without a ' + o.noun, ctaShort: 'No ' + o.noun + ' →'
    };
  };

  /* Per-card stamping. `familyIsStampable` is the physical constraint:
     Vero is smooth leather and takes a stamp; Aeris is perforated and
     never can. */
  proto._cardStampBlock = function (card) {
    var s = this.state;
    var stampable = familyIsStampable(card.key);
    if (!stampable) {
      return '<span class="lb-fr__stampnote">Perforated — cannot be stamped</span>';
    }
    var on = !!s.collarStampFor[card.key];
    var text = s.collarStampText || '';
    return '<span class="lb-fr__stamp">' +
      '<span class="lb-fr__stamp-row">' +
      '<button class="lb-fr__tg" role="switch" aria-checked="' + on + '"' +
      ' data-action="toggleStamp" data-family="' + card.key + '"' +
      ' aria-label="Add free leather stamping to ' + card.label + '"><i></i></button>' +
      '<span class="lb-fr__stamp-label">Free stamping</span>' +
      '</span>' +
      // Same collapsible treatment as the in-set chip row: the disclosure
      // eases open instead of snapping 72px of new content into the card
      // and pushing the grid row down under the shopper's finger.
      '<span class="lb-fr__stampwrap' + (on ? ' lb-fr__stampwrap--open' : '') + '"><span class="lb-fr__chipsclip">' +
      (on
        ? '<span class="lb-fr__stamp-body">' +
          '<input class="lb-fr__field lb-fr__stamp-input' + (s.stampError === card.key ? ' lb-fr__field--err' : '') + '"' +
          ' data-input="stampText" maxlength="8" placeholder="' + (s.dogName ? esc(s.dogName).toUpperCase().slice(0, 8) : 'e.g. LUNA') + '"' +
          ' value="' + esc(text) + '" aria-label="Name to stamp, up to 8 characters">' +
          // Step 1 is optional, so the dog's name is often not on file by
          // the time a shopper wants it pressed into leather. Offer it as
          // a one-tap fill when we have it, and ask for it plainly when
          // we don't — rather than letting them add a stamped collar with
          // nothing to stamp.
          //
          // Both live inside a collapsible: they exist only while the
          // field is empty, so rendering them conditionally shrank the
          // card by ~51px on the first keystroke — the same mid-typing
          // reflow the removed preview band caused, just in the opposite
          // direction. Collapsed the wrapper occupies zero pixels, so an
          // already-filled field costs nothing, and the row eases shut
          // instead of snapping.
          '<span class="lb-fr__stamphelp' + (!text ? ' lb-fr__stamphelp--open' : '') + '">' +
          '<span class="lb-fr__chipsclip">' +
          (s.dogName
            ? '<button class="lb-fr__stamp-fill" data-action="useDogName" data-family="' + card.key + '" tabindex="' + (text ? '-1' : '0') + '">Use ' + esc(s.dogName) + '</button>'
            : '<span class="lb-fr__stamp-hint">Type the name to press into the leather.</span>') +
          '</span></span>' +
          // (The dark foil-preview band that used to render the typed name
          // in spaced gold caps has been removed. The input already shows
          // the shopper exactly what they typed, and the band only
          // appeared once text existed — so it grew the card by ~44px
          // mid-keystroke, which is the one moment a size change is most
          // disruptive.)
          '</span>'
        : '') +
      '</span></span>' +
      '</span>';
  };

  proto.screenLeash = function (d) {
    var self = this;
    var s = this.state;
    var lastCollarColor = s.collars.length ? s.collars[s.collars.length - 1].colorKey : 'black';
    var leashCardColor = function (style) { return s.leashColors[style] || lastCollarColor; };

    var styleCards = FL.LEASH_STYLE_OPTIONS.map(function (style) {
      var cardColor = leashCardColor(style);
      var product = self.leashProductFor(style, cardColor);
      var img = imageFor(product, null);
      var displayName = FL.LEASH_STYLE_DISPLAY_NAME[style];
      // Per-card width: each leash card owns its own 1.5cm/2cm choice, so
      // a 1.5cm Classic and a 2cm Omni can join the same set. Widths come
      // from the CATALOG (what this mapped product actually stocks), same
      // gate the collar cards already applied — the hardcoded pair used to
      // render buttons for widths the product doesn't carry, silently
      // killing the CTA when one was chosen.
      var stockedLeashWidths = self.leashStockedWidths(style, cardColor);
      var cardWidth = self.resolveLeashWidth(style, cardColor, d.leashWidthAuto);
      var variant = product ? resolveVariant(product, { width: cardWidth }) : null;
      var priceStr = variant ? money(variant.price, self.moneyFormat) : '';
      var addedCount = s.leashes.filter(function (l) { return l.style === style; }).length;
      // Width parity across leash cards. When a style stocks two widths
      // the shopper picks; when it stocks ONE the row used to vanish
      // entirely, so the Omni card sat next to a Classic card with a
      // visible width selector and read as broken or half-built — the
      // shopper cannot tell "this leash is only made in 2cm" from "this
      // control failed to load". The row is now always present: a choice
      // where there is one, a stated specification where there isn't.
      // The row is ALWAYS emitted so adjacent leash cards keep their rows
      // on one baseline whatever their variant shape — a product sold
      // without a width option (no Width option at all) legitimately has
      // nothing to say here, and should not drag the card's other rows
      // out of alignment with its neighbour.
      var widthSeg = '<span class="lb-fr__widthrow">' + (stockedLeashWidths.length > 1
        ? '<span class="lb-fr__scard-widths" role="group" aria-label="Strap width">' + stockedLeashWidths.map(function (w) {
            var pressed = w === cardWidth;
            var star = (d.hasProfile || s.collars.length) && w === d.leashWidthAuto;
            return '<button class="lb-fr__wbtn' + (pressed ? ' lb-fr__wbtn--sel' : '') + '" data-action="setLeashCardWidth" data-style="' + style + '" data-width="' + w + '" aria-pressed="' + pressed + '"' +
              ' title="' + w + ' strap width' + (star ? ' — recommended for your dog' : '') + '">' + w + (star ? ' <span class="lb-fr__rec-star">★</span>' : '') + '</button>';
          }).join('') + '</span>'
        : (stockedLeashWidths.length === 1
          // States the width this product is AVAILABLE in — deliberately
          // not "single width" or "only made in", which would assert a
          // product-line fact this component cannot know. Widths are read
          // from the mapped Shopify product's variants at runtime; the
          // theme declares none. If a merchant later stocks a second
          // width, this silently becomes a two-chip selector with no code
          // change.
          ? '<span class="lb-fr__specfixed" title="Available in ' + stockedLeashWidths[0] + ' strap width">' +
            '<span>Width</span> <b>' + stockedLeashWidths[0] + '</b> <span>(' + inDisp(parseFloat(stockedLeashWidths[0])) + ' in)</span></span>'
          : '')) + '</span>';
      var cardSwatches = FL.LEATHERS.map(function (l) {
        var swSel = cardColor === l.key;
        var lp = self.leashProductFor(style, l.key);
        var avail = !!(lp && resolveVariant(lp, { width: cardWidth }));
        var why = avail ? l.name : l.name + ' — unavailable in ' + cardWidth;
        return '<button class="lb-fr__sw lb-fr__sw--s' + (swSel ? ' lb-fr__sw--sel' : '') + (avail ? '' : ' lb-fr__sw--oos') + '" style="background:' + l.hex + '"' +
          (avail ? ' data-action="setLeashCardColor" data-style="' + style + '" data-color="' + l.key + '"' : ' disabled aria-disabled="true"') +
          ' aria-pressed="' + (swSel ? 'true' : 'false') + '"' +
          ' aria-label="' + why + '" title="' + why + '">' + '</button>';
      }).join('');
      var leashItems = s.leashes.filter(function (l) { return l.style === style; });
      return '<div class="lb-fr__scard' + (addedCount ? ' lb-fr__scard--added' : '') + '" data-style="' + style + '">' +
        // The badge is only a true statement when a collar in the set
        // actually matches this card's leather — recomputed on every
        // color change, so a mismatched swatch hides it instantly.
        (s.collars.some(function (c) { return c.colorKey === cardColor; }) ? '<span class="lb-fr__badge">Matches your collar</span>' : '') +
        (img ? '<span class="lb-fr__scard-media" style="background-image:url(' + img + ')"></span>' : '<span class="lb-fr__scard-media lb-fr__skel"></span>') +
        '<span class="lb-fr__scard-body">' +
        '<span class="lb-fr__scard-titlerow"><span class="lb-fr__scard-name">' + displayName + '</span><span class="lb-fr__scard-price">' + priceStr + '</span></span>' +
        '<span class="lb-fr__scard-blurb">' + FL.LEASH_STYLE_DESCRIPTIONS[style] + '</span>' +
        widthSeg +
        '<span class="lb-fr__scard-swatches" role="group" aria-label="' + displayName + ' leather">' + cardSwatches + '</span>' +
        '<span class="lb-fr__scard-actions">' +
        (!variant
          ? '<button class="lb-fr__addbtn" disabled aria-disabled="true">Unavailable</button>'
          : '<button class="lb-fr__addbtn lb-fr__addbtn--primary" data-action="addLeashCard" data-style="' + style + '">' +
            (s.editingUid && leashItems.some(function (l) { return l.uid === s.editingUid; }) ? 'Update item'
              : (addedCount ? '+ Add another' : 'Add to set')) + '</button>') +
        '<span class="lb-fr__count">' + (addedCount ? addedCount + ' in set' : '&nbsp;') + '</span>' +
        '</span>' +
        self._setChips(leashItems, function (it) {
          return leatherDisplayName(it.colorKey) + ' · ' + it.width;
        }) +
        '</span></div>';
    }).join('');

    // ---- Smart width recommendation — ported from the Size Calculator's
    // own LEASH_WIDTH_BY_COLLAR_SIZE/LEASH_WIDTH_BY_HARNESS_SIZE mapping
    // (shared via lillebjorn-fit-logic.js), surfaced explicitly here
    // instead of leaving the shopper to guess between two bare cm values.
    var LEASH_WIDTH_BLURB = {
      "1.5cm": "balanced and light in hand — right for everyday walks",
      "2cm": "extra width for strength and control on a bigger or stronger-pulling dog"
    };
    // The label now comes from d.leashWidthBasis — the SAME resolution
    // chain that produced the number — so the sentence can only ever name
    // the dimension that actually decided it. A breed name is no longer
    // used here: "Recommended for Beagle: 2cm" hid which measurement was
    // responsible, which is the very thing a shopper questioning the
    // number needs to see.
    var basis = d.leashWidthBasis;
    var autoWidthIn = inDisp(parseFloat(d.leashWidthAuto));
    var widthRecCopy = '<div class="lb-fr__recbox" style="margin:14px 0">' +
      '<b>Recommended for your ' + esc(basis.label) + ': ' + d.leashWidthAuto + ' (' + autoWidthIn + ' in)</b> — ' +
      LEASH_WIDTH_BLURB[d.leashWidthAuto] + '. Matched to your ' + basis.dimension +
      ' for a balanced fit; switch it on each card any time.' +
      '</div>';

    var body = '<h1 class="lb-fr__h1">' + (s.collars.length ? 'The leash, matched to your collar' : 'The leash') + '</h1>' +
      ((d.hasProfile || s.collars.length) ? widthRecCopy : '') +
      this._swipeHint(FL.LEASH_STYLE_OPTIONS.length) +
      '<div class="lb-fr__cardgrid" role="group" aria-label="Leash styles" style="margin-top:8px">' + styleCards + '</div>' +
      '';

    var leashCta = this._productCta({ count: s.leashes.length, noun: 'leash', plural: 'leashes' });
    return this._stepShell(d, 'leash', body, leashCta);
  };

  proto.screenHarness = function (d) {
    var self = this;
    var s = this.state;
    // Projected on the SAME post-discount basis as giftUnlocked (see the
    // parity note in deriveVals) — and the added harness is itself a piece,
    // so it can be what unlocks the set discount in the first place.
    var wouldBeCount = d.itemCount + (d.harnessVariant ? 1 : 0);
    var wouldBeRate = wouldBeCount >= 2 ? this.discountRate : 0;
    var wouldBeSubtotal = d.subtotalCents + (d.harnessVariant ? d.harnessVariant.price : 0);
    var wouldBeEligible = Math.round(wouldBeSubtotal * (1 - wouldBeRate));
    var wouldUnlockGift = wouldBeEligible >= this.giftThresholdCents;
    var harnessSwatches = FL.LEATHERS.map(function (l) {
      var sel = d.harnessColor === l.key;
      var hp = self.harnessProductFor(l.key);
      var avail = d.harnessSize ? !!(hp && resolveVariant(hp, { size: d.harnessSize })) : productIsAvailable(hp);
      var why = avail ? l.name : l.name + ' — unavailable' + (d.harnessSize ? ' in ' + d.harnessSize : '');
      return '<button class="lb-fr__sw lb-fr__sw--s' + (sel ? ' lb-fr__sw--sel' : '') + (avail ? '' : ' lb-fr__sw--oos') + '" style="background:' + l.hex + '"' +
        (avail ? ' data-action="setHarnessColor" data-color="' + l.key + '"' : ' disabled aria-disabled="true"') +
        ' aria-pressed="' + (sel ? 'true' : 'false') + '"' +
        ' aria-label="' + why + '" title="' + why + '">' + '</button>';
    }).join('');
    // The "matches" note tracks the live color comparison — it shows only
    // while the harness leather genuinely matches a collar in the set.
    var matchesCollar = s.collars.some(function (c) { return c.colorKey === d.harnessColor; });
    var harnessCount = s.harnesses.length;
    // Same premium card anatomy as Collars and Leashes. The Lille Björn
    // harness is sized by ONE measurement — chest circumference — so the
    // spec badge states exactly that and nothing more.
    // The "Fitted to: Chest M · 46–54 cm / 18.1–21.3 in" badge stated
    // exactly what the "Fits chest 46–54 cm / 18.1–21.3 in" line under the
    // size pills already states, on the same card, ~80px apart — the same
    // range twice, once in caps. Its only unique affordance was a link
    // into the measuring assistant, and the size header now carries a
    // "Measure chest" trigger in the position every other card uses. The
    // badge is gone; the fits line survives because it sits with the
    // control it describes.
    var specBadge = '';
    var body = '<h1 class="lb-fr__h1">Complete ' + (d.possessive) + ' Walk System</h1>' +
      '<div class="lb-fr__cardgrid" style="margin-top:14px">' +
      '<div class="lb-fr__scard' + (harnessCount ? ' lb-fr__scard--added' : '') + '">' +
      (matchesCollar ? '<span class="lb-fr__badge">Matches your collar</span>' : '') +
      (d.harnessProduct ? '<span class="lb-fr__scard-media" style="background-image:url(' + imageFor(d.harnessProduct, d.harnessVariant) + ');background-size:contain;background-repeat:no-repeat"></span>' : '<span class="lb-fr__scard-media lb-fr__skel"></span>') +
      '<span class="lb-fr__scard-body">' +
      '<span class="lb-fr__scard-titlerow"><span class="lb-fr__scard-name">Vero Harness</span><span class="lb-fr__scard-price">' + (d.harnessVariant ? money(d.harnessVariant.price, this.moneyFormat) : '') + '</span></span>' +
      '<span class="lb-fr__scard-meta">Sized by chest circumference</span>' +
      specBadge +
      '<span class="lb-fr__scard-swatches" role="group" aria-label="Harness leather">' + harnessSwatches + '</span>' +
      (matchesCollar ? '<span class="lb-fr__micro">Matches your collar — tap a swatch to change</span>' : '') +
      // Inline chest-size pills, right in the card — no forced modal
      // detour for a standard size; the Size Guide link stays for
      // measuring precisely.
      // Identical anatomy to the collar cards' size header — same class,
      // same position, same wording pattern.
      '<span class="lb-fr__scard-sizehead">Chest size' +
      '<button class="lb-fr__link lb-fr__scard-guide" data-action="openMeasure" data-key="chest">Measure chest</button></span>' +
      '<span class="lb-fr__pillbar' + (s.sizePromptFor === 'harness' ? ' lb-fr__pillbar--prompt' : '') + '" role="group" aria-label="Harness chest size">' + FL.HARNESS_SIZES.map(function (sz) {
        var isCurrent = !!d.harnessSize && sz === d.harnessSize;
        var pillSel = isCurrent && !d.chestSuggested;
        var pillSug = isCurrent && d.chestSuggested;
        var hp = self.harnessProductFor(d.harnessColor);
        var ok = !!(hp && resolveVariant(hp, { size: sz }));
        var rng = sizeRangeDual('chest', sz);
        var lbl = ok ? ('Size ' + sz + ' \u2014 fits chest ' + rng) : ('Size ' + sz + ' \u2014 sold out');
        return '<button class="lb-fr__pill-sz' + (pillSel ? ' lb-fr__pill-sz--sel' : '') + (pillSug ? ' lb-fr__pill-sz--suggested' : '') + '"' +
          (ok ? ' data-action="manualSize" data-key="chest" data-size="' + sz + '"' : ' disabled aria-disabled="true"') +
          ' title="' + lbl + '" aria-label="' + lbl + '" aria-pressed="' + pillSel + '">' + sz + '</button>';
      }).join('') +
      // Bespoke reads as the selected option in the row, not as an empty
      // row with nothing lit. A dog past the standard matrix is being
      // offered a better product, not shown a failure.
      (d.chestBespoke ? '<span class="lb-fr__pill-sz lb-fr__pill-sz--bespoke" aria-current="true">Bespoke</span>' : '') +
      '</span>' +
      (s.sizePromptFor === 'harness'
        ? '<span class="lb-fr__scard-fits lb-fr__scard-fits--prompt">Choose a chest size to add this harness</span>'
        : (d.chestBespoke
          ? '<span class="lb-fr__scard-fits">Cut to ' + dual(d.chestCm) + ' chest</span>'
          : (d.harnessSize ? '<span class="lb-fr__scard-fits">Fits chest ' + sizeRangeDual('chest', d.harnessSize) + '</span>' : '<span class="lb-fr__scard-fits">&nbsp;</span>'))) +
      '<span class="lb-fr__scard-actions">' +
      (d.chestBespoke
        ? '<a class="lb-fr__addbtn lb-fr__addbtn--primary" href="' + this._customMailto(false, d) + '">Request bespoke fit</a>'
        : '<button class="lb-fr__addbtn lb-fr__addbtn--primary" data-action="addHarnessCard">' +
          (s.editingUid && s.harnesses.some(function (h) { return h.uid === s.editingUid; }) ? 'Update item'
            : (harnessCount ? '+ Add another' : 'Add to set')) + '</button>') +
      '<span class="lb-fr__count">' + (harnessCount ? harnessCount + ' in set' : '&nbsp;') + '</span>' +
      '</span>' +
      this._setChips(s.harnesses, function (it) {
        return leatherDisplayName(it.colorKey) + ' · ' + it.size;
      }) +
      '</span></div></div>' +
      // Bespoke banner. This replaced a terse "Custom size." advisory that
      // read as an apology for a system limit; the same fact framed as
      // what it actually is — a hand-cut harness at no extra charge — is
      // the difference between the shopper skipping the step and wanting
      // the upgrade.
      (d.chestBespoke ? '<div class="lb-fr__bespoke-banner">' +
        '<span class="lb-fr__bespoke-eyebrow">Bespoke \u00b7 no extra charge</span>' +
        '<p class="lb-fr__bespoke-head">Custom tailored for ' + d.bespokeFor + '</p>' +
        '<p class="lb-fr__bespoke-copy">We handcraft this harness to your dog\u2019s exact measurements \u2014 ' +
        'chest ' + dual(d.chestCm) + '. Same leather and hardware as the standard range, cut to fit only them. ' +
        'Send us the measurements and we\u2019ll confirm the fit and lead time.</p>' +
        '<a class="lb-fr__btn lb-fr__btn--small" href="' + this._customMailto(false, d) + '">Request bespoke harness</a>' +
        '</div>' : '') +
      (d.chestBlocked ? '<div class="lb-fr__sizeline lb-fr__sizeline--boundary" style="margin-top:12px">' +
        '<span class="lb-fr__sizeline-copy">' + esc(d.chestEval.boundaryHeaderText) + '</span>' +
        '<button class="lb-fr__sizecard-verify" data-action="openMeasure" data-key="chest">Resolve</button></div>' : '') +
      this._guardrailNote(d.chestGuardrail) +
      (s.ageProfile === 'puppy' && s.breedName ? '<p class="lb-fr__guardrail-quiet">' + esc(FL.PUPPY_HARNESS_DISCLAIMER) + '</p>' : '') +
      // Quotes the POST-discount figure, because that is the number the
      // threshold is actually tested against — quoting the merchandise sum
      // here would promise a tag the cart then wouldn't grant.
      (wouldUnlockGift ? '<div class="lb-fr__recbox" style="margin-top:12px">Adding this brings your set to ' + money(wouldBeEligible, this.moneyFormat) +
        (wouldBeRate ? ' after the ' + d.discountPct + '% set discount' : '') + ' — the engraved tag comes with it.</div>' : '') +
      // The free-tag reminder is worth keeping, but it belongs to the
      // DECISION not to add, so it stands on its own rather than hanging
      // off a skip link that the ghost bottom CTA already provides.
      (harnessCount || d.giftUnlocked ? '' : '<p class="lb-fr__sub" style="margin-top:14px;font-size:var(--lbfr-t-caption)">Moving on without a harness? You’ll miss the free engraved tag by <b class="lb-fr__amber-text">' + money(d.giftRemainingCents, this.moneyFormat) + '</b> — you can always add one later.</p>');
    var harnessCta = this._productCta({ count: harnessCount, noun: 'harness', plural: 'harnesses' });
    return this._stepShell(d, 'harness', body, harnessCta);
  };

  proto.screenTag = function (d) {
    var s = this.state;
    var stateA = d.giftUnlocked && d.tagGiftAvailable;
    var designLabel = TAG_DESIGNS.filter(function (t) { return t.key === d.tagDesign; })[0].label;
    var product = stateA ? d.tagGiftProduct : d.tagPaidProduct;
    var variant = stateA ? d.tagGiftVariant : d.tagPaidVariant;
    var img = imageFor(product, variant);

    // Tag size prices come from the mapped Shopify variants, in the shop's
    // own money_format — the "$32"/"$39" literals here were hardcoded USD
    // that neither tracked catalog changes nor matched the currency every
    // other price on the screen was rendered in. The mm size also gets its
    // inch equivalent, like every other measurement in the builder.
    var self2 = this;
    var sizeSeg = '<span class="lb-fr__seg">' + TAG_SIZES.map(function (sz) {
      var v = d.tagPaidProduct ? resolveVariant(d.tagPaidProduct, { size: sz }) : null;
      var mm = parseFloat(sz);
      var inches = isNaN(mm) ? '' : Math.round(mm / 25.4 * 100) / 100 + ' in';
      var priceTail = (!stateA && v) ? ' — ' + money(v.price, self2.moneyFormat) : '';
      return '<button data-action="setTagSize" data-size="' + sz + '" aria-pressed="' + (sz === d.tagSize) + '"' +
        ' title="' + sz + (inches ? ' (' + inches + ') diameter' : '') + '">' + sz + priceTail + '</button>';
    }).join('') + '</span>';

    /* Can this design actually be ordered right now?
       Either it is the unlocked gift, or its PAID variant resolves.

       Neither used to be required. tagValueCents falls back to 0 when the
       paid product is unmapped or its variant won't resolve, so an
       unmappable design rendered with no price at all, added nothing to
       the subtotal, and read to the shopper as free — while the set was
       nowhere near the threshold. buildCartItems then posted a line with
       an undefined id, which the coherence gate silently dropped: the
       shopper approved a free nametag that never reached the cart. */
    var tagOrderable = stateA || !!variant;
    var priceMarkup = stateA
      ? '<b style="font-size:var(--lbfr-t-title)"><span class="lb-fr__strike">' + money(d.tagValueCents || variant && variant.price || 0, this.moneyFormat) + '</span> <span class="lb-fr__ok">FREE</span></b>'
      : (variant
        ? '<b style="font-size:var(--lbfr-t-title)">' + money(variant.price, this.moneyFormat) + '</b>'
        : '<b class="lb-fr__micro" style="color:var(--lbfr-ink-soft)">Currently unavailable</b>');

    // Quiet typographic reveal — an accent eyebrow over the serif hero on
    // paper, not a dark banner slab.
    // Three rendered states + one silent one. `unconfigured` deliberately
    // emits NOTHING: with no gift variant mapped there is no free-tag
    // programme to describe, and any threshold copy would be a promise the
    // component cannot keep. `progressing` is the only branch permitted to
    // format giftRemainingCents (see the giftState invariant in deriveVals),
    // which is what makes "€0.00 away" unreachable by construction.
    var body = (stateA
      ? '<p class="lb-fr__eyebrow" style="color:var(--lbfr-accent-link);margin-bottom:8px">Free gift unlocked</p>' +
        '<h1 class="lb-fr__h1">Your engraved nametag.</h1>'
      : '<h1 class="lb-fr__h1">The engraved nametag</h1>' +
        (d.giftState === 'unconfigured' ? ''
          : '<div class="lb-fr__amberbox" style="margin:12px 0 14px">' +
            (d.giftState === 'progressing'
              ? 'Add <b>' + money(d.giftRemainingCents, this.moneyFormat) + '</b> more to your set and this tag is free.'
              : 'Complimentary with sets over ' + money(this.giftThresholdCents, this.moneyFormat) + ' — or add it on its own.') +
            '</div>')) +
      '<div class="lb-fr__card" style="margin-top:12px">' +
      '<div style="display:flex;gap:16px;flex-wrap:wrap">' +
      '<div style="width:150px;flex:none">' +
      (img ? '<span class="lb-fr__photo lb-fr__photo--contain" style="height:110px;display:block;background-image:url(' + img + ')"></span>' : '<span class="lb-fr__photo lb-fr__skel" style="height:110px;display:block"></span>') +
      '</div>' +
      '<div style="flex:1;min-width:220px">' +
      '<div style="display:flex;justify-content:space-between;align-items:baseline;flex-wrap:wrap;gap:8px"><b style="font-size:var(--lbfr-t-title)">' + designLabel + '</b>' + priceMarkup + '</div>' +
      '<div style="margin:12px 0;display:flex;gap:8px;flex-wrap:wrap">' +
      '<div class="lb-fr__seg">' + TAG_DESIGNS.map(function (t) {
        return '<button data-action="setTagDesign" data-design="' + t.key + '" aria-pressed="' + (t.key === d.tagDesign) + '">' + t.label.replace(' Nametag', '') + '</button>';
      }).join('') + '</div>' + sizeSeg + '</div>' +
      '<p class="lb-fr__q" style="margin:12px 0 6px">Engraving' + (!stateA ? ' <span style="font-weight:400;text-transform:none;letter-spacing:0">(free)</span>' : '') + '</p>' +
      // Two distinct declines: the checkbox keeps the tag but blank; the
      // skip button below removes the tag from the set entirely.
      '<label class="lb-fr__checkbox-row" style="margin:0 0 10px"><input type="checkbox" data-action="toggleNoEngraving"' + (s.tagNoEngraving ? ' checked' : '') + '><span>No engraving needed — leave the tag blank</span></label>' +
      (s.tagNoEngraving
        ? '<p class="lb-fr__sub" style="font-size:var(--lbfr-t-micro);letter-spacing:0.04em">The tag ships blank — it stays in your set.</p>'
        : '<div style="display:grid;gap:10px;max-width:300px">' +
          '<input class="lb-fr__field' + (s.tagError ? ' lb-fr__field--err' : '') + '" data-input="tagEngraveName" maxlength="12" placeholder="Pet’s name" value="' + esc(s.tagEngraveName) + '">' +
          '<input class="lb-fr__field' + (s.tagError ? ' lb-fr__field--err' : '') + '" data-input="tagEngravePhone" maxlength="14" placeholder="Your phone number" value="' + esc(s.tagEngravePhone) + '">' +
          '</div>' +
          (s.tagError ? '<p class="lb-fr__errbox" style="margin-top:10px;max-width:300px">Please provide engraving details or check “Leave tag blank”.</p>' : '') +
          '<p class="lb-fr__sub" style="font-size:var(--lbfr-t-micro);letter-spacing:0.04em;margin-top:12px">Hand-engraved to order. Please review spelling carefully.</p>') +
      '</div></div></div>' +
      '';

    // The CTA used to quote d.subtotalCents — the UNDISCOUNTED running sum
    // of the pieces, excluding the very tag the shopper is about to
    // commit. So the button promised a number that was wrong in two
    // directions at once, and the Review screen it led to showed a
    // different total. It now quotes the real landing total.
    var projectedTotal = d.subtotalCents - (d.pricing ? d.pricing.savingsCents : 0) +
      ((!stateA && variant) ? variant.price : 0);
    // Same CTA grammar as steps 2–4. The tag step used to float a text
    // link ("Continue without a nametag") just above the sticky bar while
    // the bar itself always read "Continue to review" — two competing
    // navigation affordances in one thumb zone, and a different pattern
    // from every other step, which puts the decline IN the bar as a ghost
    // button. The tag counts as chosen once the shopper has typed an
    // engraving or explicitly asked for a blank tag.
    /* CTA hierarchy on the nametag step.

       It used to be: type something, and only then does an "Add nametag"
       button appear; until you do, the single button on screen reads
       "Continue without a nametag". So the loudest — and only — action
       offered on a $32 tag the shopper had just EARNED was to decline it.
       Two costs: the reward the whole set-building flow exists to produce
       could be skipped by reflex, and the empty-engraving validation in
       _commitTag was unreachable, because you could never press Add
       without having already typed something. It was dead code.

       Now: whenever the tag can actually be ordered, "add" is the primary
       and is present from the empty state, so pressing it with nothing
       filled in triggers that validation and the shopper is TOLD they
       forgot the engraving. The decline is never hidden — it sits beside
       the primary as a quiet secondary, and when the tag is free it says
       so plainly ("Skip the free tag") rather than hiding the cost of the
       choice behind neutral wording. No confirmation dialog: making the
       likely intent the default is fair, nagging someone out of a
       decision they have stated is not. */
    var declineSecondary = {
      action: 'skipStep', data: { step: 'tag' },
      label: stateA ? 'Skip the free tag' : 'No nametag',
      short: stateA ? 'Skip free tag' : 'No nametag'
    };
    var tagCta = !tagOrderable
      // Can't be ordered at all — the decline becomes the only honest action.
      ? {
          back: true, ctaAction: 'skipStep', ctaData: { step: 'tag' }, ctaEnabled: true, ctaGhost: true,
          ctaLabel: 'Continue without a nametag', ctaShort: 'No nametag →',
          ctaMeta: 'This nametag isn’t available right now — try the other design.',
          ctaMetaTone: 'neutral'
        }
      : {
          back: true, ctaAction: 'commitTag', ctaEnabled: true,
          ctaLabel: stateA ? 'Add my free nametag' : 'Add nametag &amp; review · ' + money(projectedTotal, this.moneyFormat),
          ctaShort: stateA ? 'Add free tag' : 'Add tag · ' + money(projectedTotal, this.moneyFormat),
          ctaMeta: stateA ? 'Included free with your set' : '',
          ctaMetaTone: stateA ? 'reward' : 'neutral',
          ctaSecondary: declineSecondary
        };
    return this._stepShell(d, 'tag', body, tagCta);
  };

  proto.screenReview = function (d) {
    var s = this.state;
    var self = this;
    var heading = (d.dogLabel ? d.dogLabel + '’s' : 'Your') + ' set — ready when you are.';
    // The button now names its real destination. It used to promise
    // "& checkout" while the code redirected to the cart page — a label
    // that mis-states where a shopper is about to land is a small lie that
    // costs trust at the exact moment they're committing money.
    var ctaLabel = (d.dogLabel ? 'Add ' + d.dogLabel + '’s set' : 'Add set') + ' to cart — ' + money(d.reviewTotalCents, this.moneyFormat);

    // Edit acts on the ITEM, not the step. Previously every line's Edit
    // sent the shopper to that category's step with no indication of which
    // of their three collars they were now editing — and the step, being
    // configured from whatever card happened to be active, often loaded a
    // different one.
    function line(piece, key, label, extra, priceHtml, uid) {
      if (!piece) return '';
      var editAttrs = (uid && uid !== 'tag')
        ? 'data-action="editItem" data-uid="' + uid + '"'
        : 'data-action="editLine" data-line="' + key + '"';
      return '<div class="lb-fr__sl lb-fr__sl--item">' +
        '<span class="lb-fr__sl-thumb">' +
        (piece.image ? '<span class="lb-fr__photo lb-fr__photo--contain" style="width:44px;height:44px;background-image:url(' + piece.image + ')"></span>' : '<span class="lb-fr__photo lb-fr__skel" style="width:44px;height:44px"></span>') +
        '<span>' + label + (extra ? '<br><span class="lb-fr__sub" style="font-size:var(--lbfr-t-micro)">' + extra + '</span>' : '') + '</span></span>' +
        '<span class="lb-fr__sl-right">' + priceHtml +
        '<button class="lb-fr__editbtn" ' + editAttrs + '>Edit</button>' +
        (uid ? '<button class="lb-fr__editbtn" data-action="removeItem" data-uid="' + uid + '">Remove</button>' : '') +
        '</span></div>';
    }

    // A Review screen with nothing in it used to render the full totals
    // card (three zeroes) and a dark "Add set to cart & checkout — €0.00"
    // button wired to a function that returns early on an empty items
    // array: the single most prominent control on the screen did
    // absolutely nothing, forever, with no explanation. Reachable by
    // skipping every step, by removing the last item from here, and by any
    // returning shopper whose saved snapshot had step:'review'.
    if (!d.pieces.length && !s.tag) {
      return '<div class="lb-fr__stage">' +
        '<div class="lb-fr__stage-chrome">' +
        // The wordmark is gone: this component now renders INSIDE the themed
      // page, directly under the store's own header, which already shows
      // "The Lille Björn" in the brand's real logotype. Repeating it in a
      // second, smaller typeface read as a duplicate rather than as app
      // The step label lives IN this bar now — see railHead(). Left: where
      // you are. Right: how to abandon. One row instead of two.
      '<div class="lb-fr__top">' + this.railHead(Object.assign({}, d, { stepIndex: d.railSteps.length })) +
        '<div class="lb-fr__top-utils">' + this._startOverBtn() + '</div></div>' +
        this.progressRail(Object.assign({}, d, { stepIndex: d.railSteps.length })) +
        '</div>' +
        '<div class="lb-fr__stage-scroll"><div class="lb-fr__body lb-fr__body--review lb-fr__screen" style="padding:40px var(--lbfr-gutter) 48px;text-align:center">' +
        '<h1 class="lb-fr__h1">Your set is empty.</h1>' +
        '<p class="lb-fr__sub" style="max-width:400px;margin:0 auto 22px">Nothing has been added yet. Pick a piece to start — two or more unlocks ' + d.discountPct + '% off the set.</p>' +
        '<div class="lb-fr__addmore" style="justify-content:center">' +
        ['collar', 'leash', 'harness'].map(function (k) {
          return '<button class="lb-fr__addmore-btn" data-action="editLine" data-line="' + k + '">＋ ' + STEP_LABELS[k] + '</button>';
        }).join('') +
        '</div></div></div></div>';
    }
    var tagPiece = s.tag ? { image: s.tag.image } : null;
    var tagPriceHtml = d.tagIsGift
      ? '<s class="lb-fr__srow-was">' + money(d.tagValueCents, this.moneyFormat) + '</s><b class="lb-fr__ok">FREE</b>'
      : '<b>' + money(d.tagValueCents, this.moneyFormat) + '</b>';

    var body = '<h1 class="lb-fr__h1" style="text-align:center">' + heading + '</h1>' +
      (d.whyText ? '<p class="lb-fr__fitnote" style="text-align:center;max-width:520px;margin:6px auto 0">' + esc(d.whyText) + '</p>' : '') +
      '<div class="lb-fr__card" style="margin-top:24px;padding:14px 24px">' +
      // Every Mix & Match item renders as its own line — original price
      // struck through beside the live discounted figure once the 10%
      // set discount unlocks.
      d.pieces.map(function (p) {
        var priceHtml = d.discountUnlocked
          ? '<s class="lb-fr__srow-was">' + money(p.cents, self.moneyFormat) + '</s><b>' + money(p.discountedCents, self.moneyFormat) + '</b>'
          : '<b>' + money(p.cents, self.moneyFormat) + '</b>';
        // THE SIZING SAFEGUARD, and the only place it appears.
        //
        // On the line it actually applies to, at the moment of commitment,
        // in the same quiet sub-line that already carries the engraving —
        // not an amber alert, not a gate, not a checkbox. It states what
        // the size was derived from and puts a tape one tap away. A
        // shopper who is fine with it does nothing and loses no time; a
        // shopper who isn't now has the size, the range it covers, and
        // the money all on screen together, which is the first moment any
        // of that is worth their attention.
        //
        // Keyed to the piece's OWN dimension, not the set-wide
        // d.sizingBasis: measure the neck and leave the chest estimated,
        // and the aggregate still says 'breed-average', which put the
        // note back on the collar the shopper had just measured. Leashes
        // have no sizeKey, so they never carry this at all.
        var pSrc = p.sizeKey === 'neck' ? d.neckBasis
          : (p.sizeKey === 'chest' ? d.chestBasis : null);
        var basis = (pSrc === 'breed-average')
          ? 'Size from ' + d.averagesPhrase + ' · ' +
            '<button class="lb-fr__link" style="font-weight:400;letter-spacing:0;text-transform:none;font-size:inherit"' +
            ' data-action="openMeasure" data-key="' + p.sizeKey + '">measure ' + d.measureSubject + '</button>'
          : '';
        var sub = [p.extra, basis].filter(Boolean).join('<br>');
        return line(p, p.key, p.detail, sub, priceHtml, p.uid);
      }).join('') +
      (tagPiece ? line(tagPiece, 'tag', (TAG_DESIGNS.filter(function (t) { return t.key === s.tag.design; })[0] || {}).label + ' — ' + s.tag.size, s.tag.noEngraving ? 'No engraving — blank tag' : 'Engraving: ' + esc(s.tag.engraveName || '—') + (s.tag.engravePhone ? ' · ' + esc(s.tag.engravePhone) : ''), tagPriceHtml, 'tag') : '') +
      // Review was a terminus: the only way to add anything more was the
      // single conditional "Add harness" nudge. A shopper who reached it
      // and then wanted a second leash — or the tag they'd skipped — had
      // to hunt the progress rail and hope. Every category is now one tap
      // away from here, and each returns straight back to Review.
      '<div class="lb-fr__addmore">' +
      '<span class="lb-fr__addmore-label">Add more</span>' +
      ['collar', 'leash', 'harness'].map(function (k) {
        return '<button class="lb-fr__addmore-btn" data-action="editLine" data-line="' + k + '">＋ ' + STEP_LABELS[k] + '</button>';
      }).join('') +
      // "(free)" requires a mapped, available gift variant — not merely a
      // cleared threshold. Promising free against an unconfigured gift
      // product is the same defect as the tag step's "€0.00 away".
      (s.tag ? '' : '<button class="lb-fr__addmore-btn" data-action="editLine" data-line="tag">＋ Nametag' + (d.giftState === 'unlocked' ? ' (free)' : '') + '</button>') +
      '</div>' +
      '</div>' +
      (d.mismatchWarning ? '<div class="lb-fr__amberbox" style="margin-top:14px;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap">' +
        '<span>A slim 1.5cm leash looks under-built next to a 4.5cm collar.</span>' +
        '<button class="lb-fr__btn lb-fr__btn--small" data-action="matchLeashWidth">Match to 2cm</button></div>' : '') +
      this._upsellEngine(d) +
      // Spelling confirmation only when something is actually personalized.
      (d.needsSpellingConfirm ? '<div class="lb-fr__checkbox-row lb-fr__spellrow' +
      (s.spellingConfirmed ? ' lb-fr__spellrow--done' : '') +
      (s.spellingPrompt && !s.spellingConfirmed ? ' lb-fr__spellrow--prompt' : '') + '" id="lbfr-spellrow">' +
      '<input type="checkbox" id="lbfr-spelling" data-action="toggleSpelling"' + (s.spellingConfirmed ? ' checked' : '') + '>' +
      '<label for="lbfr-spelling">I’ve reviewed all spelling — personalized pieces are crafted to order. <b>Required</b></label></div>' : '') +
      // Totals that show their working. The old block collapsed every
      // reward into one anonymous "You save" figure, so the two things the
      // whole builder is designed to earn — the set discount and the
      // complimentary tag — were invisible at the exact moment they should
      // feel earned. Each reward is now its own itemised deduction line,
      // and the not-yet-unlocked ones are stated with the real gap.
      '<div class="lb-fr__card lb-fr__totals">' +
      '<div class="lb-fr__sl"><span>Subtotal</span><span style="font-weight:300">' + money(d.reviewSubtotalCents, this.moneyFormat) + '</span></div>' +
      (d.reviewDiscountCents
        ? '<div class="lb-fr__sl"><span class="lb-fr__ok">' + d.discountPct + '% set discount <span class="lb-fr__savetag">' + d.itemCount + ' pieces</span></span><span class="lb-fr__ok" style="font-weight:300">−' + money(d.reviewDiscountCents, this.moneyFormat) + '</span></div>'
        : '') +
      (d.reviewGiftSavingsCents
        ? '<div class="lb-fr__sl"><span class="lb-fr__ok">Complimentary engraved tag</span><span class="lb-fr__ok" style="font-weight:300">−' + money(d.reviewGiftSavingsCents, this.moneyFormat) + '</span></div>'
        : '') +
      '<div class="lb-fr__sl lb-fr__sl--noborder" style="padding:14px 0 0"><span style="font-size:var(--lbfr-t-micro);font-weight:500;letter-spacing:0.18em;text-transform:uppercase">Total</span><span class="lb-fr__total-figure">' + money(d.reviewTotalCents, this.moneyFormat) + '</span></div>' +
      (d.youSaveCents ? '<p class="lb-fr__totals-saved">You’re saving ' + money(d.youSaveCents, this.moneyFormat) + ' on this set</p>' : '') +
      (function () {
        // What's still on the table, priced.
        var open = [];
        if (!d.discountUnlocked && d.pendingDiscountCents) {
          open.push('Add one more piece → <b>' + d.discountPct + '% off</b>, saving ' + money(d.pendingDiscountCents, self.moneyFormat) + '+');
        }
        if (!d.giftUnlocked && d.giftRemainingCents) {
          open.push(money(d.giftRemainingCents, self.moneyFormat) + ' more → <b>free engraved tag</b>');
        }
        return open.length ? '<p class="lb-fr__totals-open">' + open.join('<br>') + '</p>' : '';
      })() +
      '</div>' +
      '<p style="font-size:var(--lbfr-t-micro);color:var(--lbfr-ink-soft);text-align:center;margin:12px 0 0">Complimentary Worldwide Shipping&emsp;·&emsp;Handcrafted to order in ' + esc(this.leadTimeText) + '&emsp;·&emsp;14-day returns on non-personalized items</p>' +
      // Add-to-cart failures used to be completely silent: the fetch
      // rejected, cartStatus went to 'error', and nothing anywhere
      // rendered it — the shopper pressed the button and the page simply
      // didn't respond, with no way to tell a network failure from a
      // broken button.
      (s.cartStatus === 'error'
        ? '<div class="lb-fr__errbox" style="max-width:420px;margin:14px auto 0;text-align:center">' +
          esc(s.cartError || 'We couldn’t add your set to the cart just now.') +
          ' Please try again — nothing has been charged, and your set is saved.</div>'
        : '') +
      // Review is the one screen that does NOT go through _stepShell, so
      // it renders neither .lb-fr__foot nor .lb-fr__mbar — the primary
      // action sat inline at the end of a 700px+ item card with no
      // persistent bar anywhere. On a 932px phone the button was ~570px
      // below the fold on a three-piece set: the single most important
      // control in the whole builder, and the only step where you had to
      // go looking for it. The class gives the stylesheet something to
      // pin on mobile.
      '<div class="lb-fr__review-action" style="text-align:center;margin-top:12px"><button class="lb-fr__btn" style="padding:14px 34px" data-action="addToCart"' +
      (s.cartStatus === 'adding' ? ' disabled' : '') + '>' +
      (s.cartStatus === 'adding' ? 'Adding to cart…' : (s.cartStatus === 'error' ? 'Try again — ' + money(d.reviewTotalCents, this.moneyFormat) : ctaLabel)) + '</button>' +
      (d.needsSpellingConfirm && !s.spellingConfirmed ? '<p class="lb-fr__micro" style="margin-top:8px">Confirm the spelling above to continue — personalized pieces can’t be returned.</p>' : '') +
      '</div>';

    // Same framed-stage chrome as every builder step, so Review keeps the
    // premium card layout; every rail segment stays clickable (gotoStep
    // flags editingFrom so revisited steps return straight here).
    return '<div class="lb-fr__stage">' +
      '<div class="lb-fr__stage-chrome">' +
      // The wordmark is gone: this component now renders INSIDE the themed
      // page, directly under the store's own header, which already shows
      // "The Lille Björn" in the brand's real logotype. Repeating it in a
      // second, smaller typeface read as a duplicate rather than as app
      // The step label lives IN this bar now — see railHead(). Left: where
      // you are. Right: how to abandon. One row instead of two.
      '<div class="lb-fr__top">' + this.railHead(Object.assign({}, d, { stepIndex: d.railSteps.length })) +
      '<div class="lb-fr__top-utils">' + this._startOverBtn() + '</div></div>' +
      this.progressRail(Object.assign({}, d, { stepIndex: d.railSteps.length })) +
      '</div>' +
      '<div class="lb-fr__stage-scroll">' +
      '<div class="lb-fr__body lb-fr__body--review lb-fr__screen" style="padding:16px var(--lbfr-gutter) 32px">' + body + '</div>' +
      '</div></div>';
  };

  /* ============================================================
     Step 5 upsell engine.

     Review previously carried exactly ONE upsell — a hardcoded "add the
     matching harness" banner gated on `!harnesses.length && path !==
     'harness' && !giftUnlocked`. Every other basket shape got nothing:
     a collar-only set, a tag-only set, a set one item short of the
     discount, a set a few pounds short of the free tag, a complete set
     with no tag. Those are the majority of states a real shopper lands
     in, and each has a different, obvious next step.

     This picks the single most relevant offer for the CURRENT basket,
     ranked by what the shopper stands to gain, and states the gain in
     real money. One offer, not a wall — a stack of banners reads as
     pressure and converts worse than a single well-chosen one.
     ============================================================ */
  proto._upsellEngine = function (d) {
    var s = this.state;
    var self = this;
    var offers = [];
    var hasCollar = s.collars.length > 0;
    var hasLeash = s.leashes.length > 0;
    var hasHarness = s.harnesses.length > 0;
    var pieces = d.itemCount;

    // --- Tier 1: the set discount. Biggest, most concrete gain. ---
    if (pieces === 1 && d.pendingDiscountCents) {
      // Priority is the natural companion, not array order: a leash-only
      // bag wants a collar (that's what a leash clips to), not a harness.
      var missing = !hasCollar ? 'collar' : (!hasLeash ? 'leash' : 'harness');
      offers.push({
        rank: 100,
        eyebrow: 'One piece away',
        head: 'Add a ' + missing + ' and save ' + money(d.pendingDiscountCents, this.moneyFormat),
        copy: 'A second piece takes ' + d.discountPct + '% off the whole set — including the ' +
          (hasCollar ? 'collar' : (hasLeash ? 'leash' : 'harness')) + ' already in your bag.',
        cta: 'Add a ' + missing, action: missing
      });
    }
    // --- Tier 2: the free tag threshold, when it is genuinely close. ---
    if (!d.giftUnlocked && pieces >= 1 && d.giftRemainingCents > 0) {
      var suggest = !hasHarness ? 'harness' : (!hasLeash ? 'leash' : 'collar');
      offers.push({
        rank: d.giftProgressPct >= 60 ? 90 : 55,
        eyebrow: money(d.giftRemainingCents, this.moneyFormat) + ' to go',
        head: 'A complimentary engraved nametag',
        copy: 'Sets over ' + money(this.giftThresholdCents, this.moneyFormat) + ' include a hand-engraved brass tag. ' +
          'Adding a ' + suggest + ' would clear it.',
        cta: 'Add a ' + suggest, action: suggest
      });
    }
    // --- Tier 3: complete the walk system. ---
    if (hasCollar && !hasLeash) {
      offers.push({
        rank: 70, eyebrow: 'Completes the set',
        head: 'A leash cut from the same hide',
        copy: 'Matched to your collar\u2019s leather and hardware, in the width we\u2019d recommend for ' +
          (s.breedName ? esc(s.breedName) : 'your dog') + '.',
        cta: 'Choose a leash', action: 'leash'
      });
    } else if (hasCollar && hasLeash && !hasHarness) {
      offers.push({
        rank: 65, eyebrow: 'Completes the set',
        head: 'The matching harness',
        copy: 'For pullers, puppies and long walks \u2014 the same leather, sized to ' +
          (s.dogName ? esc(s.dogName) : 'your dog') + '\u2019s chest.',
        cta: 'Choose a harness', action: 'harness'
      });
    } else if (!hasCollar && (hasLeash || hasHarness)) {
      offers.push({
        rank: 68, eyebrow: 'Completes the set',
        head: 'A collar to match',
        copy: 'Cut from the same hide as the rest of your set, fitted to ' +
          (s.dogName ? esc(s.dogName) : 'your dog') + '\u2019s neck.',
        cta: 'Choose a collar', action: 'collar'
      });
    }
    // --- Tier 4: the tag itself, once it is free and not yet taken. ---
    if (d.giftUnlocked && !s.tag && d.tagGiftAvailable) {
      offers.push({
        rank: 95, eyebrow: 'Already unlocked \u00b7 free',
        head: 'Your engraved nametag is included',
        copy: 'Your set qualifies for a hand-engraved brass tag at no charge. It isn\u2019t in your bag yet.',
        cta: 'Claim the free tag', action: 'tag'
      });
    }
    // --- Tier 5: a tag-only basket. ---
    if (!hasCollar && !hasLeash && !hasHarness && s.tag) {
      offers.push({
        rank: 80, eyebrow: 'Start the set',
        head: 'Add the collar it hangs from',
        copy: 'A nametag needs a collar. Ours are cut to your dog\u2019s neck and finished by hand.',
        cta: 'Choose a collar', action: 'collar'
      });
    }
    if (!offers.length) return '';
    offers.sort(function (a, b) { return b.rank - a.rank; });
    var o = offers[0];
    return '<div class="lb-fr__upsell">' +
      '<span class="lb-fr__upsell-eyebrow">' + o.eyebrow + '</span>' +
      '<p class="lb-fr__upsell-head">' + o.head + '</p>' +
      '<p class="lb-fr__upsell-copy">' + o.copy + '</p>' +
      '<button class="lb-fr__btn lb-fr__btn--small" data-action="editLine" data-line="' + o.action + '">' + o.cta + '</button>' +
      '</div>';
  };

  proto.screenAdded = function (d) {
    var s = this.state;
    function line(piece, label, extra, isFree) {
      if (!piece) return '';
      return '<div class="lb-fr__sl"><span class="lb-fr__sl-thumb">' +
        (piece.image ? '<span class="lb-fr__photo lb-fr__photo--contain" style="width:36px;height:36px;background-image:url(' + piece.image + ')"></span>' : '') +
        '<span>' + label + (extra ? '<br><span class="lb-fr__sub" style="font-size:var(--lbfr-t-micro)">' + extra + '</span>' : '') + '</span></span>' +
        '<b' + (isFree ? ' class="lb-fr__ok"' : '') + '>' + (isFree ? money(0, d.moneyFormat) : money(piece.cents, d.moneyFormat)) + '</b></div>';
    }
    var heading = (d.dogLabel ? d.dogLabel + '’s' : 'Your') + ' set is in the bag.';
    // (No top bar here: nothing left to put in it once the wordmark went,
    //  and the confirmation screen carries its own heading.)
    return '' +
      '<div class="lb-fr__body" style="max-width:520px;padding:24px var(--lbfr-gutter)">' +
      '<h1 class="lb-fr__h1" style="text-align:center">' + heading + '</h1>' +
      '<div class="lb-fr__card" style="margin-top:12px">' +
      d.pieces.map(function (p) { return line({ image: p.image, cents: p.cents }, p.detail, p.extra); }).join('') +
      line(s.tag ? { image: s.tag.image } : null, (TAG_DESIGNS.filter(function (t) { return t.key === (s.tag || {}).design; })[0] || {}).label + (s.tag && s.tag.noEngraving ? ' — blank' : ' — engraved'), '', d.tagIsGift) +
      (d.reviewDiscountCents ? '<div class="lb-fr__sl"><span class="lb-fr__ok">Set discount (automatic)</span><span class="lb-fr__ok" style="font-weight:300">−' + money(d.reviewDiscountCents, d.moneyFormat) + '</span></div>' : '') +
      '<div class="lb-fr__sl lb-fr__sl--noborder" style="padding:14px 0 0"><span style="font-size:var(--lbfr-t-micro);font-weight:500;letter-spacing:0.18em;text-transform:uppercase">Total</span><span class="lb-fr__total-figure">' + money(d.reviewTotalCents, d.moneyFormat) + '</span></div>' +
      '</div>' +
      '<p class="lb-fr__sub" style="text-align:center;margin:10px 0">Handcrafted to order · Complimentary Worldwide Shipping · free stamping &amp; engraving included.</p>' +
      '<button class="lb-fr__btn" style="width:100%" data-action="checkout">Proceed to Checkout</button>' +
      '<div style="text-align:center;margin-top:10px"><button class="lb-fr__link" data-action="keepBuilding">Keep building / edit set</button></div>' +
      '</div>';
  };

  /* ============================================================
     Inline size assistant
     Replaces the former right-sliding measurement drawer: expands in
     place directly beneath the size control that opened it (the sizecard
     verify link, the neutral size select, or a Step-1 measurement row),
     so the size resolution updates live in the control the shopper is
     already looking at.
     ============================================================ */
  // Modal chrome: blur-backed centered dialog on desktop, bottom sheet
  // on mobile (CSS switches the geometry) — the step layout never shifts.
  proto._sizeModal = function (d) {
    var s = this.state;
    if (!s.measureOpen || !s.measureKey) return '';
    return '<div class="lb-fr__scrim" data-action="closeMeasure"></div>' +
      '<div class="lb-fr__modal" role="dialog" aria-modal="true" aria-label="Size assistant">' +
      '<div class="lb-fr__modal-grabber"></div>' +
      this._inlineSizeAssistant(d, s.measureKey) +
      '</div>';
  };

  proto._inlineSizeAssistant = function (d, key) {
    var s = this.state;
    if (!s.measureOpen || s.measureKey !== key) return '';
    var isNeck = key === 'neck';
    var breed = d.breed;
    // STAGED editing: everything in the modal reads/writes state.measureDraft.
    // Nothing touches the main form until "Apply"; ×/scrim/Escape discard.
    var draft = s.measureDraft;
    var original = isNeck ? s.neck : s.chest;
    var ageSizes = s.breedName ? FL.applyAgeProfile(breed, s.ageProfile) : null;
    var estimateCm = ageSizes ? (isNeck ? ageSizes.neckSize : ageSizes.chestSize) : null;
    var effectiveCm = draft !== null ? draft : estimateCm;
    var grounded = effectiveCm !== null && effectiveCm !== undefined;
    var nodes = isNeck ? FL.NECK_NODES : FL.CHEST_NODES;
    var max = isNeck ? FL.NECK_CUSTOM_MAX : FL.CHEST_CUSTOM_MAX;
    var sizes = isNeck ? FL.COLLAR_SIZES : FL.HARNESS_SIZES;
    var resolvedSize = grounded ? FL.computeSizeLetter(isNeck ? 'neck' : 'chest', effectiveCm, s.measureDraftBoundary) : null;
    var evalResult = grounded ? FL.evaluateMeasurement(isNeck ? 'neck' : 'chest', effectiveCm, { breedProfile: breed, ageProfile: s.ageProfile }) : { isCustom: false };
    // Exact between-sizes state from the calculator: a measurement on a
    // genuine size boundary stays ambiguous until snug/relaxed is chosen.
    var tieUnresolved = grounded && !evalResult.isCustom && evalResult.showBoundary && !s.measureDraftBoundary;
    var displayVal = grounded ? (s.unit === 'cm' ? round2(effectiveCm) : cmToInPrecise(effectiveCm)) : '';

    // Context-aware fitting tip — the Size Guide calculator's own copy
    // (FL.WHY_TEXT / puppy disclaimers), shown when a breed is known.
    var tip = '';
    if (s.breedName) {
      var whyFn = FL.WHY_TEXT[breed.category] || FL.WHY_TEXT.standard;
      var puppyNote = s.ageProfile === 'puppy'
        ? (isNeck ? (FL.PUPPY_COLLAR_DISCLAIMER[breed.category] || '') : FL.PUPPY_HARNESS_DISCLAIMER)
        : '';
      tip = '<div class="lb-fr__tipbox lb-fr__reveal">' +
        '<span class="lb-fr__tipbox-label">Fitting note — ' + esc(s.breedName) + '</span>' +
        '<p>' + esc(whyFn(breed)) + (puppyNote ? ' ' + esc(puppyNote) : '') + '</p></div>';
    }

    // Interactive slider, synced live with the chart highlight below.
    // Native unit domains (calculator parity): in IN mode the slider's
    // min/max/step switch to inches (0.1 step) so dragging feels native —
    // only the presentation changes, state stays in cm.
    var isIn = s.unit === 'in';
    var sliderValCm = grounded ? round2(effectiveCm) : Math.round((nodes[0] + max) / 2);
    var sliderPct = Math.round(((sliderValCm - nodes[0]) / (max - nodes[0])) * 100);
    var domainMin = isIn ? cmToInPrecise(nodes[0]) : nodes[0];
    var domainMax = isIn ? cmToInPrecise(max) : max;
    var domainStep = isIn ? 0.1 : 0.5;
    var domainVal = isIn ? cmToInPrecise(sliderValCm) : sliderValCm;
    var slider = '<input type="range" class="lb-fr__slider" data-input="measureSlider" min="' + domainMin + '" max="' + domainMax + '" step="' + domainStep + '" value="' + domainVal + '"' +
      ' style="background:linear-gradient(to right, var(--lbfr-accent) ' + sliderPct + '%, #e5e0d8 ' + sliderPct + '%)"' +
      ' aria-label="' + (isNeck ? 'Neck' : 'Chest') + ' measurement slider, in ' + (isIn ? 'inches' : 'centimeters') + '">' +
      '<div class="lb-fr__slider-scale"><span>' + domainMin + ' ' + s.unit + '</span><span>' + domainMax + ' ' + s.unit + '</span></div>';

    // Between-sizes interlock, with the ORIGINAL measuring-technique
    // phrasing from the calculators: the question is how the tape was
    // held, not which fit is preferred (a loose tape reads large, so the
    // true size is the smaller letter).
    var boundaryBanner = grounded && !evalResult.isCustom && evalResult.showBoundary
      ? '<div class="lb-fr__recbox lb-fr__reveal" style="margin:12px 0">' +
        '<div style="margin-bottom:8px">' + esc(evalResult.boundaryHeaderText) + '</div>' +
        '<span class="lb-fr__boundary-btns">' +
        '<button class="lb-fr__wbtn' + (s.measureDraftBoundary === 'loose' ? ' lb-fr__wbtn--sel' : '') + '" data-action="pickBoundary" data-choice="loose">LOOSE / RELAXED MEASUREMENT</button>' +
        '<button class="lb-fr__wbtn' + (s.measureDraftBoundary === 'snug' ? ' lb-fr__wbtn--sel' : '') + '" data-action="pickBoundary" data-choice="snug">SNUG / EXACT SKIN MEASUREMENT</button>' +
        '</span></div>'
      : '';

    // Breed-relative guardrail (amber/red), verbatim from the calculator.
    var guardrail = grounded && evalResult.showGuardrail
      ? '<div class="' + (evalResult.guardrailTone === 'red' ? 'lb-fr__errbox' : 'lb-fr__amberbox') + '" style="margin:10px 0">' + esc(evalResult.guardrailMessage) + '</div>'
      : '';

    // Size rows stage a draft pick; the highlight + Apply label follow.
    // Third column: typical-breed examples per size (Size Guide parity).
    ensureBreedSizeMaps();
    var breedsMap = isNeck ? BREEDS_BY_COLLAR_SIZE : BREEDS_BY_HARNESS_SIZE;
    var chart = '<table class="lb-fr__assist-table"><tr><th>Size</th><th>' + (isNeck ? 'Neck' : 'Chest') + '</th><th>Typical breeds</th></tr>' +
      sizes.map(function (sz, i) {
        var lo = nodes[i], hi = nodes[i + 1] || max;
        // During an unresolved boundary tie BOTH candidate rows glow.
        var match = tieUnresolved
          ? (sz === evalResult.boundaryLower || sz === evalResult.boundaryUpper)
          : sz === resolvedSize;
        return '<tr data-action="pickSizeRow" data-size="' + sz + '" role="button" tabindex="0" aria-label="Select size ' + sz + '"' + (match ? ' data-match="true"' : '') + '><td class="lb-fr__assist-sz">' + sz + '</td>' +
          '<td>' + lo + '–' + hi + ' cm <span class="lb-fr__assist-in">' + inDisp(lo) + '–' + inDisp(hi) + ' in</span></td>' +
          '<td class="lb-fr__assist-breeds">' + esc(topBreedsForSize(breedsMap[sz], 3)) + '</td></tr>';
      }).join('') + '</table>';

    var result;
    if (grounded && evalResult.isCustom) {
      result = '<div class="lb-fr__amberbox" style="margin:12px 0">' + esc(FL.customBannerText(isNeck, !isNeck)) + ' <a class="lb-fr__link" style="color:var(--lbfr-amber-text)" href="' + FL.buildCustomSizeMailto({ breedName: s.breedName, ageProfile: s.ageProfile, breedProfile: breed, neckSize: Math.round(isNeck ? effectiveCm : d.neckCm), chestSize: Math.round(isNeck ? d.chestCm : effectiveCm), isCustomCollar: isNeck, isCustomHarness: !isNeck, supportEmail: this.supportEmail }) + '">Contact us →</a></div>';
    } else if (grounded && resolvedSize && !tieUnresolved) {
      result = '<p class="lb-fr__assist-result">' + dual(effectiveCm) + ' → size <span class="lb-fr__assist-letter">' + resolvedSize + '</span></p>';
    } else if (tieUnresolved) {
      result = '';
    } else {
      result = '<p class="lb-fr__assist-result" style="color:var(--lbfr-ink-soft)">Enter a measurement — or tap a size below.</p>';
    }

    var isCustomFit = grounded && evalResult.isCustom;
    var applyLabel, applyEnabled;
    if (isCustomFit) { applyLabel = 'Save measurement'; applyEnabled = true; }
    else if (tieUnresolved) { applyLabel = 'Choose loose or snug'; applyEnabled = false; }
    else if (draft === null && original !== null) { applyLabel = 'Remove size'; applyEnabled = true; }
    else if (resolvedSize) { applyLabel = 'Apply size ' + resolvedSize; applyEnabled = true; }
    else { applyLabel = 'Select a size'; applyEnabled = false; }

    var clearLink = (draft !== null || original !== null)
      ? '<button class="lb-fr__link" data-action="clearMeasure" style="font-size:var(--lbfr-t-micro)">Clear measurement</button>'
      : '';

    // Shell split into a scrolling BODY and a pinned FOOTER. Everything
    // used to live in one scroll box, so on a 390px phone the Apply
    // button — the only way to commit a measurement — sat ~79px below the
    // fold with no indication it was there. The button is now always
    // visible; only the reference chart scrolls under it, behind a fade
    // mask that shows there is more.
    // HOW to measure. The assistant offered a slider, a size chart and a
    // breed fitting note, but never once said where to put the tape —
    // for a made-to-order product whose personalised pieces are
    // non-returnable, that is the single highest-cost omission in the
    // flow. A dog owner asked for "neck circumference" with no guidance
    // measures the middle of the throat, or pulls the tape tight, and
    // the collar that arrives doesn't fit. Instructions are dimension-
    // specific, lead with WHERE, and state the two-finger rule that
    // makes a snug tape produce a comfortable collar.
    var howTo = isNeck
      ? [['Find the base', 'Where the collar naturally sits — low on the neck, just above the shoulders. Not up under the jaw.'],
         ['Wrap, don’t pull', 'Take the tape all the way around and let it sit flat against the coat.'],
         ['Two fingers', 'You should be able to slide two fingers flat under the tape. That gap is the fit.']]
      : [['Behind the front legs', 'Measure the widest part of the ribcage, a couple of centimetres behind the elbows.'],
         ['Stand them square', 'Chest measurements taken while a dog is sitting read small.'],
         ['Two fingers', 'Snug against the coat, with room to slide two fingers flat underneath.']];
    var guide = '<details class="lb-fr__howto"' + (s.breedName ? '' : ' open') + '>' +
      '<summary class="lb-fr__howto-sum">How to measure ' + (isNeck ? 'the neck' : 'the chest') +
      '<span class="lb-fr__howto-chev" aria-hidden="true">›</span></summary>' +
      '<ol class="lb-fr__howto-steps">' +
      howTo.map(function (h) {
        return '<li><b>' + h[0] + '</b><span>' + h[1] + '</span></li>';
      }).join('') +
      '<li class="lb-fr__howto-note"><b>No tape?</b><span>A strip of paper or a shoelace works — mark where it meets, then hold it against a ruler.</span></li>' +
      '</ol></details>';

    return '<div class="lb-fr__assist lb-fr__reveal">' +
      '<div class="lb-fr__assist-body">' +
      '<div class="lb-fr__assist-head">' +
      '<span class="lb-fr__assist-title">' + (isNeck
        ? ((s.neck === null && !s.breedName) ? 'Select your dog’s neck measurement' : 'Neck circumference')
        : 'Chest circumference') + '</span>' +
      '<button class="lb-fr__overlay-close" data-action="closeMeasure" aria-label="Close without applying">×</button></div>' +
      guide + tip +
      '<div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin:12px 0 4px">' +
      '<span class="lb-fr__stepper">' +
      '<button class="lb-fr__stepbtn" data-action="stepMeasure" data-dir="dec" aria-label="Decrease measurement">–</button>' +
      '<input class="lb-fr__measure-value" data-input="measureValue" value="' + displayVal + '" placeholder="e.g. 34" inputmode="decimal">' +
      '<button class="lb-fr__stepbtn" data-action="stepMeasure" data-dir="inc" aria-label="Increase measurement">+</button>' +
      '</span>' +
      '<span class="lb-fr__seg lb-fr__seg--pill"><button data-action="setUnit" data-unit="cm" aria-pressed="' + (s.unit === 'cm') + '">cm</button><button data-action="setUnit" data-unit="in" aria-pressed="' + (s.unit === 'in') + '">in</button></span>' +
      clearLink + '</div>' +
      // Live status label (calculator parity): "Size S", "Size XS/S
      // Boundary", or "Custom Size" — announced as it changes.
      '<div class="lb-fr__assist-sizelabel" aria-live="polite">' + (grounded ? esc(evalResult.sizeLabel || '') : '') + '</div>' +
      slider +
      guardrail + boundaryBanner +
      result + chart +
      '</div>' +
      '<div class="lb-fr__assist-foot">' +
      '<button class="lb-fr__btn" data-action="applyMeasure"' + (applyEnabled ? '' : ' disabled') + '>' + applyLabel + '</button>' +
      '</div>' +
      '</div>';
  };

  /* ============================================================
     Persistent panels
     ============================================================ */
  // Progress header — same visual language as the Size Guide's own
  // STEP N OF 4 label + 2px segmented bar (.lb-sg-progress), extended
  // with per-segment step names. "Step 2 of 5 — Collar" answers where am
  // I / how much is left at a glance; completed segments are real buttons
  // that jump back to that step. Pure progress only — utility actions
  // (Start over, Quick Assemble) live in the top bar, not here.
  /* "Step 1 of 5 · Your Dog" — now rendered INTO the top utility bar
     rather than above the rail.

     Removing the duplicated wordmark left that bar holding nothing but a
     right-aligned "Start over", so the chrome spent a full row on one
     small link and then opened a SECOND row underneath for the step
     label. Merging them reclaims ~48px above the fold and gives the bar
     something to be: where you are on the left, how to abandon on the
     right.

     Kept as its own method (not inlined) because three screens need it —
     the step shell, Review, and any future screen with a rail. */
  proto.railHead = function (d) {
    var atReview = d.stepIndex >= d.railSteps.length;
    var current = d.railSteps[d.stepIndex] || {};
    // "Step 2 of 5" alone told a mobile shopper how far along they were
    // but not what they were looking at — the serif title is hidden at
    // ≤480px and the per-segment names with it, leaving five unlabelled
    // bars. The count and the current step's NAME share one line at every
    // width, so the header always answers "where am I" in words.
    return '<span class="lb-fr__rail-head">' +
      '<span class="lb-fr__rail-count">' + (atReview ? 'Review' : 'Step ' + (d.stepIndex + 1) + ' of ' + d.railSteps.length) + '</span>' +
      '<span class="lb-fr__rail-now">' + (atReview ? 'Your set' : (current.label || '')) + '</span>' +
      '<span class="lb-fr__rail-title">' + (atReview ? 'Your set' : (current.label || '')) + '</span>' +
      '</span>';
  };

  proto.progressRail = function (d) {
    return '<div class="lb-fr__rail">' +
      '<div class="lb-fr__rail-steps">' + d.railSteps.map(function (st, i) {
        var stateCls = i < d.stepIndex ? ' lb-fr__step--done' : (i === d.stepIndex ? ' lb-fr__step--current' : '');
        // Numeric indexing (01, 02 …) — completion is carried by the
        // filled underline and the index/name tone alone; no glyphs.
        // Every segment is clickable at any point in the flow.
        return '<button class="lb-fr__step' + stateCls + '" data-action="gotoStep" data-step="' + st.key + '" aria-label="Go to ' + st.label + '"' +
          (i === d.stepIndex ? ' aria-current="step"' : '') + '>' +
          '<span class="lb-fr__step-name"><span class="lb-fr__step-idx">0' + (i + 1) + '</span>' + st.label + '</span>' +
          '<span class="lb-fr__step-bar"></span></button>';
      }).join('') + '</div></div>';
  };

  // One seamless benefit strip — a quiet tone-shifted band, no boxed
  // widgets, no icon badges. Two facts (set discount / nametag threshold)
  // set in the same label-over-value type as the rest of the system, the
  // gift's thin progress track being the only graphic element.
  // One typographic strip, benefits separated by middle dots — no boxed
  // chips, no meters. Unlocked facts carry the accent tone and real
  // dollar figures.
  // Cohesive status module: three reward checkpoints over one micro
  // progress bar. The fill width transitions on every add/remove (the
  // morph patches only the style attribute, so the CSS width transition
  // animates), and every amount renders in the store's active currency.
  proto.incentiveHud = function (d) {
    var pct = d.giftUnlocked ? 100 : d.giftProgressPct;
    function goal(label, done) {
      return '<span class="lb-fr__hud-goal' + (done ? ' lb-fr__hud-goal--done' : '') + '">' +
        (done ? '<i aria-hidden="true">\u2713</i>' : '') + label + '</span>';
    }
    var progressLabel = d.giftUnlocked
      ? 'Complete \u2014 every reward unlocked'
      : (d.discountUnlocked && d.lineSavingsCents
        ? 'Saving ' + money(d.lineSavingsCents, this.moneyFormat) + ' \u00b7 ' + money(d.giftRemainingCents, this.moneyFormat) + ' to the engraved tag'
        : (d.subtotalCents
          ? money(d.giftRemainingCents, this.moneyFormat) + ' to the complimentary engraved tag'
          : 'Rewards unlock as your set grows'));
    return '<div class="lb-fr__hud">' +
      '<div class="lb-fr__hud-goals">' +
      goal('Worldwide Shipping', true) +
      goal(d.discountPct + '% Off Set', d.discountUnlocked) +
      goal('Free Engraved Tag', d.giftUnlocked) +
      '</div>' +
      '<div class="lb-fr__progress' + (d.giftUnlocked ? ' lb-fr__progress--done' : '') + '" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + pct + '" aria-label="Progress toward the complimentary engraved nametag">' +
      '<span class="lb-fr__progress-track"><i style="width:' + pct + '%"></i></span>' +
      '</div>' +
      '<div class="lb-fr__progress-label">' + progressLabel + '</div>' +
      '</div>';
  };

  // One summary panel — each chosen piece appears exactly once: thumbnail,
  // name over its leather, price on the same line. Replaces the former
  // "Bespoke Selection" + "Fitted Selection" pair, which listed the same
  // items twice in two stacked formats.
  /* Two-step "Start over".
     A single unguarded click on a quiet text button in the top bar wiped
     the entire configuration AND the localStorage snapshot — irreversibly,
     with no confirmation, sitting inches from "Quick Assemble". */
  proto._startOverBtn = function () {
    if (!this.state.confirmReset) {
      return '<button class="lb-fr__startover" data-action="startOver">Start over</button>';
    }
    return '<span class="lb-fr__confirm">Clear everything?' +
      '<button class="lb-fr__confirm-yes" data-action="startOverConfirm">Yes, clear</button>' +
      '<button class="lb-fr__confirm-no" data-action="cancelReset">Keep</button></span>';
  };

  /* The savings ledger that lives beside the shopper the whole way.
     The reward banner at the top of the screen announced the programme;
     nothing next to the running total ever reported progress against it
     in money. This block sits directly under the subtotal — where the
     price is — and states, in the shop's real currency, what has already
     been earned and precisely what the next piece is worth. */
  proto._incentiveLedger = function (d) {
    var out = '';
    if (d.discountUnlocked && d.lineSavingsCents) {
      out += '<div class="lb-fr__ledger lb-fr__ledger--won">' +
        '<span class="lb-fr__ledger-tick" aria-hidden="true">✓</span>' +
        '<span><b>' + d.discountPct + '% set discount applied</b> — you’re saving ' + money(d.lineSavingsCents, this.moneyFormat) + '</span></div>';
    } else if (d.itemCount === 1 && d.pendingDiscountCents) {
      out += '<div class="lb-fr__ledger">Add one more piece to unlock <b>' + d.discountPct + '% off</b> — worth ' +
        money(d.pendingDiscountCents, this.moneyFormat) + '+ on what’s already here.</div>';
    }
    if (d.giftUnlocked) {
      out += '<div class="lb-fr__ledger lb-fr__ledger--won">' +
        '<span class="lb-fr__ledger-tick" aria-hidden="true">✓</span>' +
        '<span><b>Free engraved tag unlocked</b></span></div>';
    } else if (d.itemCount) {
      out += '<div class="lb-fr__ledger">' +
        '<span class="lb-fr__ledger-bar"><i style="width:' + d.giftProgressPct + '%"></i></span>' +
        money(d.giftRemainingCents, this.moneyFormat) + ' from a <b>free engraved tag</b></div>';
    }
    return out;
  };

  proto.orderSummary = function (d) {
    var rows;
    if (!d.pieces.length) {
      rows = '<p class="lb-fr__sub" style="font-style:italic">Nothing yet — pieces appear here as you choose them.</p>';
    } else {
      rows = d.pieces.map(function (p) {
        var priceHtml = d.discountUnlocked
          ? '<s class="lb-fr__srow-was">' + money(p.cents, this.moneyFormat) + '</s>' + money(p.discountedCents, this.moneyFormat)
          : money(p.cents, this.moneyFormat);
        return '<div class="lb-fr__srow">' +
          (p.image ? '<span class="lb-fr__photo lb-fr__photo--contain lb-fr__srow-thumb" style="background-image:url(' + p.image + ')"></span>' : '<span class="lb-fr__srow-thumb lb-fr__skel"></span>') +
          '<span class="lb-fr__srow-copy"><span class="lb-fr__srow-name">' + p.label + '</span><span class="lb-fr__srow-meta">' + esc(p.meta) + '</span></span>' +
          '<span class="lb-fr__srow-price">' + priceHtml + '</span>' +
          '<span class="lb-fr__srow-actions">' +
          '<button class="lb-fr__srow-edit" data-action="editItem" data-uid="' + p.uid + '">Edit</button>' +
          '<button class="lb-fr__srow-x" data-action="removeItem" data-uid="' + p.uid + '" aria-label="Remove ' + p.label + '">×</button>' +
          '</span></div>';
      }, this).join('');
      if (d.discountUnlocked && d.lineSavingsCents) {
        rows += '<div class="lb-fr__sl"><span class="lb-fr__ok">' + d.discountPct + '% set discount</span><span class="lb-fr__ok">−' + money(d.lineSavingsCents, this.moneyFormat) + '</span></div>';
      }
    }
    return '<div class="lb-fr__side-block"><h2 class="lb-fr__h2">Your Set' +
      (d.itemCount ? ' <span class="lb-fr__count">' + d.itemCount + '</span>' : '') + '</h2>' + rows +
      '<div class="lb-fr__sl lb-fr__sl--noborder" style="margin-top:4px"><b>Subtotal</b>' +
      (d.discountUnlocked
        ? '<b><s class="lb-fr__srow-was">' + money(d.subtotalCents, this.moneyFormat) + '</s> ' + money(d.discountedSubtotalCents, this.moneyFormat) + '</b>'
        : '<b>' + money(d.subtotalCents, this.moneyFormat) + '</b>') +
      '</div>' +
      this._incentiveLedger(d) +
      '</div>';
  };

  // The desktop sidebar is display:none on mobile — this bar is the
  // summary's only mobile home. The item-count row doubles as a toggle
  // that expands the full Bespoke/Fitted Selection panel upward as a
  // drawer (state.mobileSummaryExpanded), capped at 42vh with its own
  // scroll so it never swallows the CTA row beneath it.
  proto.mobileStickyBar = function (d, cta) {
    var expanded = this.state.mobileSummaryExpanded;
    // Reward status, on the bar, in money — the mobile shopper never sees
    // the desktop sidebar ledger, and the HUD at the top of the page has
    // long since scrolled away by the time they're choosing.
    var reward = '';
    if (d.discountUnlocked && d.lineSavingsCents) {
      reward = '<span class="lb-fr__mbar-reward lb-fr__mbar-reward--won">✓ ' + d.discountPct + '% off · saving ' + money(d.lineSavingsCents, this.moneyFormat) + '</span>';
    } else if (d.itemCount === 1 && d.pendingDiscountCents) {
      reward = '<span class="lb-fr__mbar-reward">+1 piece → ' + d.discountPct + '% off</span>';
    } else if (d.itemCount && !d.giftUnlocked) {
      reward = '<span class="lb-fr__mbar-reward">' + money(d.giftRemainingCents, this.moneyFormat) + ' → free tag</span>';
    }
    // data-step / data-empty let the stylesheet reclaim the 45px summary
    // row on step 1 while the set is still empty — there it reads
    // "0 items · Rp 0,00", which is pure noise on the one screen where
    // nothing has been added yet, and it is the only way the bar fits the
    // ~85px budget. The row returns the moment there IS a set to summarise
    // (including a shopper who walked back here from Review), so nothing
    // is ever hidden when it carries information.
    return '<div class="lb-fr__mbar" data-step="' + esc(this.state.step) + '" data-empty="' + (!d.itemCount) + '">' +
      (expanded ? '<div class="lb-fr__mbar-summary">' + this.orderSummary(d) + '</div>' : '') +
      '<button class="lb-fr__mbar-toggle" data-action="toggleMobileSummary" aria-expanded="' + !!expanded + '"' +
      ' aria-label="' + d.itemCount + ' item' + (d.itemCount === 1 ? '' : 's') + ' in your set — show details">' +
      '<span class="lb-fr__mbar-sum">' + d.itemCount + ' item' + (d.itemCount === 1 ? '' : 's') + ' · ' +
      (d.discountUnlocked ? '<s class="lb-fr__srow-was">' + money(d.subtotalCents, this.moneyFormat) + '</s> <b>' + money(d.discountedSubtotalCents, this.moneyFormat) + '</b>' : '<b>' + money(d.subtotalCents, this.moneyFormat) + '</b>') + '</span>' +
      reward +
      // Same affordance as the free-gift progress bar's mobile toggle:
      // "+" collapsed, "-" expanded. No rotating glyph.
      '<span class="lb-fr__mbar-toggle-icon" aria-hidden="true">' + (expanded ? '-' : '+') + '</span></button>' +
      // ONE thin line, never a block. The phone bar's entire height budget
      // is ~85px, so the basis line gets a single ~13px row — nowrap with
      // an ellipsis in CSS — and its own SHORT string. It must not reuse
      // the desktop sentence: at ~52 characters that truncates to
      // "…our starting sizes for Akit…", dropping the two words the line
      // exists to say. ctaMetaShort is written to fit 320px intact.
      (cta.ctaMeta ? '<div class="lb-fr__mbar-meta' + (cta.ctaMetaTone === 'neutral' ? ' lb-fr__mbar-meta--neutral' : '') + '">' + (cta.ctaMetaShort || cta.ctaMeta) + '</div>' : '') +
      '<div class="lb-fr__mbar-actions">' +
      (cta.back ? '<button class="lb-fr__back" data-action="goBack" aria-label="Back">←</button>' : '') +
      ctaSecondaryBtn(cta, true) +
      // The SHORT label. A 40-character uppercase desktop CTA at 0.16em
      // tracking wrapped to three lines inside a 375px bar and pushed the
      // button below the fold.
      '<button class="lb-fr__btn' + (cta.ctaGhost ? ' lb-fr__btn--ghost' : '') + '" style="flex:1" data-action="' + cta.ctaAction + '"' + ctaDataAttrs(cta) + (cta.ctaEnabled ? '' : ' disabled') + '>' +
      (cta.ctaShort || cta.ctaLabel).replace(/&amp;/g, '&amp;') + '</button></div>' +
      '<div class="lb-fr__mtrust">Free worldwide shipping · Free engraving · Made to order</div>' +
      '</div>';
  };

  proto.footerTrust = function () {
    return '<div class="lb-fr__foot"><button class="lb-fr__back" data-action="goBack">← Back</button>' +
      '<span class="lb-fr__trust">Complimentary Worldwide Shipping · Free stamping &amp; engraving · Handcrafted to order</span></div>';
  };

  // Common wrapper for every step-1..N screen: rail + hud + two-column grid
  // (main card + side column with Set Preview / Live Summary) + footer.
  //
  // .lb-fr__stage bounds this in a fixed-height "app window" on desktop
  // (see the CSS): .lb-fr__stage-chrome (top bar + rail + hud) and
  // .lb-fr__foot are both flex-shrink:0, permanently pinned in place, and
  // only .lb-fr__stage-scroll (the grid) ever scrolls — so Back/Continue/
  // the HUD read as fixed native-app chrome, not page furniture, and
  // switching steps never jumps the shell around. On mobile .lb-fr__stage
  // has no special styling (plain block flow) and .lb-fr__foot is
  // display:none, replaced by the existing sticky .lb-fr__mbar below.
  proto._stepShell = function (d, stepKey, mainHtml, cta) {
    // Editing from Review: the commit CTA reads as a save action, and the
    // commit handlers return straight to Review rather than continuing
    // the chain (see editingFrom in the state).
    if (this.state.editingFrom && cta.ctaEnabled && (cta.ctaAction.indexOf('commit') === 0 || cta.ctaAction === 'goCollarLeashHarness')) {
      cta = Object.assign({}, cta, { ctaLabel: 'Save &amp; return to review', ctaShort: 'Save →' });
    } else if (this.state.editingFrom && cta.ctaAction === 'continueStep') {
      cta = Object.assign({}, cta, { ctaLabel: 'Back to review', ctaShort: 'Back to review' });
    } else if (this.state.editingFrom && cta.ctaAction === 'skipCurrentStep') {
      // Was still reading "Continue without a collar" while the shopper
      // was editing an existing set from Review — describing a forward
      // walk through a chain they had already finished.
      cta = Object.assign({}, cta, { ctaLabel: 'Back to review', ctaShort: 'Back to review', ctaGhost: true });
    }
    return '' +
      // data-step is mirrored onto the stage (not just the grid) so the
      // chrome bands can adopt the profile step's centred 580px measure —
      // see "ONE FRAME FOR EVERY BAND" at the end of the stylesheet.
      '<div class="lb-fr__stage" data-step="' + stepKey + '">' +
      '<div class="lb-fr__stage-chrome">' +
      // The wordmark is gone: this component now renders INSIDE the themed
      // page, directly under the store's own header, which already shows
      // "The Lille Björn" in the brand's real logotype. Repeating it in a
      // second, smaller typeface read as a duplicate rather than as app
      // The step label lives IN this bar now — see railHead(). Left: where
      // you are. Right: how to abandon. One row instead of two.
      '<div class="lb-fr__top">' + this.railHead(d) +
      '<div class="lb-fr__top-utils">' + this._startOverBtn() + '</div></div>' +
      this.progressRail(d) +
      // The reward strip lives INSIDE the sticky chrome again. An earlier
      // pass moved it out so the chrome could slim down, but that split
      // wayfinding from reward: the shopper scrolled past "€127 to the
      // free tag" and then had no idea how close they were while actually
      // choosing. It is back in the pinned bar and, on mobile, collapsed
      // to a 3px track plus one line — see .lb-fr__hud in the CSS. The
      // height it costs is paid for by the rail compression alongside it.
      this.incentiveHud(d) +
      '</div>' +
      '<div class="lb-fr__stage-scroll">' +
      '<div class="lb-fr__grid lb-fr__screen" data-step="' + stepKey + '">' +
      '<div>' + mainHtml + '</div>' +
      '<div class="lb-fr__side">' + this.orderSummary(d) + '</div>' +
      '</div>' +
      '</div>' +
      // data-cta-rich marks a foot carrying a meta line and/or a secondary
      // control, so the stylesheet can switch step 1 to its compact
      // two-line layout instead of letting three items fight for a 516px
      // row and wrap the bar to ~159px. Attribute rather than :has() so the
      // layout is deterministic on every browser we support.
      '<div class="lb-fr__foot"' + ((cta.ctaMeta || cta.ctaSecondary) ? ' data-cta-rich="true"' : '') + '>' +
      (cta.back ? '<button class="lb-fr__back" data-action="goBack">← Back</button>' : '<span></span>') +
      '<span class="lb-fr__trust">Complimentary Worldwide Shipping · Free stamping &amp; engraving · Handcrafted to order</span>' +
      '<span class="lb-fr__foot-cta">' +
      (cta.ctaMeta ? '<span class="lb-fr__cta-meta' + (cta.ctaMetaTone === 'neutral' ? ' lb-fr__cta-meta--neutral' : '') + '">' + cta.ctaMeta + '</span>' : '') +
      ctaSecondaryBtn(cta, false) +
      '<button class="lb-fr__btn' + (cta.ctaGhost ? ' lb-fr__btn--ghost' : '') + '" data-action="' + cta.ctaAction + '"' + ctaDataAttrs(cta) + (cta.ctaEnabled ? '' : ' disabled') + '>' + cta.ctaLabel + '</button>' +
      '</span></div>' +
      '</div>' +
      this.mobileStickyBar(d, cta);
  };

  /* ============================================================
     Dispatch
     ============================================================ */
  proto.template = function () {
    var d = this.deriveVals();
    var s = this.state;
    var body;
    switch (s.step) {
      case 'entry': body = this.screenEntry(d); break;
      case 'profile': body = this.screenProfile(d); break;
      case 'collar': body = this.screenCollar(d); break;
      case 'leash': body = this.screenLeash(d); break;
      case 'harness': body = this.screenHarness(d); break;
      case 'tag': body = this.screenTag(d); break;
      case 'review': body = this.screenReview(d); break;
      case 'added': body = this.screenAdded(d); break;
      default: body = this.screenEntry(d);
    }
    // A polite live region so screen-reader users learn that a tap on
    // "Add to set" actually did something — previously the only evidence
    // an item had been committed was a visual border change and a count
    // buried in a card, announced to nobody.
    // The toast is a FLOATING layer, never a flow element. Injecting it
    // above the step content meant every "Add to set" pushed the entire
    // page down by ~40px at the exact moment the shopper's finger was
    // still travelling toward the next control — the single most violent
    // layout shift in the builder, and self-inflicted by the confirmation
    // that was supposed to reassure them.
    return '<div class="lb-fr__shell" style="position:relative">' + body + this._sizeModal(d) +
      (s.toast ? '<div class="lb-fr__toastwrap"><div class="lb-fr__toast">' + esc(s.toast) + '</div></div>' : '') +
      '<div class="lb-fr__sr" role="status" aria-live="polite">' + esc(s.announce || '') + '</div>' +
      '</div>';
  };

  /* ============================================================
     Targeted DOM patching — micro state changes (swatch selection,
     size/width picks, toggles, engraving keystrokes) patch only the
     nodes whose markup actually changed instead of tearing down and
     rebuilding the whole screen. Selection feels instant: no flicker,
     no scroll jump, no focus loss, no animation replay. Full innerHTML
     replacement is reserved for step transitions, where the screen
     crossfade SHOULD replay.
     ============================================================ */
  function syncAttributes(fromEl, toEl) {
    var i, attr;
    for (i = fromEl.attributes.length - 1; i >= 0; i--) {
      attr = fromEl.attributes[i];
      if (!toEl.hasAttribute(attr.name)) fromEl.removeAttribute(attr.name);
    }
    for (i = 0; i < toEl.attributes.length; i++) {
      attr = toEl.attributes[i];
      if (fromEl.getAttribute(attr.name) !== attr.value) fromEl.setAttribute(attr.name, attr.value);
    }
  }

  function morphNode(fromNode, toNode, activeEl, parent) {
    if (fromNode.nodeType !== toNode.nodeType ||
        (fromNode.nodeType === 1 && fromNode.nodeName !== toNode.nodeName)) {
      parent.replaceChild(toNode.cloneNode(true), fromNode);
      return;
    }
    if (fromNode.nodeType === 3 || fromNode.nodeType === 8) {
      if (fromNode.nodeValue !== toNode.nodeValue) fromNode.nodeValue = toNode.nodeValue;
      return;
    }
    if (fromNode.nodeType !== 1) return;
    syncAttributes(fromNode, toNode);
    var tag = fromNode.nodeName;
    if (tag === "INPUT") {
      if (fromNode.type === "checkbox" || fromNode.type === "radio") {
        fromNode.checked = toNode.hasAttribute("checked");
      } else if (fromNode !== activeEl && fromNode.value !== toNode.value) {
        fromNode.value = toNode.value;
      }
    }
    morphChildren(fromNode, toNode, activeEl);
    // Selects sync after children so the fresh <option> set is in place.
    if ((tag === "SELECT" || tag === "TEXTAREA") && fromNode !== activeEl && fromNode.value !== toNode.value) {
      fromNode.value = toNode.value;
    }
  }

  function morphChildren(fromEl, toEl, activeEl) {
    var toNodes = toEl.childNodes;
    while (fromEl.childNodes.length > toNodes.length) fromEl.removeChild(fromEl.lastChild);
    for (var i = 0; i < toNodes.length; i++) {
      var fromNode = fromEl.childNodes[i];
      if (!fromNode) { fromEl.appendChild(toNodes[i].cloneNode(true)); continue; }
      morphNode(fromNode, toNodes[i], activeEl, fromEl);
    }
  }

  /* The fixed bottom bar's height is not a constant — it grows with the
     reward line, the CTA caption, and the expanded summary drawer. The
     scroll region has to reserve exactly that much or content ends up
     unreachable behind it, so the real measurement is published as a CSS
     custom property and everything that must clear the bar reads it. */
  proto._measureChrome = function () {
    var bar = this.querySelector(".lb-fr__mbar");
    if (!bar) return;
    var h = Math.round(bar.getBoundingClientRect().height);
    if (h && h !== this._lastBarH) {
      this._lastBarH = h;
      this.style.setProperty("--lbfr-mbar-h", h + "px");
    }
  };
  proto._watchChrome = function () {
    var self = this;
    if (this._chromeObserver || typeof ResizeObserver === "undefined") return;
    this._chromeObserver = new ResizeObserver(function () { self._measureChrome(); });
    var bar = this.querySelector(".lb-fr__mbar");
    if (bar) this._chromeObserver.observe(bar);
  };
  /* Drop the fade mask once the assistant's body is fully scrolled, so
     the final chart row isn't left permanently dimmed. */
  proto._watchAssistScroll = function () {
    var body = this.querySelector(".lb-fr__assist-body");
    if (!body || body._lbfrWired) return;
    body._lbfrWired = true;
    var sync = function () {
      var atEnd = body.scrollTop + body.clientHeight >= body.scrollHeight - 2;
      body.setAttribute("data-at-end", atEnd ? "true" : "false");
    };
    body.addEventListener("scroll", sync, { passive: true });
    sync();
  };

  proto.render = function () {
    this._isRerendering = true;
    var html = this.template();
    var stepChanged = this._renderedStep !== this.state.step;
    if (stepChanged || !this.firstElementChild) {
      this.innerHTML = html;
    } else {
      var fresh = document.createElement("div");
      fresh.innerHTML = html;
      morphChildren(this, fresh, document.activeElement);
    }
    this._renderedStep = this.state.step;
    this._isRerendering = false;
    this._persist();
    // Body-scroll lock while the size assistant is open. On mobile it's a
    // bottom sheet: without this, a flick anywhere outside the sheet
    // scrolls the page behind it, so the sheet slides over unrelated
    // content and the shopper loses the step they were configuring.
    var lock = !!this.state.measureOpen;
    if (lock !== this._scrollLocked) {
      this._scrollLocked = lock;
      document.documentElement.style.overflow = lock ? "hidden" : "";
      if (lock) {
        var modal = this.querySelector(".lb-fr__modal");
        var focusTarget = modal && modal.querySelector('[data-input="measureValue"]');
        if (focusTarget) { try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); } }
      }
    }
    // Re-measure after every render: the bar's height changes with the
    // reward line, the CTA caption and the expanded drawer.
    this._measureChrome();
    this._watchChrome();
    if (this.state.measureOpen) this._watchAssistScroll();
    this._watchCarousel();
    var activeFocusId = this._activeFocusId;
    if (activeFocusId) {
      var el = this.querySelector('[data-input="' + activeFocusId + '"]');
      if (el && el !== document.activeElement) {
        el.focus();
        if (el.setSelectionRange && el.type !== 'number') {
          try { el.setSelectionRange(el.value.length, el.value.length); } catch (e) {}
        }
      }
    }
  };

  /* ============================================================
     Step transitions
     ============================================================ */
  proto._goto = function (step) {
    if (step !== this.state.step && !this._suppressHistory) {
      try {
        history.pushState({ lbfrStep: step }, "");
        this._historyDepth = (this._historyDepth || 0) + 1;
      } catch (e) { /* history unavailable (sandboxed iframe) — degrade quietly */ }
    }
    window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY - 20, behavior: 'smooth' });
    // Transient chrome never survives a step change.
    this.setState({ step: step, toast: null, confirmReset: false, mobileSummaryExpanded: false });
  };

  proto._nextAfter = function (stepKey) {
    var d = this.deriveVals();
    var idx = d.pathSteps.indexOf(stepKey);
    var next = d.pathSteps[idx + 1];
    this._goto(next || 'review');
  };

  proto._prevBefore = function (stepKey) {
    var d = this.deriveVals();
    var idx = d.pathSteps.indexOf(stepKey);
    if (idx <= 0) { this._goto('entry'); return; }
    this._goto(d.pathSteps[idx - 1]);
  };

  /* ============================================================
     Cart-building actions
     ============================================================ */
  // ---- Mix & Match adds: each helper appends ONE configured item to its
  // category array (multiple collars/leashes/harnesses per set). The
  // _commit* wrappers are the footer's add-first-item-and-advance path.
  /* Append-or-replace, plus the feedback the original build never gave.
     Committing an item used to be silent: state changed, a border moved,
     and that was the whole confirmation. And "editing" an item physically
     REMOVED it from the set first — so a shopper who tapped Edit and then
     navigated away, hit Back, or simply changed their mind lost the item
     with no warning and no undo. Items now stay in the set throughout the
     edit and are replaced in place. */
  proto._putItem = function (listKey, item, label) {
    var s = this.state;
    var list = s[listKey];
    var uid = s.editingUid;
    var idx = -1;
    if (uid) {
      for (var i = 0; i < list.length; i++) { if (list[i].uid === uid) { idx = i; break; } }
    }
    var next, verb;
    if (idx !== -1) {
      item.uid = uid;
      next = list.slice();
      next[idx] = item;
      verb = "updated";
    } else {
      item.uid = nextUid();
      next = list.concat([item]);
      verb = "added to your set";
    }
    var before = this.deriveVals();
    var patch = { editingUid: null, announce: label + " " + verb + "." };
    patch[listKey] = next;
    this.setState(patch);
    var after = this.deriveVals();
    // One-shot celebration, only on the transition that actually earns it.
    if (!before.discountUnlocked && after.discountUnlocked) {
      this.setState({ toast: after.discountPct + "% set discount unlocked — you’re saving " + money(after.lineSavingsCents, this.moneyFormat) + "." });
      this._pushEvent("fitting_room_10pct_unlocked", {});
    } else if (!before.giftUnlocked && after.giftUnlocked) {
      this.setState({ toast: "Free engraved nametag unlocked." });
      this._pushEvent("fitting_room_gift_unlocked", {});
    } else {
      this.setState({ toast: label + " " + verb + "." });
    }
    this._scheduleToastClear();
    return true;
  };
  /* Last line of defence against a silent dead button.

     Every _add*Item path can decline for a legitimate reason (no size
     resolved yet — it opens the assistant), but a decline for any OTHER
     reason used to be indistinguishable from a broken control: the
     shopper tapped "Add to set" and the interface did not react at all.
     If nothing was committed and nothing else took over the screen, say
     so out loud instead of leaving them tapping. */
  /* THREE outcomes, not two.
       true        — the item was committed
       "handled"   — declined for a reason the helper has ALREADY shown
                     (no size picked yet, stamping on with nothing to
                     stamp, a bespoke neck)
       false       — declined because the variant genuinely won't resolve

     Only the last one earns the generic message. It used to be two
     states, so tapping "Add to set" before choosing a size fired
     "That combination isn't available right now — try another colour or
     width" — three untrue claims at once: the combination is fine, the
     colour is fine, and the width is fine. It also sent the shopper
     hunting through swatches for a problem that was one unpicked size
     pill, and it did so in the loudest component on the screen while the
     real instruction sat underneath the pills in grey. */
  proto._addFromCard = function (result) {
    if (result === true || result === "handled" || this.state.measureOpen) return;
    this.setState({
      toast: "That combination isn’t available right now — try another colour or width.",
      announce: "That combination isn’t available right now."
    });
    this._scheduleToastClear();
  };

  /* Bring the size row the shopper needs into view and let the highlight
     do the explaining. Replaces a modal that covered the whole screen to
     ask for something the card could ask for itself. */
  proto._scrollSizeIntoView = function (key) {
    var self = this;
    requestAnimationFrame(function () {
      var sel = key === "harness"
        ? '.lb-fr__scard .lb-fr__pillbar'
        : '.lb-fr__scard[data-family="' + key + '"] .lb-fr__pillbar--card';
      var row = self.querySelector(sel);
      if (row && row.scrollIntoView) row.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    clearTimeout(this._promptTimer);
    this._promptTimer = setTimeout(function () {
      if (!self.isConnected) return;
      if (self.state.sizePromptFor) self.setState({ sizePromptFor: null });
    }, 4000);
  };

  proto._scheduleToastClear = function () {
    var self = this;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(function () {
      if (!self.isConnected) return;
      if (self.state.toast) self.setState({ toast: null });
    }, 3400);
  };

  proto._addCollarItem = function (familyKey) {
    var d = this.deriveVals();
    // This card's effective size: per-card override, else Step-1 size.
    var itemSize = this.state.collarSizes[familyKey] || d.collarSize;
    // A neck past the standard matrix has no stock size to commit — the
    // card's CTA is a bespoke mailto in that state, so this is defence in
    // depth for the keyboard and Quick Assemble paths. Without it the
    // handler fell through to the sizePromptFor branch below and asked the
    // shopper to "choose a size", directly contradicting the bespoke
    // banner sitting above the grid.
    if (d.neckBespoke) return "handled";
    if (!itemSize) {
      // Nothing to add yet, but hijacking the screen with a modal is the
      // wrong correction: the control the shopper needs is already on the
      // card, six pixels above the button they just pressed. Point at it
      // instead — the pill row highlights and the card says what's needed.
      // The inline "Choose a size to add this collar" hint under the pills
      // was the only feedback here, and at 11px grey it lost every time to
      // the black toast that (wrongly) fired alongside it. The toast now
      // carries the correct instruction — same visibility, true statement
      // — while the pill row still highlights and scrolls into view.
      this.setState({
        sizePromptFor: familyKey,
        collarFamily: familyKey,
        toast: "Choose a neck size for this collar.",
        announce: "Choose a collar size first."
      });
      this._scrollSizeIntoView(familyKey);
      this._scheduleToastClear();
      return "handled";
    }
    var colorKey = this.state.collarColors[familyKey] || "black";
    var product = this.collarProductFor(familyKey, colorKey);
    if (!product) return false;
    // Same resolver the card rendered from, called with the same arguments
    // — so "Add to set" can only ever commit the width the shopper is
    // looking at. (The card passes collarSizeEst; itemSize is that same
    // letter unless this card carries its own size override, in which case
    // the override is the correct basis for its own width.)
    var width = this.collarWidthOptions(familyKey, colorKey, d.breed.category, itemSize).width;
    var variant = resolveVariant(product, { size: itemSize, width: width });
    if (!variant) return false;
    // Physically impossible combinations can never be committed: the
    // per-family flag is only ever set on a stampable line, and is
    // re-checked here so no restored snapshot or stale state can slip an
    // Aeris stamp into a cart line.
    var stampable = familyIsStampable(familyKey);
    var wantsStamp = !!this.state.collarStampFor[familyKey] && stampable;
    var stampText = (this.state.collarStampText || "").trim();
    if (wantsStamp && !stampText) {
      // Stamping on with nothing to stamp — ask, don't silently ship a
      // blank collar the shopper believed was personalised.
      this.setState({
        stampError: familyKey, collarFamily: familyKey,
        toast: "Add the name to stamp, or switch stamping off.",
        announce: "Add the name to stamp, or turn stamping off."
      });
      this._scheduleToastClear();
      return "handled";
    }
    this._putItem("collars", {
      familyKey: familyKey, width: width, colorKey: colorKey, size: itemSize,
      variantId: variant.id, cents: variant.price,
      stampEnabled: wantsStamp, stampText: wantsStamp ? stampText : "",
      // productPhoto, not imageFor(product, variant) — the card renders the
      // product-level photo, so the committed item must too.
      image: productPhoto(product)
    }, FAMILY_LABELS[familyKey] + " Collar · " + leatherDisplayName(colorKey) + " · " + itemSize);
    this._pushEvent("fitting_room_item_added", { type: "collar", family: familyKey, color: colorKey });
    return true;
  };
  proto._addLeashItem = function (style) {
    var d = this.deriveVals();
    var lastCollarColor = this.state.collars.length ? this.state.collars[this.state.collars.length - 1].colorKey : "black";
    var colorKey = this.state.leashColors[style] || lastCollarColor;
    var width = this.resolveLeashWidth(style, colorKey, d.leashWidthAuto);
    var product = this.leashProductFor(style, colorKey);
    var variant = product ? resolveVariant(product, { width: width }) : null;
    if (!variant) return false;
    this._putItem("leashes", {
      style: style, width: width, colorKey: colorKey,
      variantId: variant.id, cents: variant.price,
      // Matches the leash card, which renders imageFor(product, null).
      image: productPhoto(product)
    }, (FL.LEASH_STYLE_DISPLAY_NAME[style] || style) + " Leash · " + leatherDisplayName(colorKey));
    this._pushEvent("fitting_room_item_added", { type: "leash", style: style, color: colorKey });
    return true;
  };
  proto._addHarnessItem = function () {
    var d = this.deriveVals();
    if (!d.harnessSize) {
      this.setState({
        sizePromptFor: "harness",
        toast: "Choose a chest size for this harness.",
        announce: "Choose a chest size first."
      });
      this._scrollSizeIntoView("harness");
      this._scheduleToastClear();
      return "handled";
    }
    if (!d.harnessVariant) return false;
    this._putItem("harnesses", {
      colorKey: d.harnessColor, size: d.harnessSize,
      variantId: d.harnessVariant.id, cents: d.harnessVariant.price,
      image: imageFor(d.harnessProduct, d.harnessVariant)
    }, "Vero Harness · " + leatherDisplayName(d.harnessColor) + " · " + d.harnessSize);
    this._pushEvent("fitting_room_item_added", { type: "harness", color: d.harnessColor });
    return true;
  };
  /* Quick Assemble: build the recommended set in one action and land on
     Review. Only fills categories the shopper hasn't already chosen for
     themselves, so pressing it mid-flow tops up rather than duplicating. */
  proto._quickAssemble = function () {
    var d = this.deriveVals();
    var added = [];
    if (!this.state.collars.length && d.collarSize) {
      if (this._addCollarItem(d.recommendedFamily)) added.push("collar");
    }
    if (!this.state.leashes.length) {
      if (this._addLeashItem(FL.LEASH_STYLE_DEFAULT)) added.push("leash");
    }
    if (!this.state.harnesses.length && d.harnessSize) {
      if (this._addHarnessItem()) added.push("harness");
    }
    this._pushEvent("fitting_room_quick_assemble", { added: added.join(",") });
    this._goto("review");
    var after = this.deriveVals();
    this.setState({
      toast: added.length
        ? "Recommended set assembled — " + added.length + " piece" + (added.length === 1 ? "" : "s") +
          " added. Edit or remove anything below."
        : "Everything’s already in your set.",
      announce: added.length + " items added to your set."
    });
    this._scheduleToastClear();
    return after;
  };

  proto._commitTag = function () {
    // Validation: engraving left empty without the blank-tag checkbox —
    // highlight the fields and block Continue.
    if (!this.state.tagNoEngraving && !this.state.tagEngraveName.trim() && !this.state.tagEngravePhone.trim()) {
      this.setState({ tagError: true });
      return;
    }
    var d = this.deriveVals();
    var isGift = d.giftUnlocked && d.tagGiftAvailable;
    var variant = isGift ? d.tagGiftVariant : d.tagPaidVariant;
    // Defence in depth for the keyboard/restored-snapshot paths: never
    // commit a tag whose variant doesn't resolve. Without this the item
    // lands in the set priced at 0 (see tagValueCents' `|| 0` fallback),
    // shows as free on Review, and is then dropped from the cart payload
    // for having no id.
    if (!variant) {
      this.setState({
        toast: "That nametag isn’t available right now — try the other design.",
        announce: "This nametag isn’t available."
      });
      this._scheduleToastClear();
      return;
    }
    this.setState({
      tag: {
        design: this.state.tagDesign, size: this.state.tagSize,
        engraveName: this.state.tagNoEngraving ? "" : this.state.tagEngraveName,
        engravePhone: this.state.tagNoEngraving ? "" : this.state.tagEngravePhone,
        noEngraving: this.state.tagNoEngraving,
        isGift: isGift, variantId: variant && variant.id, cents: isGift ? 0 : (variant ? variant.price : 0),
        image: imageFor(isGift ? d.tagGiftProduct : d.tagPaidProduct, variant)
      }
    });
    this._pushEvent('fitting_room_step_completed', { step: 'tag', path: this.state.path });
    this.state.editingFrom = false;
    this._goto('review');
  };

  /* ============================================================
     Add to cart — pattern per lillebjorn-fit-finder.js's addToCart:
     full {id, quantity, properties} items array, inline drawer-section
     refresh, header badge refresh, dataLayer + Shopify.analytics events.
     ============================================================ */
  function resolveCartDrawerSectionId() {
    var liveDrawer = document.querySelector(".cart-drawer__wrapper");
    if (!liveDrawer) return null;
    var sectionHost = liveDrawer.closest('[id^="shopify-section-"]');
    return sectionHost ? sectionHost.id.replace(/^shopify-section-/, "") : null;
  }

  proto.refreshHeaderCartCount = function (cart) {
    document.querySelectorAll(".header__cart-count").forEach(function (badge) {
      var numberEl = badge.querySelector(".cart-count-number");
      if (numberEl) numberEl.textContent = cart.item_count;
      var isEmpty = cart.item_count === 0;
      badge.classList.toggle("opacity-0", isEmpty);
      badge.classList.toggle("hidden-xs", isEmpty);
      badge.className = badge.className.replace(/header-cart-count-\d+/, "header-cart-count-" + cart.item_count);
    });
  };

  proto.refreshCartDrawerMarkup = function (preFetchedHtml) {
    var liveDrawer = document.querySelector(".cart-drawer__wrapper");
    if (!liveDrawer) return Promise.resolve();
    function applyFreshDrawer(html) {
      var freshDoc = new DOMParser().parseFromString(html, "text/html");
      var freshDrawer = freshDoc.querySelector(".cart-drawer__wrapper");
      if (!freshDrawer) return false;
      liveDrawer.innerHTML = freshDrawer.innerHTML;
      return true;
    }
    if (preFetchedHtml && applyFreshDrawer(preFetchedHtml)) return Promise.resolve();
    var routes = window.routes || {};
    var sectionId = resolveCartDrawerSectionId();
    if (!sectionId) return Promise.resolve();
    return fetch((routes.cart_url || "/cart") + "?sections=" + sectionId, { headers: { Accept: "application/json" } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (sections) {
        var sectionHtml = sections && sections[sectionId];
        if (sectionHtml) applyFreshDrawer(sectionHtml);
      })
      .catch(function () {});
  };

  proto.addToCart = function (items) {
    var self = this;
    var dropped = this._lastDroppedLines || 0;
    if (!items.length) {
      this.setState({
        cartStatus: "error",
        cartError: dropped
          ? "The pieces in your set are no longer available in the sizes or colours you chose. Please reconfigure them."
          : "Your set is empty — add a piece before checking out."
      });
      return;
    }
    if (dropped) {
      // Some lines resolved, some didn't. Say so BEFORE navigating, rather
      // than dropping them quietly and letting the cart total silently
      // disagree with the Review screen the shopper just approved.
      console.warn("lillebjorn-fitting-room: " + dropped + " cart line(s) had no resolvable variant and were omitted.");
    }
    this.setState({ cartStatus: "adding", cartError: null });
    // Request generation. Without it a slow FIRST attempt that ultimately
    // fails can land after the shopper has already pressed "Try again",
    // overwriting the newer attempt's state with a stale error — or worse,
    // clearing an error the newer request legitimately produced. Every
    // handler below checks it still owns the latest request before it
    // touches state or navigates.
    var gen = (this._cartGen = (this._cartGen || 0) + 1);
    var isStale = function () { return self._cartGen !== gen; };

    var routes = window.routes || {};
    var cartRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    var cartAddUrl = routes.cart_add_url || (cartRoot + "cart/add.js");
    var drawerSectionId = resolveCartDrawerSectionId();
    var payload = { items: items };
    if (drawerSectionId) {
      payload.sections = [drawerSectionId];
      payload.sections_url = window.location.pathname;
    }

    fetch(cartAddUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function (response) {
        if (isStale()) return;
        var item = (response && response.items && response.items[0]) || response;
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail: { item: item } }));
        document.dispatchEvent(new CustomEvent("cart:build", { bubbles: true, detail: { item: item } }));
        document.documentElement.dispatchEvent(new CustomEvent("CartUpdate", { bubbles: true, detail: { item: item } }));
        self.dispatchEvent(new CustomEvent("lillebjorn:added-to-cart", { bubbles: true, detail: { item: item } }));

        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === "function") {
          var publishedLines = (response && response.items) || [item];
          publishedLines.forEach(function (line) {
            try { window.Shopify.analytics.publish("product_added_to_cart", { cartLine: line }); }
            catch (pixelErr) { console.warn("lillebjorn-fitting-room: Shopify.analytics.publish failed", pixelErr); }
          });
        }

        var d = self.deriveVals();
        self._pushEvent("fitting_room_add_to_cart", {
          ecommerce: {
            currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined,
            value: d.reviewTotalCents / 100,
            items: (response.items || []).map(function (line) {
              return { item_id: String(line.variant_id != null ? line.variant_id : line.id), item_name: line.product_title || line.title || "", price: (line.price || 0) / 100, quantity: line.quantity || 1 };
            })
          }
        });

        var cartJsUrl = (routes.cart_url || (cartRoot + "cart")) + ".js";
        var cartCountPromise = fetch(cartJsUrl, { headers: { Accept: "application/json" } })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (cart) { if (cart) self.refreshHeaderCartCount(cart); })
          .catch(function () {});
        var inlineSectionHtml = drawerSectionId && response && response.sections && response.sections[drawerSectionId];
        var drawerRefreshPromise = self.refreshCartDrawerMarkup(inlineSectionHtml).catch(function () {});

        return Promise.all([cartCountPromise, drawerRefreshPromise]).then(function () {
          if (isStale()) return;
          self.setState({ cartStatus: "added", step: "added" });
          // Hand off to Shopify's NATIVE CART, not straight to checkout.
          // The set is a considered, configurable purchase — the cart page
          // is where a shopper confirms quantities, sees line-item
          // properties (engraving text, stamp, size/width) rendered by the
          // theme, and applies a discount code. Skipping it sent them to a
          // payment form with no chance to check that "LUNA" was spelled
          // the way they typed it, on items that are made to order and
          // non-returnable.
          //
          // routes.cart_url is preferred over Shopify.routes.root + "cart"
          // because it is already locale- and market-correct on
          // multi-language storefronts (/en-gb/cart, /fr/panier); the root
          // concatenation is the fallback for themes that don't publish
          // window.routes.
          var cartPageUrl = routes.cart_url || (cartRoot + "cart");
          // Flush the pending state snapshot before navigating away, so a
          // shopper who hits Back from the cart finds their set intact.
          clearTimeout(self._persistTimer);
          self._persistNow();
          window.location.href = cartPageUrl;
        });
      })
      .catch(function (err) {
        if (isStale()) return;
        console.error("lillebjorn-fitting-room: add to cart failed", err);
        // Two failures that need different words. Shopify rejecting the
        // request tells us WHY ("...is sold out") and that reason is the
        // most useful thing we can show. A network-level failure gives us
        // a developer string — "Failed to fetch", "NetworkError when
        // attempting to fetch resource" — which means nothing to a
        // shopper and looks like the site is broken. Offline is the one
        // case where the shopper can actually fix it themselves, so it
        // gets its own sentence.
        var shopifyReason = err && err.description;
        var offline = (typeof navigator !== "undefined" && navigator.onLine === false) ||
          (err instanceof TypeError) ||
          /failed to fetch|networkerror|load failed/i.test((err && err.message) || "");
        var msg = shopifyReason
          ? shopifyReason
          : (offline
            ? "You appear to be offline. Your set is saved — reconnect and try again."
            : "We couldn’t reach the cart just now.");
        self.setState({
          cartStatus: "error",
          cartError: msg,
          announce: "Add to cart failed. " + msg
        });
      });
  };

  /* ============================================================
     Event wiring
     ============================================================ */
  /* ============================================================
     Browser history integration.
     Without this, the hardware/browser Back button on a five-step
     configurator does the most destructive thing available: it leaves the
     page entirely, abandoning a half-built set. Every forward step pushes
     a history entry tagged with our marker, so Back walks the shopper one
     step back inside the builder — exactly what the gesture means to
     them — and only leaves the page once they're back at the entry
     screen. Entries carry no URL change, so nothing about the theme's own
     routing, canonical URL, or analytics pageviews is affected.
     ============================================================ */
  proto._wireDocumentListeners = function () {
    if (this._docListenersWired) return;
    var self = this;
    this._onPopState = function (e) {
      var st = e.state;
      if (st && st.lbfrStep) {
        // Never re-push while restoring — that would trap the shopper.
        self._suppressHistory = true;
        self.setState({ step: st.lbfrStep, measureOpen: false, confirmReset: false });
        self._suppressHistory = false;
      } else if (self._historyDepth > 0) {
        self._historyDepth = 0;
        self._suppressHistory = true;
        self.setState({ step: "entry", measureOpen: false, confirmReset: false });
        self._suppressHistory = false;
      }
    };
    window.addEventListener("popstate", this._onPopState);
    // The persist debounce buys back a main-thread write per slider frame,
    // but it must never cost the shopper their set: a tab closed, a link
    // followed, or a mobile app-switch within the debounce window would
    // otherwise discard the last change. pagehide fires in every one of
    // those cases (including iOS Safari's bfcache path, where unload does
    // not), so flush synchronously there.
    this._onPageHide = function () { clearTimeout(self._persistTimer); self._persistNow(); };
    window.addEventListener("pagehide", this._onPageHide);
    document.addEventListener("visibilitychange", this._onPageHide);
    this._historyDepth = 0;
    this._docListenersWired = true;
  };

  proto._wireEvents = function () {
    var self = this;

    this.addEventListener("focusin", function (e) {
      var t = e.target.closest("[data-input]");
      self._activeFocusId = t ? t.dataset.input : null;
      // The breed list opens the moment the field gains focus — before any
      // typing — showing the first alphabetical breeds, matching the Size
      // Calculator's combobox (its input "focus" handler calls open()
      // unconditionally). Guarded on showBreedList so the re-render's own
      // focus restoration (render() refocuses _activeFocusId, which fires
      // focusin again) can't loop setState forever.
      if (t && t.dataset.input === "breed" && !self.state.showBreedList) {
        self.setState({ showBreedList: true });
      }
    });

    // Bug fix: selecting a breed option must not blur the search input
    // first. The click on data-action="pickBreed" only fires on mouseup,
    // AFTER mousedown — and mousedown was blurring the input, which
    // synchronously re-renders with showBreedList:false (see the "breed"
    // branch of the focusout listener below) and tears the very button
    // being clicked out of the DOM before its click ever lands. That's
    // why a breed appeared to do nothing: the list showed, but nothing
    // ever committed. Same root cause and same fix as the Size
    // Calculator's breed combobox (lillebjorn-fit-finder.js's
    // wireBreedCombobox uses "mousedown" for exactly this reason) —
    // adapted here to this component's single delegated-click model by
    // suppressing the default focus-shift on mousedown so the DOM is
    // still intact when "click" runs pickBreed.
    this.addEventListener("mousedown", function (e) {
      if (e.target.closest('[data-action="pickBreed"]')) e.preventDefault();
    });

    // Combobox keyboard support — ArrowUp/Down move the active option,
    // Enter commits it, Escape closes. Mirrors the Size Calculator's
    // wireBreedCombobox semantics on this component's delegated model.
    this.addEventListener("keydown", function (e) {
      // Any role="button" surface (size-chart rows, product cards) is
      // keyboard-activatable — but never hijack keys aimed at real
      // interactive elements nested inside a card.
      if ((e.key === "Enter" || e.key === " ") && e.target.closest &&
          !e.target.closest("button, input, select, textarea, a")) {
        var kbtn = e.target.closest('[role="button"][data-action]');
        if (kbtn) { e.preventDefault(); kbtn.click(); return; }
      }
      if (e.key === "Escape" && self.state.measureOpen) {
        // Escape discards the staged draft, same as × / scrim.
        self.setState({ measureOpen: false, measureDraft: null });
        return;
      }
      var t = e.target;
      if (!t.dataset || t.dataset.input !== "breed") return;
      if (!self.state.showBreedList) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); self.setState({ showBreedList: true, breedActiveIdx: 0 }); }
        return;
      }
      var options = self._breedMatches().concat(["Mixed breed / not sure"]);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        self.setState({ breedActiveIdx: Math.min(self.state.breedActiveIdx + 1, options.length - 1) });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        self.setState({ breedActiveIdx: Math.max(self.state.breedActiveIdx - 1, 0) });
      } else if (e.key === "Enter") {
        e.preventDefault();
        var name = options[self.state.breedActiveIdx] || options[0];
        if (name) self.applyBreedSelection(name);
      } else if (e.key === "Escape") {
        self.setState({ showBreedList: false });
      }
    });

    this.addEventListener("click", function (e) {
      var t = e.target.closest("[data-action]");
      if (!t) return;
      var s = self.state;
      var d = self.deriveVals();
      switch (t.dataset.action) {
        case "choosePath":
          self.state.path = t.dataset.path;
          self._pushEvent("fitting_room_path_chosen", { path: t.dataset.path });
          self._goto("profile");
          break;
        case "expressStart":
          // Quick Assemble, twice rebuilt. It originally swapped the
          // collar step for a layout whose only size control was a
          // <select> whose change handler had been deleted in an earlier
          // refactor — inert. It was then reduced to pre-arming the
          // card-select latches, which this pass has just removed as
          // meaningless. So it now does the thing its name has promised
          // all along: assemble the recommended set outright.
          //
          // Nothing here is a commitment the shopper can't undo — they
          // land on Review with all three pieces itemised, each with Edit
          // and Remove, and the rail still open behind them.
          self.setState({ expressMode: true, path: self.state.path || "full" });
          if (!d.hasProfile) {
            // Nothing to recommend FROM yet — a size basis has to come
            // first, or "recommended" would be a fiction.
            self._goto("profile");
            self.setState({ toast: "Tell us about your dog, then we’ll assemble the recommended set." });
            self._scheduleToastClear();
            break;
          }
          self._quickAssemble();
          break;
        case "setAge":
          // Estimates re-derive from the new age profile automatically;
          // entered measurements are ground truth and stay untouched.
          self.setState({ ageProfile: t.dataset.age });
          break;
        case "pickBreed": self.applyBreedSelection(t.dataset.name); break;
        case "goCollarLeashHarness":
          self._pushEvent("fitting_room_step_completed", { step: "profile", path: s.path });
          // Editing the profile from Review returns to Review like every
          // other edit — and keeps later steps' CTAs reading "Continue",
          // never a stale "Save & return".
          if (s.editingFrom) { s.editingFrom = false; self._goto("review"); }
          else self._nextAfter("profile");
          break;
        case "gotoStep": {
          var stepIdx = d.pathSteps.indexOf(t.dataset.step);
          if (stepIdx === -1) break;
          // Every rail segment is a live jump point at any moment in the
          // flow. Leaving Review flags editingFrom so the revisited
          // step's commit returns straight back to Review.
          if (s.step === "review") s.editingFrom = true;
          self._goto(t.dataset.step);
          break;
        }
        case "openMeasure":
          // The draft (value + snug/relaxed boundary choice) starts from
          // the committed state; the modal edits ONLY the draft until
          // Apply.
          self.setState({
            measureOpen: true, measureKey: t.dataset.key,
            measureDraft: s[t.dataset.key],
            measureDraftBoundary: s[t.dataset.key + "BoundaryChoice"] || null,
            measureDraftSource: s[t.dataset.key + "Source"] || null
          });
          break;
        case "closeMeasure":
          // × / scrim: discard the staged draft, commit nothing.
          self.setState({ measureOpen: false, measureDraft: null, measureDraftBoundary: null, measureDraftSource: null });
          break;
        case "setUnit": self.setState({ unit: t.dataset.unit }); break;
        case "clearMeasure": self.setState({ measureDraft: null, measureDraftBoundary: null, measureDraftSource: null }); break;
        case "manualSize": {
          var mkey = t.dataset.key;
          var mSizes = mkey === "neck" ? FL.COLLAR_SIZES : FL.HARNESS_SIZES;
          var mNodes = mkey === "neck" ? FL.NECK_NODES : FL.CHEST_NODES;
          var mMax = mkey === "neck" ? FL.NECK_CUSTOM_MAX : FL.CHEST_CUSTOM_MAX;
          var mi = mSizes.indexOf(t.dataset.size);
          if (mi === -1) break;
          var mPatch = { sizePromptFor: null };
          var currentLetter = mkey === "neck" ? d.collarSize : d.harnessSize;
          if (s[mkey] !== null && currentLetter === t.dataset.size) {
            // Toggle off: tapping the selected pill clears the selection.
            mPatch[mkey] = null;
            mPatch[mkey + "BoundaryChoice"] = null;
            mPatch[mkey + "Source"] = null;
          } else {
            mPatch[mkey] = (mNodes[mi] + (mNodes[mi + 1] || mMax)) / 2;
            mPatch[mkey + "BoundaryChoice"] = null;
            mPatch[mkey + "Source"] = "pill";
            // Picking a size on the harness step is configuring the
            // harness — arm the primary CTA.
            /* (chest pills configure the harness card in place — there is
                no separate "picked" latch to arm any more) */
          }
          self.setState(mPatch);
          break;
        }
        case "stepMeasure": {
          // −/+ steppers: 1cm per step in cm mode, 0.1in in inches mode;
          // seeded from the breed estimate (or mid-range) when empty.
          var stIsNeck = s.measureKey === "neck";
          var stLo = stIsNeck ? FL.NECK_NODES[0] : FL.CHEST_NODES[0];
          var stHi = stIsNeck ? FL.NECK_CUSTOM_MAX : FL.CHEST_CUSTOM_MAX;
          var stCur = s.measureDraft;
          if (stCur === null || stCur === undefined) {
            var stAges = s.breedName ? FL.applyAgeProfile(self.currentBreedProfile(), s.ageProfile) : null;
            stCur = stAges ? (stIsNeck ? stAges.neckSize : stAges.chestSize) : Math.round((stLo + stHi) / 2);
          }
          var stStep = s.unit === "in" ? inToCm(0.1) : 1;
          var stNext = FL.clamp(stCur + (t.dataset.dir === "inc" ? stStep : -stStep), stLo, stHi);
          self.setState({ measureDraft: round2(stNext), measureDraftBoundary: null, measureDraftSource: "measured" });
          break;
        }
        case "pickBoundary":
          // Between-sizes resolution (calculator semantics: "loose" tape
          // → the smaller/snug-fit letter, "snug" tape → the larger).
          self.setState({ measureDraftBoundary: t.dataset.choice });
          break;
        case "pickSizeRow": {
          var mk = s.measureKey;
          if (!mk) break;
          var rowSizes = mk === "neck" ? FL.COLLAR_SIZES : FL.HARNESS_SIZES;
          var rowNodes = mk === "neck" ? FL.NECK_NODES : FL.CHEST_NODES;
          var rowMax = mk === "neck" ? FL.NECK_CUSTOM_MAX : FL.CHEST_CUSTOM_MAX;
          var rowIdx = rowSizes.indexOf(t.dataset.size);
          if (rowIdx === -1) break;
          // Stage the tapped size (range midpoint, never a tie) as the
          // DRAFT — the main form only changes when the shopper Applies.
          //
          // Recorded as 'pill', not 'measured'. This line used to claim
          // "ANY modal interaction counts as a measured resolution" while
          // applyMeasure four lines below stated the opposite rule ("only
          // slider/typed input earns 'measured'; a row pick stays a
          // standard-size choice") — two comments in direct contradiction,
          // with the code following this one. Tapping a size letter is
          // not a measurement wherever the button happens to live, and
          // the provenance is now carried onto the order as
          // _lb_sizing_basis, where "Measured by customer" against a dog
          // nobody measured is the one value that must never be wrong.
          self.setState({ measureDraft: (rowNodes[rowIdx] + (rowNodes[rowIdx + 1] || rowMax)) / 2, measureDraftBoundary: null, measureDraftSource: "pill" });
          break;
        }
        case "applyMeasure": {
          var ak = s.measureKey;
          var applyPatch = { measureOpen: false, measureDraft: null, measureDraftBoundary: null, measureDraftSource: null };
          if (ak) {
            // APPLY WHAT THE MODAL IS SHOWING.
            //
            // measureDraft stays null until the shopper moves the slider,
            // types, or taps a row — but the modal has been *displaying*
            // the breed estimate the whole time (effectiveCm falls back to
            // it) and the button reads "Apply size XL" off that same
            // number. So opening the assistant and pressing Apply without
            // touching anything committed null: the modal closed, nothing
            // changed, and the step still asked to be measured. The button
            // named a size it then did not apply.
            //
            // Untouched means they accepted the estimate we showed them,
            // which is precisely 'breed-average' — the same basis the
            // step-1 secondary records. It is not 'measured': nobody
            // measured anything.
            var estCm = ak === "neck" ? d.neckCm : d.chestCm;
            var touched = s.measureDraft !== null;
            var applyVal = touched ? s.measureDraft : (estCm != null ? estCm : null);
            applyPatch[ak] = applyVal;
            applyPatch[ak + "BoundaryChoice"] = s.measureDraftBoundary;
            // Provenance travels with the value: only slider/typed input
            // earns 'measured'; a row pick stays a standard-size choice.
            applyPatch[ak + "Source"] = applyVal === null ? null
              : (touched ? (s.measureDraftSource || "measured") : "breed-average");
          }
          self._pushEvent("fitting_room_size_calculated", { key: ak });
          self.setState(applyPatch);
          break;
        }
        case "setCollarCardColor": {
          // A swatch tap immediately selects that product too.
          var cc = Object.assign({}, s.collarColors);
          cc[t.dataset.family] = t.dataset.color;
          self.setState({ collarColors: cc, collarFamily: t.dataset.family });
          break;
        }
        case "setCollarTab": self.setState({ collarTab: t.dataset.tab }); break;
        case "setCollarCardSize": {
          // Never let a stock-size tap silently destroy a real out-of-range
          // measurement. The !d.collarSize branch below writes a range
          // MIDPOINT into s.neck — which, with a 52 cm neck on file, threw
          // away the very number the bespoke request is built from, cleared
          // the banner, and persisted the loss 200 ms later with no undo.
          // 17 adult breeds default into this zone (Rottweiler, Great Dane,
          // Bernese Mountain Dog, Newfoundland …), so it was not a fringe path.
          if (d.neckBespoke && s.neck !== null) {
            self.setState({
              toast: 'A ' + dual(s.neck) + ' neck is beyond our standard sizes — we cut this one by hand. Clear the measurement first if you’d rather pick a stock size.',
              announce: 'This measurement is outside standard sizing.'
            });
            self._scheduleToastClear();
            break;
          }
          if (!d.collarSize) {
            // Step 1 skipped. Tapping a size pill used to throw a full
            // modal over the screen, pre-staged with the size just tapped,
            // and demand a second confirmation to apply it — a takeover to
            // register a choice the shopper had already unambiguously
            // made, on the control they made it with. The tap now simply
            // does what it looks like it does: selects that size. The
            // assistant remains available from "Measure neck" on every
            // card, for anyone who actually wants to measure.
            var gNodes = FL.NECK_NODES, gMax = FL.NECK_CUSTOM_MAX;
            var gi = FL.COLLAR_SIZES.indexOf(t.dataset.size);
            if (gi !== -1) {
              self.setState({
                collarFamily: t.dataset.family,
                neck: (gNodes[gi] + (gNodes[gi + 1] || gMax)) / 2,
                neckBoundaryChoice: null,
                neckSource: "pill",
                sizePromptFor: null,
                announce: "Collar size " + t.dataset.size + " selected."
              });
            }
            break;
          }
          // Inline size pill on a collar card: sets a per-card override
          // (multi-dog sets); re-tapping the override clears it back to
          // the inherited Step-1 size.
          var csm = Object.assign({}, s.collarSizes);
          if (csm[t.dataset.family] === t.dataset.size) delete csm[t.dataset.family];
          else csm[t.dataset.family] = t.dataset.size;
          self.setState({ collarSizes: csm, collarFamily: t.dataset.family });
          break;
        }
        case "setCollarCardWidth": {
          var cwm = Object.assign({}, s.collarWidths);
          cwm[t.dataset.family] = t.dataset.width;
          self.setState({ collarWidths: cwm, collarFamily: t.dataset.family });
          break;
        }
        case "toggleStamp": {
          var stFam = t.dataset.family;
          if (!stFam || !familyIsStampable(stFam)) break;
          var stMap = Object.assign({}, s.collarStampFor);
          if (stMap[stFam]) delete stMap[stFam]; else stMap[stFam] = true;
          var stPatch = { collarStampFor: stMap, stampError: null, collarFamily: stFam };
          // Turning it on with the dog's name already on file fills it in
          // — the shopper told us once in Step 1 and shouldn't retype it.
          if (stMap[stFam] && !s.collarStampText && s.dogName) {
            stPatch.collarStampText = s.dogName.toUpperCase().slice(0, 8);
          }
          self.setState(stPatch);
          break;
        }
        case "useDogName":
          self.setState({ collarStampText: (s.dogName || "").toUpperCase().slice(0, 8), stampError: null });
          break;
        case "addCollarCard": self._addFromCard(self._addCollarItem(t.dataset.family)); break;
        case "setLeashCardColor": {
          var lc = Object.assign({}, s.leashColors);
          lc[t.dataset.style] = t.dataset.color;
          self.setState({ leashColors: lc, leashStyle: t.dataset.style });
          break;
        }
        case "setHarnessColor": self.setState({ harnessColor: t.dataset.color }); break;
        case "setLeashCardWidth": {
          var lwm = Object.assign({}, s.leashWidths);
          lwm[t.dataset.style] = t.dataset.width;
          self.setState({ leashWidths: lwm, leashStyle: t.dataset.style });
          break;
        }
        case "skipCurrentStep": {
          // "Continue without a …" declines the step; it must never
          // delete. The old handler emptied the whole category array —
          // harmless only because the control happened to be unreachable
          // once anything was added, which is exactly the kind of
          // invisible coupling that breaks the first time a CTA condition
          // is edited. Declining now just disarms the pick.
          var curKey = s.step;
          if (s.editingFrom) { s.editingFrom = false; self._goto("review"); }
          else self._nextAfter(curKey);
          break;
        }
        case "addLeashCard": self._addFromCard(self._addLeashItem(t.dataset.style)); break;
        case "addHarnessCard": self._addFromCard(self._addHarnessItem()); break;
        case "continueStep":
          if (s.editingFrom) { s.editingFrom = false; self._goto("review"); }
          else self._nextAfter(s.step);
          break;
        case "removeItem": {
          var ruid = t.dataset.uid;
          if (ruid === "tag") { self.setState({ tag: null, announce: "Nametag removed." }); break; }
          self.setState({
            collars: s.collars.filter(function (i) { return i.uid !== ruid; }),
            leashes: s.leashes.filter(function (i) { return i.uid !== ruid; }),
            harnesses: s.harnesses.filter(function (i) { return i.uid !== ruid; }),
            // Removing the item currently under edit ends the edit rather
            // than leaving a dangling marker that would silently turn the
            // next add into an "Update" of something that no longer exists.
            editingUid: s.editingUid === ruid ? null : s.editingUid,
            announce: "Item removed from your set."
          });
          // Losing the second-to-last piece drops the set discount — say
          // so, rather than letting prices quietly rise by 10%.
          var afterRemove = self.deriveVals();
          if (!afterRemove.discountUnlocked && afterRemove.itemCount === 1) {
            self.setState({ toast: "Set discount removed — add a second piece to get " + afterRemove.discountPct + "% off again." });
            self._scheduleToastClear();
          }
          break;
        }
        case "editItem": {
          // Non-destructive edit: load the item's exact configuration back
          // into its product card and mark it as the item being edited.
          // The item STAYS in the set the whole time — the previous
          // implementation lifted it out first, so abandoning the edit
          // (Back, a rail jump, a browser Back, closing the tab) silently
          // destroyed a committed, priced item with no undo. The next
          // commit replaces it in place; "Cancel edit" simply drops the
          // marker and leaves the original untouched.
          var euid = t.dataset.uid;
          var found = null, foundType = null;
          s.collars.forEach(function (i) { if (i.uid === euid) { found = i; foundType = "collar"; } });
          s.leashes.forEach(function (i) { if (i.uid === euid) { found = i; foundType = "leash"; } });
          s.harnesses.forEach(function (i) { if (i.uid === euid) { found = i; foundType = "harness"; } });
          if (!found) break;
          if (foundType === "collar") {
            var ecc = Object.assign({}, s.collarColors); ecc[found.familyKey] = found.colorKey;
            var ecw = Object.assign({}, s.collarWidths); ecw[found.familyKey] = found.width;
            var ecs = Object.assign({}, s.collarSizes); ecs[found.familyKey] = found.size;
            self.setState({
              collarFamily: found.familyKey, collarColors: ecc, collarWidths: ecw, collarSizes: ecs,
              collarStampFor: (function () {
                var m = Object.assign({}, s.collarStampFor);
                if (found.stampEnabled) m[found.familyKey] = true; else delete m[found.familyKey];
                return m;
              })(),
              collarStampText: found.stampText || "",
              editingUid: euid,
              announce: "Editing " + (FAMILY_LABELS[found.familyKey] || "") + " Collar."
            });
          } else if (foundType === "leash") {
            var elc = Object.assign({}, s.leashColors); elc[found.style] = found.colorKey;
            var elw = Object.assign({}, s.leashWidths); elw[found.style] = found.width;
            self.setState({
              leashStyle: found.style, leashColors: elc, leashWidths: elw,
              editingUid: euid, announce: "Editing leash."
            });
          } else {
            self.setState({
              harnessColor: found.colorKey,
              editingUid: euid, announce: "Editing harness."
            });
          }
          if (s.step === "review") s.editingFrom = true;
          if (s.step !== foundType) self._goto(foundType);
          break;
        }
        case "cancelEdit":
          self.setState({ editingUid: null, announce: "Edit cancelled — nothing changed." });
          break;
        case "toggleNoEngraving": self.setState({ tagNoEngraving: !s.tagNoEngraving, tagEngraveName: "", tagEngravePhone: "", tagError: false }); break;
        case "skipStep": {
          // Same rule: declining a step disarms the pick, it does not
          // delete committed items. (The tag is genuinely singular — its
          // "skip" means "don't include one", so that one does clear.)
          var skipKey = t.dataset.step;
          if (skipKey === "tag") self.setState({ tag: null, tagError: false });
          if (s.editingFrom) { s.editingFrom = false; self._goto("review"); }
          else self._nextAfter(skipKey);
          break;
        }
        case "startOver":
          // Destructive and irreversible — it must be asked for twice.
          // With items in the set, arm the confirmation; with an empty
          // set there is nothing to lose, so just reset.
          if (d.itemCount || s.tag || s.breedName || s.neck !== null || s.chest !== null) {
            self.setState({ confirmReset: true });
            break;
          }
          /* falls through */
        case "startOverConfirm":
          // Complete reset: wipe the persisted snapshot AND rebuild the
          // whole state from the factory — nothing survives.
          try { localStorage.removeItem(STORAGE_KEY); } catch (soErr) { /* storage unavailable */ }
          self.state = self._initialState();
          self._renderedStep = null;
          self._goto("entry");
          break;
        case "cancelReset": self.setState({ confirmReset: false }); break;
        case "toggleMobileSummary": self.setState({ mobileSummaryExpanded: !s.mobileSummaryExpanded }); break;
        case "setTagDesign": self.setState({ tagDesign: t.dataset.design }); break;
        case "setTagSize": self.setState({ tagSize: t.dataset.size }); break;
        case "commitTag": self._commitTag(); break;
        case "toggleSpelling": self.setState({ spellingConfirmed: !s.spellingConfirmed, spellingPrompt: false }); break;
        case "matchLeashWidth": {
          // Upgrade every slim 1.5cm leash in the set to its REAL 2cm
          // variant — id, price, and image — so the cart lines always
          // match what the Review screen shows.
          var upgraded = s.leashes.map(function (ls) {
            if (ls.width !== "1.5cm") return ls;
            var lp = self.leashProductFor(ls.style, ls.colorKey);
            var lv = lp ? resolveVariant(lp, { width: "2cm" }) : null;
            return lv ? Object.assign({}, ls, { width: "2cm", variantId: lv.id, cents: lv.price, image: imageFor(lp, lv) }) : ls;
          });
          self.setState({ leashes: upgraded });
          break;
        }
        case "addHarnessFromReview": self._goto("harness"); break;
        case "editLine": self.state.editingFrom = true; self._goto(t.dataset.line); break;
        case "goBack": self._prevBefore(s.step); break;
        case "addToCart":
          if (s.cartStatus === "adding") return;
          if (d.needsSpellingConfirm && !s.spellingConfirmed) {
            // The button used to be disabled and this branch silently
            // returned, so the last action in the whole flow was a grey
            // rectangle that did nothing, with the reason set in small
            // grey type above it. Pressing it now says what is missing and
            // takes the shopper to the control that unblocks it.
            self.setState({
              spellingPrompt: true,
              toast: "Confirm the spelling first — personalized pieces are made to order and can’t be returned.",
              announce: "Please confirm the spelling before adding to cart."
            });
            self._scheduleToastClear();
            requestAnimationFrame(function () {
              var row = self.querySelector("#lbfr-spellrow");
              if (row && row.scrollIntoView) row.scrollIntoView({ block: "center", behavior: "smooth" });
              var box = self.querySelector("#lbfr-spelling");
              if (box) { try { box.focus({ preventScroll: true }); } catch (e) { box.focus(); } }
            });
            return;
          }
          self.addToCart(self.buildCartItems(d));
          break;
        case "checkout": {
          // Native Shopify checkout, not the cart page.
          var checkoutRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
          window.location.href = checkoutRoot + "checkout";
          break;
        }
        case "keepBuilding": self._goto("review"); break;
      }
    });

    this.addEventListener("input", function (e) {
      var el = e.target;
      var key = el.dataset.input;
      if (!key) return;
      if (key === "dogName") { self.state.dogName = el.value; self._persist(); return; }
      if (key === "breed") {
        var patch = { breedQuery: el.value, showBreedList: true, breedActiveIdx: 0 };
        if (!el.value.trim()) patch.breedName = "";
        self.setState(patch);
        return;
      }
      // The morph render never rewrites the focused input's value, so the
      // uppercase/clamp transforms are applied to the element directly
      // before rendering the stamp-band preview.
      if (key === "stampText") {
        self.state.collarStampText = el.value.toUpperCase().slice(0, 8);
        if (el.value !== self.state.collarStampText) el.value = self.state.collarStampText;
        self.state.stampError = null;
        self.render(); return;
      }
      // Hard clamp mirrors the maxlength attributes — the physical brass
      // disc caps what can be engraved, so state can never exceed it even
      // via paste or programmatic input.
      if (key === "tagEngraveName") {
        self.state.tagEngraveName = el.value.slice(0, 12);
        if (el.value !== self.state.tagEngraveName) el.value = self.state.tagEngraveName;
        self.state.tagError = false;
        self.render(); return;
      }
      if (key === "tagEngravePhone") {
        self.state.tagEngravePhone = el.value.slice(0, 14);
        if (el.value !== self.state.tagEngravePhone) el.value = self.state.tagEngravePhone;
        self.state.tagError = false;
        self.render(); return;
      }
      // Slider drags stage the draft immediately (no debounce) so the
      // chart highlight and Apply label track the thumb in real time.
      // The slider's value is in the DISPLAYED unit domain (inches in IN
      // mode) — convert to cm and clamp before it touches the draft.
      if (key === "measureSlider") {
        var sv = parseFloat(el.value);
        if (!isNaN(sv)) {
          var svCm = self.state.unit === "in" ? inToCm(sv) : sv;
          var svIsNeck = self.state.measureKey === "neck";
          self.state.measureDraft = FL.clamp(svCm, svIsNeck ? FL.NECK_NODES[0] : FL.CHEST_NODES[0], svIsNeck ? FL.NECK_CUSTOM_MAX : FL.CHEST_CUSTOM_MAX);
          self.state.measureDraftBoundary = null;
          self.state.measureDraftSource = "measured";
          self.render();
        }
        return;
      }
      if (key === "measureValue") {
        var raw = el.value;
        clearTimeout(self._debounceTimers.measure);
        self._debounceTimers.measure = setTimeout(function () {
          // Empty field = deliberate clear (calculator parity).
          if (raw.trim() === "") {
            self.state.measureDraft = null;
            self.state.measureDraftBoundary = null;
            self.state.measureDraftSource = null;
            self.render();
            return;
          }
          // Decimal-comma keyboards submit "10,5" — normalize to a dot.
          var num = parseFloat(raw.trim().replace(",", "."));
          if (isNaN(num)) return;
          // Typed values stage the DRAFT only — Apply commits it. Clamp
          // to the sizing matrix's real bounds; a new value invalidates
          // any earlier loose/snug choice.
          var typedCm = self.state.unit === "cm" ? num : inToCm(num);
          var tIsNeck = self.state.measureKey === "neck";
          self.state.measureDraft = FL.clamp(typedCm, tIsNeck ? FL.NECK_NODES[0] : FL.CHEST_NODES[0], tIsNeck ? FL.NECK_CUSTOM_MAX : FL.CHEST_CUSTOM_MAX);
          self.state.measureDraftBoundary = null;
          self.state.measureDraftSource = "measured";
          self.render();
          self._pushEvent("fitting_room_measurement_used", { key: self.state.measureKey });
        }, 300);
      }
    });

    this.addEventListener("change", function (e) {
      // (The old collarSizeSelect/harnessSizeSelect dropdowns are gone —
      // their handlers with them; size selection lives in the assistant.)
      if (e.target.dataset.input === "buildOverride") {
        self.setState({ buildOverride: e.target.value });
      }
    });

    this.addEventListener("focusout", function (e) {
      if (self._isRerendering) return;
      var t = e.target.closest && e.target.closest("[data-input]");
      if (!t) return;
      if (self._activeFocusId === t.dataset.input) self._activeFocusId = null;
      if (t.dataset.input === "breed") {
        // Free-typed text that was never explicitly clicked from the list
        // (e.g. "Whippet Mix", which matches no exact suggestion) still
        // resolves as the literal breed name on blur — same fallback the
        // Size Calculator's combobox uses, so leaving the field without
        // picking a dropdown row isn't a dead end.
        var typed = t.value.trim();
        if (typed && typed !== self.state.breedName) self.applyBreedSelection(typed);
        else self.setState({ showBreedList: false });
      }
    });
  };

  customElements.define("lillebjorn-fitting-room", LillebjornFittingRoom);
})();
